<template>
  <div class="disk-index disk-app">
    <div class="disk-body">
      <aside class="disk-side">
        <div class="disk-brand">
          <img :src="icons.folder" alt="">
          <span>网盘</span>
        </div>
        <div class="disk-nav">
          <div v-for="sp in mainSpaces" :key="sp.key"
               :class="['disk-space-item', { on: activeSpace && activeSpace.key === sp.key }]"
               @click="enterSpace(sp)">
            <Icon :type="spaceIcon(sp)" size="18"></Icon>
            <span>{{ spaceLabel(sp) }}</span>
          </div>
          <div :class="['disk-space-item', { on: inRecycle }]" @click="enterSpace(recycleSpace())">
            <Icon type="ios-trash-outline" size="18"></Icon>
            <span>回收站</span>
          </div>
          <div class="disk-space-item" @click="$router.push('/disk/shares')">
            <Icon type="android-share-alt" size="18"></Icon>
            <span>分享记录</span>
          </div>
        </div>
        <div class="disk-side-section">
          <div class="disk-side-title">
            <span>我的群</span>
            <Tooltip content="新建群" placement="top" transfer>
              <a class="disk-side-add" @click.stop="openCreateGroup"><Icon type="ios-plus-empty" size="20"></Icon></a>
            </Tooltip>
          </div>
          <div class="disk-group-search">
            <Input v-model="groupKeyword" size="small" clearable placeholder="搜索群" icon="ios-search"></Input>
          </div>
          <div v-if="!groupSpaces.length" class="disk-side-empty">还没有加入任何群</div>
          <div v-else-if="!filteredGroupSpaces.length" class="disk-side-empty">没有匹配的群</div>
          <div class="disk-group-list">
            <div v-for="sp in filteredGroupSpaces" :key="sp.key"
                 :class="['disk-space-item', 'disk-group-item', { on: activeSpace && activeSpace.key === sp.key }]"
                 @click="enterSpace(sp)">
              <span class="disk-avatar disk-avatar-sm" :style="{ background: avatarColor(sp.node.name) }">{{ avatarText(sp.node.name) }}</span>
              <span class="disk-group-item-name" :title="sp.node.name">{{ sp.node.name }}</span>
            </div>
          </div>
        </div>
      </aside>
      <div class="disk-main">
        <div class="disk-topbar">
          <div class="disk-path">
            <a :class="['disk-path-back', { disabled: breadcrumb.length < 2 }]" title="返回上一级" @click="goUp">
              <Icon type="ios-arrow-left" size="18"></Icon>
            </a>
            <template v-for="(item, index) in breadcrumb">
              <Icon v-if="index > 0" :key="'sep-' + item.id + '-' + index" type="ios-arrow-right" class="disk-path-sep"></Icon>
              <span v-if="index === 0 && inGroupSpace && activeGroup" :key="'p-' + item.id + '-' + index"
                    :class="['disk-path-group', { current: breadcrumb.length === 1 }]" @click="breadcrumb.length > 1 && jumpTo(0)">
                <span class="disk-avatar disk-avatar-xs" :style="{ background: avatarColor(activeGroup.name) }">{{ avatarText(activeGroup.name) }}</span>
                <span class="disk-path-group-name" :title="activeGroup.name">{{ activeGroup.name }}</span>
                <span :class="['disk-role-tag', 'role-' + (activeGroup.myRole || 3)]">{{ roleText(activeGroup.myRole) }}</span>
              </span>
              <a v-else-if="index < breadcrumb.length - 1" :key="'p-' + item.id + '-' + index" class="disk-path-item" @click="jumpTo(index)">{{ item.name }}</a>
              <span v-else :key="'p-' + item.id + '-' + index" class="disk-path-item current">{{ item.name }}</span>
            </template>
          </div>
          <div class="disk-topbar-right">
            <div v-show="!inRecycle" class="disk-search">
              <Input v-model="keyword" placeholder="搜索当前目录及子目录" clearable icon="ios-search"
                     @on-enter="search" @on-click="search"></Input>
            </div>
            <template v-if="inGroupSpace && activeGroup">
              <Tooltip :content="isGroupManager ? '成员管理' : '群成员'" placement="bottom" transfer>
                <a :class="['disk-members', { readonly: !isGroupManager }]" @click="isGroupManager && openMemberManage()">
                  <Icon type="ios-people-outline" size="20"></Icon>
                  <span>{{ activeGroup.memberCount || 0 }}</span>
                </a>
              </Tooltip>
              <Tooltip content="群动态" placement="bottom" transfer>
                <a class="disk-icon-btn" @click="openActivityPanel"><Icon type="ios-pulse" size="20"></Icon></a>
              </Tooltip>
              <Dropdown v-if="isGroupOwner || !isGroupManager" trigger="click" placement="bottom-end" transfer @on-click="onGroupSetting">
                <a class="disk-icon-btn" title="更多"><Icon type="more" size="20"></Icon></a>
                <DropdownMenu slot="list">
                  <template v-if="isGroupOwner">
                    <DropdownItem name="rename">修改群名</DropdownItem>
                    <DropdownItem name="transfer">转让群主</DropdownItem>
                    <DropdownItem name="dissolve" divided><span class="disk-text-danger">解散群</span></DropdownItem>
                  </template>
                  <DropdownItem v-else name="leave"><span class="disk-text-danger">退出群</span></DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </template>
          </div>
        </div>
        <disk-group-nav
          v-if="inGroupSpace && activeGroup && Number(activeGroup.id) === 1"
          :group-id="activeGroup.id"
          :root-node-id="activeGroup.rootNodeId || (activeSpace && activeSpace.node && activeSpace.node.id)"
          :current-folder-id="currentFolder && currentFolder.id"
          @open-target="onNavOpenTarget">
        </disk-group-nav>

        <div class="disk-toolbar">
          <input ref="fileInput" type="file" multiple style="display: none" @change="onFilePicked">
          <input ref="folderInput" type="file" multiple webkitdirectory style="display: none" @change="onFilePicked">
          <template v-if="!inRecycle && selectedRows.length">
            <div class="disk-batch">
              <span class="disk-batch-count">已选 <b>{{ selectedRows.length }}</b> 项</span>
              <Button v-if="canDownload" icon="ios-download-outline" @click="downloadSelected">下载</Button>
              <Button v-if="canShare" icon="android-share-alt" @click="openShare()">分享</Button>
              <Button v-if="canMove" icon="ios-redo-outline" @click="openMoveSelected">移动</Button>
              <Button v-if="canAcl" icon="ios-locked-outline" @click="openAcl()">授权</Button>
              <Button v-if="canDelete" icon="ios-trash-outline" class="disk-btn-danger" @click="deleteSelected">删除</Button>
              <a class="disk-batch-clear" @click="clearSelection">取消选择</a>
            </div>
          </template>
          <template v-else-if="!inRecycle">
            <Button type="primary" icon="ios-cloud-upload-outline" :disabled="!canUpload" @click="chooseUpload">上传</Button>
            <Button icon="ios-plus-empty" :disabled="!canUpload" @click="openMkdir">新建文件夹</Button>
          </template>
          <div v-else class="disk-recycle-note">
            <Icon type="ios-information-outline"></Icon>
            删除的文件会保留在这里，可随时恢复
          </div>
          <div class="disk-count">{{ listKeyword ? '搜索结果 ' : '共 ' }}{{ list.length }} 项</div>
        </div>

        <Table v-show="loading || list.length" ref="fileTable" highlight-row class="disk-file-table"
               :columns="displayColumns" :data="list" :loading="loading" :row-class-name="fileRowClass"
               @on-selection-change="onSelectionChange" @on-current-change="onCurrentChange"></Table>
        <div v-if="!loading && !list.length" class="disk-empty">
          <img :src="inRecycle ? icons.file : icons.folder" alt="" class="disk-empty-icon">
          <div class="disk-empty-title">{{ emptyTitle }}</div>
          <div v-if="!inRecycle && !listKeyword && canUpload" class="disk-empty-sub">把文件上传到这里，团队成员就能一起查看</div>
          <Button v-if="!inRecycle && !listKeyword && canUpload" type="primary" icon="ios-cloud-upload-outline" @click="chooseUpload">上传文件</Button>
        </div>
      </div>
    </div>

    <Modal v-model="showMkdirModal" class-name="disk-modal" title="新建文件夹" width="440" :mask-closable="false">
      <div class="disk-dm-field">
        <img :src="icons.folder" alt="" class="disk-dm-field-icon">
        <Input v-model="mkdirName" size="large" placeholder="请输入文件夹名称" :maxlength="255" @on-enter="onMkdir"></Input>
      </div>
      <div slot="footer">
        <Button type="text" @click="showMkdirModal = false">取消</Button>
        <Button type="primary" :loading="mkdirLoading" @click="onMkdir">确定</Button>
      </div>
    </Modal>

    <Modal v-model="showRenameModal" class-name="disk-modal" title="重命名" width="440" :mask-closable="false">
      <div class="disk-dm-field">
        <img v-if="renameNode" :src="fileIcon(renameNode)" alt="" class="disk-dm-field-icon">
        <Input v-model="renameName" size="large" placeholder="请输入新名称" :maxlength="255" @on-enter="onRename"></Input>
      </div>
      <div slot="footer">
        <Button type="text" @click="showRenameModal = false">取消</Button>
        <Button type="primary" :loading="renameLoading" @click="onRename">确定</Button>
      </div>
    </Modal>

    <Modal v-model="showMoveModal" class-name="disk-modal" :title="pickerMode === 'copy' ? '复制到' : '移动到'" width="560" :mask-closable="false">
      <div class="disk-dm-target">
        <img v-if="moveNodes.length === 1" :src="fileIcon(moveNodes[0])" alt="">
        <img v-else :src="icons.file" alt="">
        <span>{{ moveTitle }}</span>
      </div>
      <div class="disk-dm-tabs">
        <a v-for="sp in moveSpaces" :key="sp.key"
           :class="['disk-dm-tab', { on: moveCrumbs.length && moveCrumbs[0].id === sp.node.id }]"
           @click="switchMoveSpace(sp)">{{ sp.node.name }}</a>
      </div>
      <div class="disk-picker">
        <div class="disk-picker-path">
          <template v-for="(item, index) in moveCrumbs">
            <Icon v-if="index > 0" :key="'ms-' + item.id + '-' + index" type="ios-arrow-right" class="disk-path-sep"></Icon>
            <a v-if="index < moveCrumbs.length - 1" :key="'mp-' + item.id + '-' + index" @click="jumpMove(index)">{{ item.name }}</a>
            <span v-else :key="'mp-' + item.id + '-' + index" class="current">{{ item.name }}</span>
          </template>
        </div>
        <div class="disk-picker-body">
          <div v-if="moveFolderLoading" class="disk-picker-empty"><Spin></Spin></div>
          <div v-else-if="!moveFolders.length" class="disk-picker-empty">
            <img :src="icons.folder" alt="">
            <span>{{ pickerMode === 'copy' ? '这里没有子文件夹，可以直接复制到此处' : '这里没有子文件夹，可以直接移动到此处' }}</span>
          </div>
          <template v-else>
            <div v-for="folder in moveFolders" :key="folder.id" class="disk-move-folder" @click="enterMoveFolder(folder)">
              <img :src="icons.folder" alt="">
              <span>{{ folder.name }}</span>
              <Icon type="ios-arrow-right" class="disk-move-folder-arrow"></Icon>
            </div>
          </template>
        </div>
      </div>
      <div slot="footer">
        <Button type="text" @click="showMoveModal = false">取消</Button>
        <Button type="primary" :loading="moveSaving" :disabled="moveHereDisabled" @click="confirmMove">{{ pickerMode === 'copy' ? '复制到此处' : '移动到此处' }}</Button>
      </div>
    </Modal>

    <Modal v-model="showPreviewModal" class-name="disk-modal" :title="previewName" width="960" @on-visible-change="onPreviewVisible">
      <div class="disk-preview" :class="{ 'disk-preview-center': centerPreview }">
        <div v-if="previewLoading" class="disk-preview-tip">加载中...</div>
        <div v-else-if="previewError" class="disk-preview-tip">{{ previewError }}</div>
        <img v-else-if="previewType === 'image'" :src="previewUrl" class="disk-preview-image">
        <iframe v-else-if="previewType === 'pdf'" :src="previewBlobUrl" class="disk-preview-frame"></iframe>
        <template v-else-if="previewType === 'video'">
          <video ref="previewVideo" :src="previewUrl" controls class="disk-preview-media" @error="onMediaError"></video>
          <div v-if="mediaError" class="disk-preview-tip">该格式无法预览，请下载</div>
        </template>
        <template v-else-if="previewType === 'audio'">
          <audio ref="previewAudio" :src="previewUrl" controls @error="onMediaError"></audio>
          <div v-if="mediaError" class="disk-preview-tip">该格式无法预览，请下载</div>
        </template>
        <template v-else-if="previewType === 'office' && previewEngine === 'aliyun'">
          <div ref="aliyunOffice" class="disk-preview-frame"></div>
        </template>
        <template v-else-if="previewType === 'office'">
          <iframe :src="officeUrl" class="disk-preview-frame"></iframe>
          <div class="disk-preview-hint">若无法打开，请下载后查看</div>
        </template>
        <template v-else-if="previewType === 'csv'">
          <div v-if="previewEmpty" class="disk-preview-tip">没有可预览的内容</div>
          <div v-else class="disk-preview-table-wrap">
            <table class="disk-preview-table">
              <tbody>
                <tr v-for="(line, lineIndex) in previewRows" :key="lineIndex">
                  <td v-for="(cell, cellIndex) in line" :key="cellIndex"
                      :class="{ 'disk-preview-th': lineIndex === 0 }">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <div v-else-if="previewType === 'markdown'" class="disk-preview-markdown" v-html="previewHtml"></div>
        <template v-else-if="previewType === 'json'">
          <div v-if="jsonError" class="disk-preview-hint">不是合法 JSON</div>
          <pre class="disk-preview-pre">{{ previewText }}</pre>
        </template>
        <pre v-else-if="previewType === 'text'" class="disk-preview-pre">{{ previewText }}</pre>
        <div v-else class="disk-preview-tip">该格式无法预览，请下载</div>
      </div>
      <div slot="footer">
        <Button type="primary" icon="ios-download" @click="download(previewDownloadId)">下载</Button>
      </div>
    </Modal>

    <Modal v-model="showAclModal" class-name="disk-modal" title="授权" width="760">
      <div class="disk-dm-target">
        <img v-if="aclNodes.length === 1" :src="fileIcon(aclNodes[0])" alt="">
        <img v-else :src="icons.file" alt="">
        <span :title="aclTitle">{{ aclTitle }}</span>
      </div>
      <div class="disk-dm-card">
        <div class="disk-dm-card-title">添加授权</div>
        <div class="disk-acl-form">
          <RadioGroup v-model="aclForm.subjectType" type="button" @on-change="onSubjectTypeChange">
            <Radio :label="1">员工</Radio>
            <Radio :label="2">权限组</Radio>
          </RadioGroup>
          <template v-if="aclForm.subjectType === 1">
            <Select v-model="aclForm.subjectIds" multiple filterable transfer
                    :loading="adminUserLoading" :disabled="adminUserLoading" :key="'acl-users-' + adminUserSelectKey"
                    placeholder="搜索并选择员工" class="disk-acl-select">
              <Option v-for="user in aclAdminUsers" :value="user.id" :key="user.id" :label="userLabel(user)">
                {{ userLabel(user) }}
              </Option>
            </Select>
            <a class="disk-acl-all" :class="{ disabled: adminUserLoading || !aclAdminUsers.length }"
               @click="!(adminUserLoading || !aclAdminUsers.length) && selectAllUsers()">全选员工</a>
          </template>
          <template v-else>
            <Select v-model="aclForm.subjectIds" multiple filterable transfer
                    :loading="adminGroupLoading" :disabled="adminGroupLoading" :key="'acl-groups-' + adminGroupSelectKey"
                    placeholder="搜索并选择权限组" class="disk-acl-select">
              <Option v-for="group in adminGroups" :value="group.id" :key="group.id" :label="group.title">{{ group.title }}</Option>
            </Select>
            <a class="disk-acl-all" :class="{ disabled: adminGroupLoading || !adminGroups.length }"
               @click="!(adminGroupLoading || !adminGroups.length) && selectAllGroups()">全选权限组</a>
          </template>
        </div>
        <div class="disk-acl-perm">
          <span class="disk-acl-perm-label">可以</span>
          <CheckboxGroup v-model="aclForm.permBits" class="disk-chip-checks">
            <Checkbox v-for="item in permOptions" :label="item.value" :key="item.value">{{ item.label }}</Checkbox>
          </CheckboxGroup>
          <Button type="primary" class="disk-acl-save" :loading="aclSaving" @click="saveAcl">保存授权</Button>
        </div>
      </div>
      <template v-if="aclNodes.length <= 1">
        <div class="disk-dm-section-title">已授权 <span>{{ aclList.length }}</span></div>
        <div class="disk-person-list">
          <div v-if="aclLoading" class="disk-picker-empty"><Spin></Spin></div>
          <div v-else-if="!aclList.length" class="disk-person-empty">还没有授权给任何人</div>
          <div v-for="item in (aclLoading ? [] : aclList)" :key="item.id || (item.subjectType + '-' + item.subjectId)" class="disk-person">
            <span class="disk-avatar disk-avatar-md" :style="{ background: avatarColor(aclSubjectName(item)) }">{{ avatarText(aclSubjectName(item)) }}</span>
            <div class="disk-person-main">
              <div class="disk-person-name">
                {{ aclSubjectName(item) }}
                <span class="disk-mini-tag">{{ item.subjectType === 2 ? '权限组' : '员工' }}</span>
              </div>
              <div class="disk-person-sub">{{ permText(item.perm) }}</div>
            </div>
            <a class="disk-link-danger" @click="removeAcl(item)">移除</a>
          </div>
        </div>
      </template>
      <div v-else class="disk-tip">保存后，同样的授权会写到每一个选中项。</div>
      <div slot="footer">
        <Button @click="showAclModal = false">关闭</Button>
      </div>
    </Modal>

    <Modal v-model="showShareModal" class-name="disk-modal" title="分享文件" width="720">
      <div class="disk-dm-target">
        <img v-if="shareNodes.length === 1" :src="fileIcon(shareNodes[0])" alt="">
        <img v-else :src="icons.file" alt="">
        <span :title="shareTitle">{{ shareTitle }}</span>
      </div>
      <div class="disk-dm-card">
        <div class="disk-share-row">
          <span class="disk-share-label">有效期</span>
          <RadioGroup v-model="shareForm.expireType" type="button">
            <Radio label="1">1天</Radio>
            <Radio label="7">7天</Radio>
            <Radio label="30">30天</Radio>
            <Radio label="forever">永久</Radio>
            <Radio label="custom">自定义</Radio>
          </RadioGroup>
        </div>
        <div v-if="shareForm.expireType === 'custom'" class="disk-share-row">
          <span class="disk-share-label">到期时间</span>
          <DatePicker type="datetime" format="yyyy-MM-dd HH:mm:ss" placeholder="请选择到期时间" transfer
                      :value="shareForm.customExpireDate" style="width: 240px"
                      @on-change="onCustomExpire"></DatePicker>
        </div>
        <div class="disk-share-row">
          <span class="disk-share-label">提取码</span>
          <Input v-model="shareForm.extractCode" :maxlength="4" placeholder="不填则无需提取码" style="width: 200px"></Input>
          <a class="disk-share-random" @click="randomExtractCode">随机生成</a>
        </div>
        <div class="disk-share-row disk-share-submit">
          <span class="disk-share-label"></span>
          <Button type="primary" icon="link" :loading="shareSaving" @click="createShare">创建链接并复制</Button>
        </div>
      </div>
      <div class="disk-dm-section-title">分享链接 <span>{{ shareList.length }}</span></div>
      <div class="disk-share-list">
        <div v-if="!shareList.length" class="disk-person-empty">还没有分享链接</div>
        <div v-for="item in shareList" :key="item.id || item.token" class="disk-share-item">
          <div class="disk-share-item-top">
            <Icon type="link" class="disk-share-item-icon"></Icon>
            <a class="disk-share-url" :href="buildShareUrl(item.token, item.extractCode)" target="_blank" rel="noopener"
               :title="buildShareUrl(item.token, item.extractCode)">{{ buildShareUrl(item.token, item.extractCode) }}</a>
            <Button size="small" type="primary" ghost @click="copyText(buildShareUrl(item.token, item.extractCode))">复制</Button>
          </div>
          <div class="disk-share-item-meta">
            <span v-if="shareNodes.length > 1">{{ item.nodeName }}</span>
            <span>提取码 <b>{{ item.extractCode || '无' }}</b></span>
            <span>{{ item.expireTime ? formatTime(item.expireTime) + ' 到期' : '永久有效' }}</span>
            <span v-if="item.creatorName">{{ item.creatorName }} 分享</span>
            <span>访问 {{ item.visitCount || 0 }} · 预览 {{ item.previewCount || 0 }} · 下载 {{ item.downloadCount || 0 }}</span>
            <span class="disk-share-item-ops">
              <a @click="openShareLog(item)">访问记录</a>
              <a class="disk-link-danger" @click="revokeShare(item)">撤销</a>
            </span>
          </div>
        </div>
      </div>
      <div slot="footer">
        <Button @click="showShareModal = false">完成</Button>
      </div>
    </Modal>

    <Modal v-model="showShareLogModal" class-name="disk-modal" title="访问记录" width="720">
      <Table border :columns="shareLogColumns" :data="shareLogList" :loading="shareLogLoading"
             no-data-text="还没有预览或下载"></Table>
    </Modal>

    <Modal v-model="showUploadModal" class-name="disk-modal" title="上传文件" width="860" :mask-closable="false">
      <div class="disk-upload-head">
        <div class="disk-dm-target">
          <img :src="icons.folder" alt="">
          <span>上传到：{{ currentFolder ? currentFolder.name : '当前目录' }}</span>
        </div>
        <div class="disk-upload-switch">
          <span>同名文件覆盖</span>
          <i-switch v-model="overwriteSame" size="large">
            <span slot="open">是</span>
            <span slot="close">否</span>
          </i-switch>
        </div>
      </div>
      <div class="disk-drop" @dragover.prevent @drop.prevent="onDropFiles">
        <Icon type="ios-cloud-upload-outline" size="56" class="disk-drop-icon"></Icon>
        <p class="disk-drop-title">把文件或文件夹拖到这里</p>
        <p class="disk-drop-sub">或者</p>
        <div>
          <Button type="primary" ghost icon="ios-paper-outline" @click="pickFiles">选择文件</Button>
          <Button type="primary" ghost icon="ios-folder-outline" style="margin-left: 8px" @click="pickFolder">选择文件夹</Button>
        </div>
      </div>
      <div class="disk-upload-rules">
        <a @click="uploadRulesOpen = !uploadRulesOpen">
          <Icon type="ios-information-outline"></Icon>
          上传须知
          <Icon :type="uploadRulesOpen ? 'ios-arrow-up' : 'ios-arrow-down'"></Icon>
        </a>
        <div v-show="uploadRulesOpen" class="disk-upload-rules-body">
          <p>文件命名规范：1.使用 UTF-8 编码 2.区分大小写 3.长度必须在 1~1023 字节之间 4. 不能以 / 或者 \ 字符开头</p>
          <p>文件上传提示：待上传列表最多允许添加 10000 个文件，超出部分被丢弃，如果要完整上传，请将文件打包成 Zip 包，上传后解压。</p>
          <p>扫描只加入待上传列表，不会上传。点下方「上传文件」后才开始。同名是否覆盖默认关闭。关闭时同名文件会另存为「主名.1.0.后缀」，已有 1.x 则次版本加 1。打开后原文件会被彻底删除，不进回收站，再用同名上传新文件。同名文件夹仍直接使用。</p>
        </div>
      </div>
      <p v-if="scanning" class="disk-tip">正在扫描，已读取 {{ scanCount }} 个文件…</p>
      <Table v-if="uploadTotal" :columns="uploadColumns" :data="uploadPageRows" no-data-text="没有文件或文件夹" class="disk-upload-table"></Table>
      <div v-if="uploadTotal" class="disk-upload-page">
        <span>共 {{ uploadTotal }} 个</span>
        <Page :total="uploadTotal" :page-size="uploadPageSize" :current="uploadPage" size="small"
              @on-change="onUploadPage"></Page>
      </div>
      <div slot="footer">
        <Button type="primary" :disabled="!canBeginUpload" :loading="uploading" @click="beginUpload">上传文件</Button>
        <Button @click="showUploadModal = false">取消</Button>
      </div>
    </Modal>

    <div v-if="activityPanelOpen" class="disk-activity-drawer">
      <div class="disk-activity-head">
        <span>群动态</span>
        <a @click="activityPanelOpen = false">收起</a>
      </div>
      <div v-if="activityLoading && !activityGroups.length" class="disk-activity-empty">加载中…</div>
      <div v-else-if="!activityGroups.length" class="disk-activity-empty">暂无群动态</div>
      <div v-else class="disk-activity-body" @scroll="onActivityScroll">
        <div v-for="g in activityGroups" :key="g.day" class="disk-activity-day">
          <div class="disk-activity-day-label">{{ g.label }}</div>
          <div v-for="item in g.items" :key="item.id" class="disk-activity-card">
            <div class="disk-activity-dot"></div>
            <div class="disk-activity-card-main">
              <div class="disk-activity-card-top">
                <strong>{{ item.actorName || '成员' }}</strong>
                <span class="disk-activity-time">{{ formatActivityTime(item.gmtCreate) }}</span>
              </div>
              <div v-if="activityLinkable(item)" class="disk-activity-summary">
                <template v-for="(part, pi) in activitySummaryParts(item)">
                  <a v-if="part.link" :key="pi" class="disk-activity-link" @click="locateActivity(item)">{{ part.text }}</a>
                  <span v-else :key="pi">{{ part.text }}</span>
                </template>
              </div>
              <div v-else class="disk-activity-summary">{{ item.summary }}</div>
            </div>
          </div>
        </div>
        <div v-if="activityLoading" class="disk-activity-empty">加载中…</div>
        <div v-else-if="activityNoMore && activityList.length" class="disk-activity-empty">没有更多了</div>
      </div>
    </div>

    <div class="disk-task-float" :class="{ open: taskPanelOpen }">
      <div v-if="taskPanelOpen" class="disk-task-panel">
        <div class="disk-task-head">
          <span>任务列表</span>
          <a @click="taskPanelOpen = false">收起</a>
        </div>
        <div v-if="showTaskOverall" class="disk-task-overall">
          <Progress :percent="taskOverallPercent" :stroke-width="8"></Progress>
          <div class="disk-task-overall-text">
            已传 {{ formatSize(taskOverallDone) }} / 共 {{ formatSize(taskOverallTotal) }}（{{ taskOverallPercent }}%）
            <span class="disk-task-speed">{{ formatSpeed(taskSpeedBps) }}</span>
          </div>
        </div>
        <div class="disk-task-note">刷新或关闭浏览器会取消当前上传任务，并清除全部记录。</div>
        <div class="disk-task-filters">
          <a v-for="tab in taskTabs" :key="tab.key" :class="{ on: taskFilter === tab.key }"
             @click="taskFilter = tab.key">{{ tab.label }}({{ tab.count }})</a>
        </div>
        <div class="disk-task-tools">
          <span class="disk-task-concurrency">
            并发：智能
            <span v-if="uploadAutoHint" class="disk-upload-auto-hint">{{ uploadAutoHint }}</span>
          </span>
          <div class="disk-task-tool-btns">
            <Button size="small" :disabled="!taskUploadingCount" @click="stopUploads">停止上传</Button>
            <Button size="small" style="margin-left: 8px" :disabled="!taskResumableCount || uploading" @click="startAllUploads">全部开始</Button>
            <Button size="small" style="margin-left: 8px" :disabled="!taskRemovableCount" @click="clearAllTasks">全部移除</Button>
          </div>
        </div>
        <div class="disk-task-cols">
          <span>文件</span>
          <span>上传到</span>
          <span>状态</span>
          <span>操作</span>
        </div>
        <div v-if="!taskView.length" class="disk-task-empty">暂无上传任务</div>
        <div v-for="item in taskView" :key="item.key" class="disk-task-row">
          <div>
            <div class="disk-task-name" :title="item.name">{{ item.name }}</div>
            <div class="disk-task-size">{{ formatSize(item.size) }}</div>
            <Progress v-if="taskFilter === 'uploading' && item.state === 'uploading'" :percent="item.percent" :stroke-width="6"></Progress>
          </div>
          <div class="disk-task-dest" :title="item.dest">{{ item.dest || '/' }}</div>
          <div :class="['disk-task-state', item.state]" :title="item.failMsg || item.stateLabel">{{ item.stateLabel }}</div>
          <div class="disk-task-ops">
            <a v-if="item.state === 'uploading'" @click="pauseUpload(item)">暂停</a>
            <a v-if="item.state === 'stopped' || item.state === 'failed'" @click="continueUpload(item)">继续上传</a>
            <a v-if="item.state === 'stopped' || item.state === 'failed' || item.state === 'success'" @click="clearTask(item)">移除</a>
          </div>
        </div>
      </div>
      <div :class="['disk-task-bar', { uploading: taskBarUploading }]" @click="taskPanelOpen = !taskPanelOpen">
        <template v-if="taskBarUploading">
          <span class="disk-wave">
            <span class="disk-wave-fill back" :style="{ top: taskWaveTop }"></span>
            <span class="disk-wave-fill" :style="{ top: taskWaveTop }"></span>
          </span>
          <span class="disk-task-progress">
            <span class="disk-task-progress-main">上传中 {{ taskOverallPercent }}%</span>
            <span class="disk-task-progress-speed">{{ formatSpeed(taskSpeedBps) }}</span>
          </span>
        </template>
        <template v-else>
          <Icon type="ios-cloud-upload"></Icon>
          <span>任务列表</span>
        </template>
        <span v-if="taskBadge" class="disk-task-badge">{{ taskBadge }}</span>
        <Icon :type="taskPanelOpen ? 'ios-arrow-down' : 'ios-arrow-up'" class="disk-task-bar-arrow"></Icon>
      </div>
    </div>

    <Modal v-model="showPurgeModal" class-name="disk-modal" width="440" :mask-closable="false" :closable="!purgeLoading">
      <div class="disk-purge">
        <div class="disk-purge-icon"><Icon type="alert-circled" size="28"></Icon></div>
        <div class="disk-purge-main">
          <div class="disk-purge-title">永久删除，不可恢复</div>
          <div v-if="purgeNode" class="disk-purge-target">
            <img :src="fileIcon(purgeNode)" alt="">
            <span :title="purgeNode.name">{{ purgeNode.name }}</span>
          </div>
          <ul class="disk-purge-list">
            <li>将同时删除云端存储的文件，删除后无法从回收站恢复，也无法下载</li>
            <li v-if="purgeNode && purgeNode.nodeType === 1">这是文件夹，里面的所有文件和子文件夹会一起被永久删除</li>
            <li>相关的授权记录会一并清除</li>
          </ul>
        </div>
      </div>
      <div slot="footer">
        <Button :disabled="purgeLoading" @click="showPurgeModal = false">取消</Button>
        <Button type="error" :loading="purgeLoading" @click="confirmPurge">彻底删除</Button>
      </div>
    </Modal>

    <Modal v-model="showCreateGroupModal" class-name="disk-modal" title="新建群" width="440" :mask-closable="false">
      <div class="disk-dm-field">
        <span class="disk-avatar disk-avatar-md" :style="{ background: avatarColor(createGroupName) }">{{ avatarText(createGroupName) }}</span>
        <Input v-model="createGroupName" size="large" placeholder="给群起个名字" :maxlength="64" @on-enter="onCreateGroup"></Input>
      </div>
      <div class="disk-dm-hint">建群后可以邀请同事，一起上传和管理群文件</div>
      <div slot="footer">
        <Button type="text" @click="showCreateGroupModal = false">取消</Button>
        <Button type="primary" :loading="createGroupLoading" @click="onCreateGroup">确定</Button>
      </div>
    </Modal>

    <Modal v-model="showMemberModal" class-name="disk-modal" title="成员管理" width="860" :mask-closable="false">
      <div v-if="activeGroup" class="disk-member-head">
        <span class="disk-avatar disk-avatar-md" :style="{ background: avatarColor(activeGroup.name) }">{{ avatarText(activeGroup.name) }}</span>
        <div class="disk-member-head-main">
          <div class="disk-person-name">{{ activeGroup.name }}</div>
          <div class="disk-person-sub">{{ memberList.length }} 条成员记录</div>
        </div>
        <Dropdown v-if="isGroupOwner" trigger="click" placement="bottom-end" transfer @on-click="onGroupSetting">
          <Button icon="ios-gear-outline">群设置</Button>
          <DropdownMenu slot="list">
            <DropdownItem name="rename">修改群名</DropdownItem>
            <DropdownItem name="transfer">转让群主</DropdownItem>
            <DropdownItem name="dissolve" divided><span class="disk-text-danger">解散群</span></DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
      <div v-if="activeGroup" class="disk-dm-card disk-member-toolbar">
        <RadioGroup v-model="memberForm.subjectType" type="button" @on-change="memberForm.subjectIds = []">
          <Radio :label="1">员工</Radio>
          <Radio :label="2">账号组</Radio>
        </RadioGroup>
        <Select v-if="memberForm.subjectType === 1" v-model="memberForm.subjectIds" multiple filterable transfer
                :loading="adminUserLoading" class="disk-member-select" placeholder="搜索并选择要邀请的员工">
          <Option v-for="user in adminUsers" :value="user.id" :key="'mu-' + user.id" :label="userLabel(user)">
            {{ userLabel(user) }}
          </Option>
        </Select>
        <Select v-else v-model="memberForm.subjectIds" multiple filterable transfer
                :loading="adminGroupLoading" class="disk-member-select" placeholder="搜索并选择账号组">
          <Option v-for="group in adminGroups" :value="group.id" :key="'mg-' + group.id" :label="group.title">
            {{ group.title }}
          </Option>
        </Select>
        <Button type="primary" icon="ios-personadd-outline" :loading="memberSaving" @click="addGroupMembers">邀请</Button>
      </div>
      <Table :columns="memberColumns" :data="memberList" :loading="memberLoading"
             class="disk-member-table" @on-expand="onMemberExpand"></Table>
      <div slot="footer">
        <Button type="text" @click="showMemberModal = false">关闭</Button>
      </div>
    </Modal>

    <Modal v-model="showRenameGroupModal" class-name="disk-modal" title="修改群名" width="440" :mask-closable="false">
      <div class="disk-dm-field">
        <span class="disk-avatar disk-avatar-md" :style="{ background: avatarColor(renameGroupName) }">{{ avatarText(renameGroupName) }}</span>
        <Input v-model="renameGroupName" size="large" placeholder="请输入新群名称" :maxlength="64" @on-enter="onRenameGroup"></Input>
      </div>
      <div slot="footer">
        <Button type="text" @click="showRenameGroupModal = false">取消</Button>
        <Button type="primary" :loading="renameGroupLoading" @click="onRenameGroup">确定</Button>
      </div>
    </Modal>

    <Modal v-model="showTransferModal" class-name="disk-modal" title="转让群主" width="440" :mask-closable="false">
      <div class="disk-dm-hint disk-dm-hint-top">转让后你将变为普通成员，新群主拥有全部管理权限</div>
      <Select v-model="transferOwnerId" size="large" filterable transfer style="width: 100%" placeholder="选择新群主（须已是群成员）">
        <Option v-for="m in personMembers" :value="m.subjectId" :key="'tr-' + m.subjectId" :label="m.subjectName">
          {{ m.subjectName }}（{{ roleText(m.role) }}）
        </Option>
      </Select>
      <div slot="footer">
        <Button type="text" @click="showTransferModal = false">取消</Button>
        <Button type="primary" :loading="transferLoading" @click="onTransferGroup">确定转让</Button>
      </div>
    </Modal>

  </div>
</template>
<script>
  import DiskGroupNav from './DiskGroupNav.vue'
  import {iconFolder, iconFile, fileIconOf} from './fileIcon'

  const AVATAR_COLORS = ['#4C8DF6', '#12B886', '#7950F2', '#F59F00', '#E64980', '#15AABF', '#F76707', '#5C7CFA']
  export default {
    name: 'DiskIndex',
    components: { DiskGroupNav },
    data () {
      return {
        icons: {folder: iconFolder, file: iconFile},
        spaces: [],
        groupList: [],
        activeSpace: null,
        activeGroup: null,
        groupKeyword: '',
        showCreateGroupModal: false,
        createGroupName: '',
        createGroupLoading: false,
        showMemberModal: false,
        activityPanelOpen: false,
        activityLocating: false,
        flashNodeId: null,
        flashFolderId: null,
        activityLoading: false,
        activityList: [],
        activityPageIndex: 1,
        activityPageSize: 20,
        activityTotal: 0,
        activityNoMore: false,
        memberList: [],
        memberLoading: false,
        memberSaving: false,
        memberForm: {subjectType: 1, subjectIds: []},
        showRenameGroupModal: false,
        renameGroupName: '',
        renameGroupLoading: false,
        showTransferModal: false,
        transferOwnerId: null,
        transferLoading: false,
        memberColumns: [
          {
            type: 'expand',
            width: 50,
            render: (h, params) => {
              if (params.row.subjectType !== 2) {
                return h('span', '')
              }
              if (params.row._usersLoading) {
                return h('div', {class: 'disk-tip'}, '加载中...')
              }
              let users = params.row._users || []
              if (!users.length) {
                return h('div', {class: 'disk-tip'}, '组内暂无有效成员')
              }
              return h('Table', {
                props: {
                  columns: this.accountUserColumns,
                  data: users,
                  size: 'small',
                  showHeader: true
                }
              })
            }
          },
          {
            title: '成员',
            minWidth: 220,
            render: (h, params) => {
              let row = params.row
              let name = String(row.subjectName || row.subjectId || '')
              let sub = row.subjectType === 2
                ? '账号组' + (row.groupUserCount != null ? ' · ' + row.groupUserCount + ' 人' : '')
                : '员工'
              return h('div', {class: 'disk-person disk-person-inline'}, [
                h('span', {class: 'disk-avatar disk-avatar-md', style: {background: this.avatarColor(name)}}, this.avatarText(name)),
                h('div', {class: 'disk-person-main'}, [
                  h('div', {class: 'disk-person-name'}, name),
                  h('div', {class: 'disk-person-sub'}, sub)
                ])
              ])
            }
          },
          {
            title: '角色',
            width: 100,
            align: 'center',
            render: (h, params) => h('span', {class: ['disk-role-tag', 'role-' + (params.row.role || 3)]}, this.roleText(params.row.role))
          },
          {
            title: '上传',
            width: 100,
            align: 'center',
            render: (h, params) => {
              if (params.row.role === 1) {
                return h('span', '开')
              }
              return h('i-switch', {
                props: {value: !!params.row.uploadEnabled, size: 'small'},
                on: {'on-change': (v) => this.toggleMemberUpload(params.row, v)}
              })
            }
          },
          {
            title: '操作',
            width: 220,
            align: 'center',
            render: (h, params) => {
              let row = params.row
              let ops = []
              if (row.subjectType === 2) {
                ops.push(h('span', {
                  style: {marginRight: '12px', color: '#a8adb7', fontSize: '12px'}
                }, '展开设组内上传'))
              }
              if (this.isGroupOwner && row.subjectType === 1 && row.role !== 1) {
                ops.push(h('a', {
                  style: {marginRight: '12px'},
                  on: {click: () => this.toggleMemberAdmin(row)}
                }, row.role === 2 ? '取消管理员' : '设为管理员'))
              }
              if (row.role !== 1) {
                ops.push(h('a', {
                  class: 'disk-link-danger',
                  on: {click: () => this.removeGroupMember(row)}
                }, '移除'))
              }
              return ops.length ? h('div', ops) : h('span', '-')
            }
          }
        ],
        accountUserColumns: [
          {
            title: '成员',
            minWidth: 140,
            render: (h, params) => h('span', params.row.nickname || params.row.adminUserId)
          },
          {
            title: '有效上传',
            width: 110,
            align: 'center',
            render: (h, params) => {
              if (params.row.owner) {
                return h('span', '开（群主）')
              }
              return h('i-switch', {
                props: {value: !!params.row.uploadEnabled, size: 'small'},
                on: {'on-change': (v) => this.toggleAccountUserUpload(params.row, v)}
              })
            }
          },
          {
            title: '覆盖',
            width: 90,
            align: 'center',
            render: (h, params) => h('span', params.row.hasOverride ? '个人覆盖' : '跟随组')
          },
          {
            title: '操作',
            width: 110,
            align: 'center',
            render: (h, params) => {
              if (!params.row.hasOverride || params.row.owner) {
                return h('span', '-')
              }
              return h('a', {on: {click: () => this.clearAccountUserOverride(params.row)}}, '恢复默认')
            }
          }
        ],
        breadcrumb: [],
        currentFolder: null,
        list: [],
        keyword: '',
        listKeyword: '',
        loading: false,
        selectedRow: null,
        selectedRows: [],
        showMkdirModal: false,
        mkdirName: '',
        mkdirLoading: false,
        showRenameModal: false,
        renameName: '',
        renameLoading: false,
        renameNode: null,
        showMoveModal: false,
        pickerMode: 'move',
        moveNode: null,
        moveNodes: [],
        moveCrumbs: [],
        moveFolders: [],
        moveFolderLoading: false,
        moveSaving: false,
        showPreviewModal: false,
        previewUrl: '',
        previewBlobUrl: '',
        previewEngine: '',
        previewWebofficeUrl: '',
        previewAccessToken: '',
        previewRefreshToken: '',
        previewType: '',
        previewName: '',
        previewExt: '',
        previewDownloadId: null,
        previewLoading: false,
        previewError: '',
        previewText: '',
        previewHtml: '',
        previewRows: [],
        previewEmpty: false,
        jsonError: false,
        mediaError: false,
        showAclModal: false,
        aclNode: null,
        aclNodes: [],
        aclList: [],
        aclLoading: false,
        aclForm: {subjectType: 1, subjectId: null, subjectIds: [], permBits: [1, 2]},
        aclSaving: false,
        adminUserLoading: false,
        adminUserSelectKey: 0,
        adminGroupLoading: false,
        adminGroupSelectKey: 0,
        permOptions: [
          {value: 1, label: '查看'},
          {value: 2, label: '下载'},
          {value: 4, label: '上传'},
          {value: 8, label: '编辑'},
          {value: 16, label: '删除'},
          {value: 32, label: '分享'},
          {value: 64, label: '授权'}
        ],
        adminUsers: [],
        adminGroups: [],
        showShareModal: false,
        showShareLogModal: false,
        shareLogList: [],
        shareLogLoading: false,
        shareNode: null,
        shareNodes: [],
        shareList: [],
        shareSaving: false,
        shareForm: {expireType: 'forever', customExpire: '', customExpireDate: '', extractCode: ''},
        uploadTasks: [],
        uploadTotal: 0,
        uploadWaiting: 0,
        uploadStamp: 0,
        uploadPage: 1,
        uploadPageSize: 100,
        scanning: false,
        overwriteSame: false,
        scanCount: 0,
        uploading: false,
        uploadConcurrency: 6,
        uploadAutoHint: '',
        pendingSessions: [],
        showUploadModal: false,
        taskPanelOpen: false,
        taskFilter: 'all',
        taskSpeedBps: 0,
        resumeSession: null,
        pendingParentId: 0,
        rowMenuOpenId: null,
        uploadRulesOpen: false,
        showPurgeModal: false,
        purgeNode: null,
        purgeLoading: false,
        columns: [
          {type: 'selection', width: 56, align: 'center'},
          {
            title: '名称',
            minWidth: 360,
            render: (h, params) => {
              let row = params.row
              let nameAttrs = {class: 'disk-name-text', attrs: {title: row.name}}
              let nameNode
              if (row.nodeType === 1) {
                nameNode = h('a', Object.assign({on: {click: () => this.enterFolder(row)}}, nameAttrs), row.name)
              } else if (row.status === 1 && this.hasPerm(row, 2)) {
                nameNode = h('a', Object.assign({on: {click: () => this.previewRow(row)}}, nameAttrs), row.name)
              } else {
                nameNode = h('span', nameAttrs, row.name)
              }
              let main = [
                h('img', {class: 'disk-name-icon', attrs: {src: this.fileIcon(row), alt: ''}}),
                nameNode
              ]
              if (row.status === 0) {
                main.push(h('span', {class: 'disk-name-tag disk-name-tag-warn'}, '上传中'))
              } else if (row.status === 2) {
                main.push(h('span', {class: 'disk-name-tag disk-name-tag-err'}, '失败'))
              }
              let children = [h('div', {class: 'disk-name-main'}, main)]
              let ops = this.renderRowMenu(h, row)
              if (ops) {
                children.push(ops)
              }
              return h('div', {class: 'disk-name-cell'}, children)
            }
          },
          {
            title: '大小',
            width: 120,
            align: 'right',
            render: (h, params) => h('span', {class: 'disk-col-muted'}, params.row.nodeType === 1 ? '-' : this.formatSize(params.row.size))
          }
        ],
        recycleColumns: [
          {
            title: '名称',
            minWidth: 300,
            render: (h, params) => {
              let row = params.row
              let children = [h('div', {class: 'disk-name-main'}, [
                h('img', {class: 'disk-name-icon', attrs: {src: this.fileIcon(row), alt: ''}}),
                h('span', {class: 'disk-name-text', attrs: {title: row.name}}, row.name)
              ])]
              if (this.hasPerm(row, 16)) {
                children.push(h('div', {class: 'disk-row-ops'}, [
                  h('span', {
                    class: 'disk-op-btn disk-op-text',
                    attrs: {role: 'button'},
                    on: {
                      click: (event) => {
                        event.stopPropagation()
                        this.restoreRecycle(row)
                      }
                    }
                  }, [h('Icon', {props: {type: 'ios-undo-outline', size: 16}}), h('span', '恢复')]),
                  h('span', {
                    class: 'disk-op-btn disk-op-text disk-op-danger',
                    attrs: {role: 'button'},
                    on: {
                      click: (event) => {
                        event.stopPropagation()
                        this.openPurge(row)
                      }
                    }
                  }, [h('Icon', {props: {type: 'ios-trash-outline', size: 16}}), h('span', '彻底删除')])
                ]))
              }
              return h('div', {class: 'disk-name-cell'}, children)
            }
          },
          {
            title: '大小',
            width: 110,
            align: 'right',
            render: (h, params) => h('span', {class: 'disk-col-muted'}, params.row.nodeType === 1 ? '-' : this.formatSize(params.row.size))
          },
          {
            title: '删除时间',
            width: 170,
            render: (h, params) => h('span', {class: 'disk-col-muted'}, this.formatTime(params.row.gmtDeleted))
          },
          {
            title: '删除人',
            width: 120,
            render: (h, params) => h('span', {class: 'disk-col-muted'}, params.row.deleterName || '-')
          }
        ],
        shareLogColumns: [
          {title: '访问 IP', minWidth: 140, key: 'ip'},
          {
            title: '访问时间',
            width: 170,
            render: (h, params) => h('span', this.formatTime(params.row.gmtCreate))
          },
          {title: '预览次数', width: 100, align: 'center', key: 'previewCount'},
          {title: '下载次数', width: 100, align: 'center', key: 'downloadCount'}
        ],
        uploadColumns: [
          {title: '名称', minWidth: 180, key: 'name'},
          {title: '文件目录', minWidth: 140, key: 'dir'},
          {title: '类型', width: 140, key: 'typeName'},
          {
            title: '文件大小',
            width: 110,
            align: 'right',
            render: (h, params) => h('span', this.formatSize(params.row.size))
          },
          {
            title: '状态',
            width: 160,
            render: (h, params) => h('span', params.row.statusText)
          },
          {
            title: '操作',
            width: 80,
            align: 'center',
            render: (h, params) => params.row.statusText === '等待上传'
              ? h('a', {on: {click: () => this.removeQueued(params.row)}}, '移除')
              : h('span', '-')
          }
        ]
      }
    },
    computed: {
      displayColumns () {
        if (this.inRecycle) {
          return this.recycleColumns
        }
        let cols = this.columns.slice()
        let insertAt = 2
        let kw = (this.listKeyword || '').trim()
        if (kw) {
          cols.splice(insertAt, 0, {
            title: '所在目录',
            minWidth: 180,
            render: (h, params) => {
              let path = params.row.parentPath
              if (!path) {
                return h('span', {style: {color: '#808695'}}, '-')
              }
              return h('span', {
                style: {color: '#808695'},
                attrs: {title: path}
              }, path)
            }
          })
          insertAt += 1
        }
        if (this.inGroupSpace) {
          cols.splice(insertAt, 0, {
            title: '上传人',
            width: 120,
            render: (h, params) => h('span', params.row.ownerAdminName || params.row.creatorName || '-')
          })
        }
        return cols
      },
      mainSpaces () {
        return (this.spaces || []).filter(sp => !String(sp.key).startsWith('group-'))
      },
      groupSpaces () {
        return (this.spaces || []).filter(sp => String(sp.key).startsWith('group-'))
      },
      filteredGroupSpaces () {
        var kw = (this.groupKeyword || '').trim().toLowerCase()
        var list = this.groupSpaces
        if (!kw) {
          return list
        }
        return list.filter(function (sp) {
          var name = (sp.node && sp.node.name) || (sp.group && sp.group.name) || ''
          return String(name).toLowerCase().indexOf(kw) !== -1
        })
      },
      activityGroups () {
        const list = this.activityList || []
        const map = {}
        const order = []
        const now = new Date()
        const today = this.activityDayKey(now)
        const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
        const yesterday = this.activityDayKey(y)
        list.forEach(item => {
          const day = this.activityDayKey(item.gmtCreate)
          if (!map[day]) {
            map[day] = []
            order.push(day)
          }
          map[day].push(item)
        })
        return order.map(day => {
          let label = day
          if (day === today) label = '今日'
          else if (day === yesterday) label = '昨日'
          return { day, label, items: map[day] }
        })
      },
      inGroupSpace () {
        return this.activeSpace && String(this.activeSpace.key).startsWith('group-')
      },
      isGroupOwner () {
        return this.activeGroup && this.activeGroup.myRole === 1
      },
      isGroupManager () {
        return this.activeGroup && (this.activeGroup.myRole === 1 || this.activeGroup.myRole === 2)
      },
      personMembers () {
        return (this.memberList || []).filter(m => m.subjectType === 1 && m.role !== 1)
      },
      canUpload () {
        if (!this.currentFolder) {
          return false
        }
        if (this.currentFolder.perm == null) {
          return true
        }
        return this.hasPerm(this.currentFolder, 4)
      },
      moveSpaces () {
        return (this.spaces || []).filter(sp => sp.key === 'personal' || sp.key === 'company' || String(sp.key).startsWith('group-'))
      },
      moveTitle () {
        let rows = this.moveNodes || []
        let verb = this.pickerMode === 'copy' ? '复制' : '移动'
        if (rows.length === 1) {
          return verb + '「' + rows[0].name + '」到：'
        }
        if (rows.length > 1) {
          return verb + ' ' + rows.length + ' 项到：'
        }
        return verb + '到：'
      },
      moveHereDisabled () {
        let target = this.moveCrumbs.length ? this.moveCrumbs[this.moveCrumbs.length - 1] : null
        let rows = this.moveNodes || []
        if (!rows.length || !target) {
          return true
        }
        if (rows.some(row => row.id === target.id)) {
          return true
        }
        if (rows.every(row => row.parentId === target.id)) {
          return true
        }
        return !this.hasPerm(target, 4)
      },
      canMove () {
        return this.pickedRows.some(row => this.hasPerm(row, 8))
      },
      pickedRows () {
        if (this.selectedRows && this.selectedRows.length) {
          return this.selectedRows
        }
        return this.selectedRow ? [this.selectedRow] : []
      },
      canDownload () {
        return this.pickedRows.some(row => row.nodeType === 2 && row.status === 1 && this.hasPerm(row, 2))
      },
      canShare () {
        return this.pickedRows.some(row => this.hasPerm(row, 32))
      },
      canAcl () {
        return this.pickedRows.some(row => this.hasPerm(row, 64))
      },
      shareTitle () {
        return this.batchTitle(this.shareNodes, '分享')
      },
      aclTitle () {
        return this.batchTitle(this.aclNodes, '授权')
      },
      aclAdminUsers () {
        let rows = this.adminUsers || []
        let blocked = {}
        rows.forEach(function (user) {
          let title = user && user.groupTitle != null ? String(user.groupTitle).trim() : ''
          if (title === '代理') {
            blocked[user.id] = true
          }
        })
        let seen = {}
        return rows.filter(function (user) {
          if (!user || seen[user.id] || blocked[user.id]) {
            return false
          }
          if (user.status !== 1 && user.status !== '1') {
            return false
          }
          seen[user.id] = true
          return true
        })
      },
      canDelete () {
        return this.pickedRows.some(row => this.hasPerm(row, 16))
      },
      centerPreview () {
        return ['image', 'pdf', 'video', 'audio'].indexOf(this.previewType) >= 0
      },
      officeUrl () {
        return 'https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(this.previewUrl || '')
      },
      inRecycle () {
        return this.activeSpace && this.activeSpace.key === 'recycle'
      },
      emptyTitle () {
        if (this.listKeyword) {
          return '没有找到相关文件'
        }
        if (this.inRecycle) {
          return '回收站是空的'
        }
        if (this.activeSpace && this.activeSpace.key === 'granted') {
          return '还没有人授权文件给你'
        }
        return '这个文件夹是空的'
      },
      canBeginUpload () {
        return !this.scanning && this.uploadWaiting > 0
      },
      uploadPageRows () {
        let stamp = this.uploadStamp
        let list = this._uploadQueue || []
        if (!stamp && !list.length) {
          return []
        }
        let size = this.uploadPageSize
        let start = (this.uploadPage - 1) * size
        if (start < 0 || start >= list.length) {
          start = 0
        }
        return list.slice(start, start + size)
      },
      taskItems () {
        let stamp = this.uploadStamp
        let items = []
        let seen = {}
        let list = this._uploadQueue || []
        if (!stamp && !list.length && !this.pendingSessions.length) {
          return items
        }
        list.forEach(item => {
          if (item.statusText === '等待上传') {
            return
          }
          if (item.sessionId) {
            seen[item.sessionId] = true
          }
          let state = this.taskState(item.statusText)
          items.push({
            key: item.key,
            name: item.name,
            size: item.size,
            dest: item.dest,
            statusText: item.statusText,
            percent: item.percent || 0,
            state: state.key,
            stateLabel: state.label,
            failMsg: item.failMsg || '',
            session: null,
            queueKey: item.key
          })
        })
        this.pendingSessions.forEach(session => {
          if (seen[session.sessionId]) {
            return
          }
          items.push({
            key: 'pending-' + session.sessionId,
            name: session.name,
            size: session.fileSize,
            dest: '/',
            statusText: '已停止',
            percent: 0,
            state: 'stopped',
            stateLabel: '已停止',
            session: session,
            queueKey: null
          })
        })
        return items
      },
      taskTabs () {
        let items = this.taskItems
        let count = function (state) {
          return items.filter(item => item.state === state).length
        }
        return [
          {key: 'all', label: '全部', count: items.length},
          {key: 'uploading', label: '上传中', count: count('uploading')},
          {key: 'stopped', label: '已停止', count: count('stopped')},
          {key: 'success', label: '上传成功', count: count('success')},
          {key: 'failed', label: '上传失败', count: count('failed')}
        ]
      },
      taskView () {
        if (this.taskFilter === 'all') {
          return this.taskItems
        }
        return this.taskItems.filter(item => item.state === this.taskFilter)
      },
      taskUploadingCount () {
        return this.taskItems.filter(item => item.state === 'uploading').length
      },
      taskResumableCount () {
        let self = this
        let n = 0
        this.taskItems.forEach(function (item) {
          if (item.state !== 'stopped' && item.state !== 'failed') {
            return
          }
          if (!item.queueKey) {
            return
          }
          let task = self.queueByKey(item.queueKey)
          if (task && self._uploadFiles && self._uploadFiles[task.key]) {
            n++
          }
        })
        n += (this._uploadQueue || []).filter(item => item.statusText === '等待上传').length
        return n
      },
      taskRemovableCount () {
        if (this.taskFilter === 'uploading') {
          return 0
        }
        return this.taskView.filter(item => {
          return item.state === 'stopped' || item.state === 'failed' || item.state === 'success'
        }).length
      },
      taskBarUploading () {
        return this.taskUploadingCount > 0
      },
      taskWaveTop () {
        let p = Number(this.taskOverallPercent) || 0
        p = Math.max(8, Math.min(100, p))
        return (100 - p) + '%'
      },
      taskBadge () {
        return this.taskItems.filter(item => item.state !== 'success').length
      },
      showTaskOverall () {
        let stamp = this.uploadStamp
        let list = this._uploadQueue || []
        if (!stamp && !list.length && !this.uploadWaiting && !this.taskItems.length) {
          return false
        }
        return !!(list.length || this.uploadWaiting || this.taskItems.length)
      },
      taskOverallTotal () {
        let stamp = this.uploadStamp
        let list = this._uploadQueue || []
        let total = 0
        let seen = {}
        if (!stamp && !list.length && !this.pendingSessions.length) {
          return 0
        }
        list.forEach(function (item) {
          total += Number(item.size) || 0
          if (item.sessionId) {
            seen[item.sessionId] = true
          }
        })
        this.pendingSessions.forEach(function (session) {
          if (seen[session.sessionId]) {
            return
          }
          total += Number(session.fileSize) || 0
        })
        return total
      },
      taskOverallDone () {
        let stamp = this.uploadStamp
        let list = this._uploadQueue || []
        let done = 0
        if (!stamp && !list.length && !this.pendingSessions.length) {
          return 0
        }
        list.forEach(function (item) {
          let size = Number(item.size) || 0
          let p = Number(item.percent) || 0
          if (item.statusText === '已完成') {
            p = 100
          } else if (item.statusText === '等待上传') {
            p = 0
          }
          if (p < 0) {
            p = 0
          }
          if (p > 100) {
            p = 100
          }
          done += size * p / 100
        })
        // pendingSessions not in queue: stopped → 0 bytes done
        return done
      },
      taskOverallPercent () {
        let total = this.taskOverallTotal
        if (!total) {
          return 0
        }
        return Math.round(this.taskOverallDone * 100 / total)
      }
    },
    created () {
      this._uploadFiles = Object.create(null)
      this._uploadSeen = Object.create(null)
      this._uploadQueue = []
      this._speedSamples = []
      this._initWaiters = []
      this._initFlushTimer = null
      this._initFlushing = false
      this._signedLive = 0
      this._completeBuffer = []
      this._completeFlushTimer = null
      this._completeFlushing = false
      this.clearPersistedUploadConcurrency()
      this.uploadConcurrency = 6
      let self = this
      this._speedTimer = setInterval(function () {
        self.sampleTaskSpeed()
      }, 500)
      this.loadSpace()
      this.loadPending()
      this.loadAdminUsers()
      this.loadAdminGroups()
    },
    watch: {
      '$route.path' (path) {
        if (path === '/disk/recycle') {
          if (!this.inRecycle) {
            this.showSpace(this.recycleSpace())
          }
          return
        }
        if (path === '/disk/index' && (this.inRecycle || this.pendingSpace)) {
          let space = this.pendingSpace || (this.spaces && this.spaces[0])
          this.pendingSpace = null
          if (space) {
            this.showSpace(space)
          }
        }
      }
    },
    mounted () {
      if (this.$refs.folderInput) {
        this.$refs.folderInput.setAttribute('webkitdirectory', '')
        this.$refs.folderInput.setAttribute('directory', '')
      }
    },
    beforeDestroy () {
      if (this._speedTimer) {
        clearInterval(this._speedTimer)
        this._speedTimer = null
      }
      if (this._uploadAutoHintTimer) {
        clearTimeout(this._uploadAutoHintTimer)
        this._uploadAutoHintTimer = null
      }
      this.stopSmartConcurrencyAdapt()
      if (this._flashTimer) {
        clearTimeout(this._flashTimer)
        this._flashTimer = null
      }
    },
    methods: {
      loadSpace () {
        let self = this
        self.$http.get('/disk/space').then(function (res) {
          if (!res || res.status !== 'ok' || !res.data) {
            return
          }
          let spaces = []
          let personal = self.normalizeNode(res.data.personal, 'personal')
          let company = self.normalizeNode(res.data.company, 'company')
          if (personal) {
            spaces.push({key: 'personal', node: personal})
          }
          if (company) {
            spaces.push({key: 'company', node: company})
          }
          spaces.push({key: 'granted', node: {id: 0, name: '授权给我', nodeType: 1, perm: 1}})
          let groups = res.data.groups || []
          self.groupList = groups
          groups.forEach(function (g) {
            if (!g || !g.root) {
              return
            }
            let node = self.normalizeNode(g.root, 'group')
            node.name = g.name || node.name
            spaces.push({key: 'group-' + g.id, node: node, group: g})
          })
          self.spaces = spaces
          if (self.$route.path === '/disk/recycle') {
            self.showSpace(self.recycleSpace())
          } else if (self.activeSpace && self.activeSpace.key) {
            let keep = spaces.filter(function (sp) { return sp.key === self.activeSpace.key })[0]
            if (keep) {
              self.showSpace(keep)
              return
            }
            if (spaces.length) {
              self.showSpace(spaces[0])
            }
          } else if (spaces.length) {
            self.showSpace(spaces[0])
          }
        })
      },
      spaceLabel (sp) {
        if (!sp || !sp.node) {
          return ''
        }
        if (sp.node.name) {
          return sp.node.name
        }
        if (sp.key === 'personal') {
          return '我的文件'
        }
        if (sp.key === 'company') {
          return '公司文件'
        }
        return sp.key
      },
      spaceIcon (sp) {
        if (sp.key === 'granted') {
          return 'ios-people-outline'
        }
        if (sp.key === 'company') {
          return 'ios-briefcase-outline'
        }
        return 'ios-folder-outline'
      },
      avatarText (name) {
        let text = String(name || '').trim()
        return text ? text.charAt(0).toUpperCase() : '?'
      },
      avatarColor (name) {
        let text = String(name || '')
        let sum = 0
        for (let i = 0; i < text.length; i++) {
          sum += text.charCodeAt(i)
        }
        return AVATAR_COLORS[sum % AVATAR_COLORS.length]
      },
      goUp () {
        if (this.breadcrumb.length > 1) {
          this.jumpTo(this.breadcrumb.length - 2)
        }
      },
      aclSubjectName (item) {
        return item.subjectName || item.name || String(item.subjectId || '')
      },
      randomExtractCode () {
        let chars = 'abcdefghijkmnpqrstuvwxyz23456789'
        let code = ''
        for (let i = 0; i < 4; i++) {
          code += chars.charAt(Math.floor(Math.random() * chars.length))
        }
        this.shareForm.extractCode = code
      },
      clearSelection () {
        if (this.$refs.fileTable) {
          this.$refs.fileTable.selectAll(false)
        }
        this.selectedRows = []
        this.selectedRow = null
      },
      onGroupSetting (name) {
        if (name === 'rename') {
          this.openRenameGroup()
        } else if (name === 'transfer') {
          this.openTransferGroup()
        } else if (name === 'dissolve') {
          this.dissolveGroup()
        } else if (name === 'leave') {
          this.leaveGroup()
        }
      },
      roleText (role) {
        if (role === 1) {
          return '群主'
        }
        if (role === 2) {
          return '管理员'
        }
        return '成员'
      },
      normalizeNode (node, key) {
        if (node == null) {
          return null
        }
        if (typeof node === 'number' || typeof node === 'string') {
          return {id: Number(node), name: key === 'personal' ? '我的文件' : '公司文件'}
        }
        return node
      },
      recycleSpace () {
        return {key: 'recycle', node: {id: 0, name: '回收站', nodeType: 1, perm: 1}}
      },
      enterSpace (space) {
        if (space.key !== 'recycle' && this.$route.path === '/disk/recycle') {
          this.pendingSpace = space
          this.$router.push('/disk/index')
          return
        }
        this.showSpace(space)
      },
      showSpace (space) {
        this.activeSpace = space
        this.activeGroup = space && space.group ? space.group : null
        this.breadcrumb = [space.node]
        this.currentFolder = space.node
        this.keyword = ''
        this.listKeyword = ''
        this.selectedRow = null
        this.selectedRows = []
        if (space.key === 'granted') {
          this.loadGranted()
        } else if (space.key === 'recycle') {
          this.loadRecycle()
        } else {
          this.loadList()
        }
      },
      enterFolder (row) {
        if (!row || row.nodeType !== 1) {
          return
        }
        this.breadcrumb.push(row)
        this.currentFolder = row
        this.keyword = ''
        this.listKeyword = ''
        this.selectedRow = null
        this.selectedRows = []
        this.loadList()
      },
      jumpTo (index) {
        this.breadcrumb = this.breadcrumb.slice(0, index + 1)
        this.currentFolder = this.breadcrumb[this.breadcrumb.length - 1]
        this.keyword = ''
        this.listKeyword = ''
        this.selectedRow = null
        this.selectedRows = []
        if (this.activeSpace && this.activeSpace.key === 'recycle') {
          this.loadRecycle()
          return
        }
        this.loadList()
      },
      onNavOpenTarget (data) {
        if (!data || !data.node) {
          return
        }
        let trail = data.trail && data.trail.length ? data.trail.slice() : [data.node]
        this.breadcrumb = trail
        this.currentFolder = data.node
        this.keyword = ''
        this.listKeyword = ''
        this.selectedRow = null
        this.selectedRows = []
        this.loadList()
      },
      loadList () {
        let self = this
        if (self.activeSpace && self.activeSpace.key === 'granted' && self.breadcrumb.length === 1) {
          self.loadGranted()
          return
        }
        if (self.activeSpace && self.activeSpace.key === 'recycle') {
          self.loadRecycle()
          return
        }
        let parentId = self.currentFolder ? self.currentFolder.id : 0
        self.loading = true
        self.$http.get('/disk/list', {params: {parentId: parentId, keyword: self.keyword}}).then(function (res) {
          self.loading = false
          self.selectedRow = null
          self.selectedRows = []
          if (res && res.status === 'ok') {
            self.list = res.data || []
            self.listKeyword = (self.keyword || '').trim()
            self.scrollToFlashRow()
          }
        })
      },
      fileRowClass (row) {
        return this.flashNodeId && row && row.id === this.flashNodeId ? 'disk-row-flash' : ''
      },
      scrollToFlashRow () {
        const self = this
        if (!self.flashNodeId) return
        // 先返回的其他目录列表不处理，避免误清掉待定位的目标
        if (self.flashFolderId && self.list.length && self.list[0].parentId !== self.flashFolderId) return
        const hit = self.list.some(function (r) { return r.id === self.flashNodeId })
        if (!hit) {
          self.flashNodeId = null
          self.flashFolderId = null
          return
        }
        self.$nextTick(function () {
          const table = self.$refs.fileTable && self.$refs.fileTable.$el
          const rowEl = table && table.querySelector('.ivu-table-body .disk-row-flash')
          if (rowEl && rowEl.scrollIntoView) {
            rowEl.scrollIntoView({block: 'center'})
          }
          if (self._flashTimer) clearTimeout(self._flashTimer)
          self._flashTimer = setTimeout(function () {
            self.flashNodeId = null
            self.flashFolderId = null
            self._flashTimer = null
          }, 3000)
        })
      },
      loadRecycle () {
        let self = this
        self.loading = true
        self.$http.get('/disk/recycle/list').then(function (res) {
          self.loading = false
          self.selectedRow = null
          self.selectedRows = []
          if (res && res.status === 'ok') {
            self.list = res.data || []
            self.listKeyword = ''
          }
        }).catch(function () {
          self.loading = false
        })
      },
      restoreRecycle (row) {
        let self = this
        if (!row) {
          return
        }
        self.$http.post('/disk/recycle/restore', {id: row.id}).then(function (res) {
          if (res && res.status === 'ok') {
            self.$Message.success('已恢复')
            self.loadRecycle()
          }
        })
      },
      openPurge (row) {
        if (!row) {
          return
        }
        this.purgeNode = row
        this.showPurgeModal = true
      },
      confirmPurge () {
        let self = this
        let node = self.purgeNode
        if (!node || self.purgeLoading) {
          return
        }
        self.purgeLoading = true
        self.$http.post('/disk/recycle/purge', {id: node.id}).then(function (res) {
          self.purgeLoading = false
          if (res && res.status === 'ok') {
            self.showPurgeModal = false
            self.purgeNode = null
            self.$Message.success('已彻底删除')
            self.loadRecycle()
          }
        }).catch(function () {
          self.purgeLoading = false
        })
      },
      loadGranted () {
        let self = this
        self.loading = true
        self.$http.get('/disk/granted').then(function (res) {
          self.loading = false
          self.selectedRow = null
          self.selectedRows = []
          if (res && res.status === 'ok') {
            self.list = res.data || []
            self.listKeyword = ''
          }
        })
      },
      search () {
        this.loadList()
      },
      onCurrentChange (currentRow) {
        this.selectedRow = currentRow
      },
      onSelectionChange (rows) {
        this.selectedRows = rows || []
        this.selectedRow = this.selectedRows.length === 1 ? this.selectedRows[0] : null
      },
      fileIcon (row) {
        return fileIconOf(row && row.name, row && row.nodeType === 1)
      },
      renderRowMenu (h, row) {
        let self = this
        let actions = []
        let canDownload = row.nodeType === 2 && row.status === 1 && self.hasPerm(row, 2)
        if (row.nodeType === 1) {
          actions.push({name: 'open', label: '打开'})
        }
        if (canDownload) {
          actions.push({name: 'download', label: '下载'})
          actions.push({name: 'preview', label: '预览'})
        }
        if (self.hasPerm(row, 8)) {
          actions.push({name: 'rename', label: '重命名'})
          actions.push({name: 'move', label: '移动'})
          actions.push({name: 'copy', label: '复制'})
        }
        if (self.hasPerm(row, 32)) {
          actions.push({name: 'share', label: '分享'})
        }
        if (self.hasPerm(row, 64)) {
          actions.push({name: 'acl', label: '授权'})
        }
        if (self.hasPerm(row, 16)) {
          actions.push({name: 'delete', label: '删除', divided: true})
        }
        if (!actions.length) {
          return null
        }
        let stop = function (event) {
          event.stopPropagation()
        }
        let quick = []
        let quickBtn = function (name, icon, label) {
          quick.push(h('Tooltip', {props: {content: label, placement: 'top', transfer: true, delay: 300}}, [
            h('span', {
              class: 'disk-op-btn',
              attrs: {role: 'button', 'aria-label': label},
              on: {
                click: function (event) {
                  event.stopPropagation()
                  self.onRowAction(row, name)
                }
              }
            }, [h('Icon', {props: {type: icon, size: 18}})])
          ]))
        }
        if (self.hasPerm(row, 32)) {
          quickBtn('share', 'android-share-alt', '分享')
        }
        if (canDownload) {
          quickBtn('download', 'ios-download-outline', '下载')
        }
        if (self.hasPerm(row, 16)) {
          quickBtn('delete', 'ios-trash-outline', '删除')
        }
        let more = h('Dropdown', {
          props: {transfer: true, trigger: 'click', placement: 'bottom-end'},
          nativeOn: {click: stop},
          on: {
            'on-click': function (name) {
              self.onRowAction(row, name)
            },
            'on-visible-change': function (visible) {
              self.rowMenuOpenId = visible ? row.id : (self.rowMenuOpenId === row.id ? null : self.rowMenuOpenId)
            }
          }
        }, [
          h('span', {class: 'disk-op-btn', attrs: {role: 'button', 'aria-label': '更多操作', title: '更多操作'}}, [
            h('Icon', {props: {type: 'more', size: 18}})
          ]),
          h('Dropdown-menu', {slot: 'list', class: 'disk-op-menu'}, actions.map(function (item) {
            return h('Dropdown-item', {props: {name: item.name, divided: !!item.divided}}, item.label)
          }))
        ])
        quick.push(more)
        return h('div', {
          class: ['disk-row-ops', self.rowMenuOpenId === row.id ? 'is-active' : ''],
          on: {click: stop}
        }, quick)
      },
      onRowAction (row, name) {
        this.selectedRow = row
        if (name === 'open') {
          this.enterFolder(row)
        } else if (name === 'download') {
          this.download(row.id)
        } else if (name === 'preview') {
          this.previewRow(row)
        } else if (name === 'rename') {
          this.openRename(row)
        } else if (name === 'move') {
          this.openMove(row)
        } else if (name === 'copy') {
          this.openCopy(row)
        } else if (name === 'share') {
          this.openShare(row)
        } else if (name === 'acl') {
          this.openAcl(row)
        } else if (name === 'delete') {
          this.confirmDelete([row])
        }
      },
      openRename (row) {
        this.renameNode = row
        this.renameName = row && row.name ? row.name : ''
        this.showRenameModal = true
      },
      openMoveSelected () {
        let rows = this.pickedRows.filter(row => this.hasPerm(row, 8))
        if (!rows.length) {
          return
        }
        this.openMove(rows[0], rows)
      },
      openCopy (row) {
        this.openPicker('copy', row, row ? [row] : [])
      },
      openMove (row, rows) {
        this.openPicker('move', row, rows)
      },
      openPicker (mode, row, rows) {
        this.pickerMode = mode === 'copy' ? 'copy' : 'move'
        this.moveNode = row
        this.moveNodes = rows && rows.length ? rows : (row ? [row] : [])
        this.moveFolders = []
        if (!this.inRecycle && this.breadcrumb && this.breadcrumb.length) {
          this.moveCrumbs = this.breadcrumb.slice()
        } else if (this.moveSpaces.length) {
          this.moveCrumbs = [this.moveSpaces[0].node]
        } else {
          this.moveCrumbs = []
        }
        this.showMoveModal = true
        this.loadMoveFolders()
      },
      switchMoveSpace (space) {
        if (!space || !space.node) {
          return
        }
        this.moveCrumbs = [space.node]
        this.loadMoveFolders()
      },
      jumpMove (index) {
        this.moveCrumbs = this.moveCrumbs.slice(0, index + 1)
        this.loadMoveFolders()
      },
      enterMoveFolder (folder) {
        if (!folder) {
          return
        }
        this.moveCrumbs.push(folder)
        this.loadMoveFolders()
      },
      loadMoveFolders () {
        let self = this
        let folder = self.moveCrumbs.length ? self.moveCrumbs[self.moveCrumbs.length - 1] : null
        if (!folder) {
          self.moveFolders = []
          return
        }
        self.moveFolderLoading = true
        self.$http.get('/disk/list', {params: {parentId: folder.id}}).then(function (res) {
          self.moveFolderLoading = false
          let rows = res && res.status === 'ok' ? (res.data || []) : []
          let movingIds = {}
          ;(self.moveNodes || []).forEach(function (item) {
            if (item && item.nodeType === 1) {
              movingIds[item.id] = true
            }
          })
          self.moveFolders = rows.filter(function (item) {
            return item.nodeType === 1 && !movingIds[item.id]
          })
        }).catch(function () {
          self.moveFolderLoading = false
        })
      },
      confirmMove () {
        let self = this
        let target = self.moveCrumbs.length ? self.moveCrumbs[self.moveCrumbs.length - 1] : null
        let isCopy = self.pickerMode === 'copy'
        let rows = (self.moveNodes || []).filter(function (row) {
          // 复制允许同目录触发，由后端同名校验；移动仍跳过已在目标下的项
          return isCopy || row.parentId !== target.id
        })
        if (!rows.length || !target || self.moveHereDisabled) {
          return
        }
        self.moveSaving = true
        let index = 0
        let api = isCopy ? '/disk/copy' : '/disk/move'
        let okText = isCopy ? '已复制' : '已移动'
        let next = function () {
          if (index >= rows.length) {
            self.moveSaving = false
            self.$Message.success(okText)
            self.showMoveModal = false
            self.loadList()
            return
          }
          let row = rows[index]
          index += 1
          self.$http.post(api, {
            id: row.id,
            targetParentId: target.id
          }).then(function (res) {
            if (res && res.status === 'ok') {
              next()
            } else {
              self.moveSaving = false
            }
          }).catch(function () {
            self.moveSaving = false
          })
        }
        next()
      },
      onRename () {
        let self = this
        if (!self.renameNode) {
          return
        }
        if (!self.renameName || !self.renameName.trim()) {
          self.$Message.warning('请输入新名称')
          return
        }
        self.renameLoading = true
        self.$http.post('/disk/rename', {
          id: self.renameNode.id,
          name: self.renameName.trim()
        }).then(function (res) {
          self.renameLoading = false
          if (res && res.status === 'ok') {
            self.$Message.success('已修改')
            self.showRenameModal = false
            self.loadList()
          }
        }).catch(function () {
          self.renameLoading = false
        })
      },
      hasPerm (node, bit) {
        if (!node || node.perm == null) {
          return false
        }
        return (node.perm & bit) === bit
      },
      formatSize (size) {
        if (size == null || size === '') {
          return '-'
        }
        let n = Number(size)
        if (isNaN(n) || n <= 0) {
          return '0 B'
        }
        let units = ['B', 'KB', 'MB', 'GB', 'TB']
        let i = 0
        while (n >= 1024 && i < units.length - 1) {
          n = n / 1024
          i++
        }
        return (i === 0 ? n : n.toFixed(2)) + ' ' + units[i]
      },
      formatSpeed (bps) {
        let n = Number(bps)
        if (isNaN(n) || n <= 0) {
          return '0 B/s'
        }
        let units = ['B/s', 'KB/s', 'MB/s', 'GB/s', 'TB/s']
        let i = 0
        while (n >= 1024 && i < units.length - 1) {
          n = n / 1024
          i++
        }
        return (i === 0 ? Math.round(n) : n.toFixed(1)) + ' ' + units[i]
      },
      sampleTaskSpeed () {
        let now = new Date().getTime()
        let bytes = this.taskOverallDone
        if (!this._speedSamples) {
          this._speedSamples = []
        }
        let samples = this._speedSamples
        samples.push({t: now, bytes: bytes})
        while (samples.length > 1 && now - samples[0].t > 3000) {
          samples.shift()
        }
        if (this.taskUploadingCount === 0) {
          this.taskSpeedBps = 0
          this._speedSamples = [{t: now, bytes: bytes}]
          return
        }
        if (samples.length < 2) {
          this.taskSpeedBps = 0
          return
        }
        let first = samples[0]
        let last = samples[samples.length - 1]
        let dt = (last.t - first.t) / 1000
        if (dt <= 0) {
          this.taskSpeedBps = 0
          return
        }
        let rate = (last.bytes - first.bytes) / dt
        this.taskSpeedBps = rate > 0 ? rate : 0
      },
      formatTime (value) {
        if (value == null || value === '') {
          return ''
        }
        if (typeof value === 'number') {
          return this.$moment(value).format('YYYY-MM-DD HH:mm:ss')
        }
        let text = String(value)
        if (/^\d{10,}$/.test(text)) {
          return this.$moment(Number(text)).format('YYYY-MM-DD HH:mm:ss')
        }
        return text
      },
      openMkdir () {
        this.mkdirName = ''
        this.showMkdirModal = true
      },
      onMkdir () {
        let self = this
        if (!self.mkdirName || !self.mkdirName.trim()) {
          self.$Message.warning('请输入文件夹名称')
          return
        }
        self.mkdirLoading = true
        self.$http.post('/disk/mkdir', {
          parentId: self.currentFolder ? self.currentFolder.id : 0,
          name: self.mkdirName.trim()
        }).then(function (res) {
          self.mkdirLoading = false
          if (res && res.status === 'ok') {
            self.$Message.success('创建成功')
            self.showMkdirModal = false
            self.loadList()
          }
        }).catch(function () {
          self.mkdirLoading = false
        })
      },
      downloadSelected () {
        let files = this.pickedRows.filter(row => row.nodeType === 2 && row.status === 1 && this.hasPerm(row, 2))
        if (!files.length) {
          this.$Message.warning('请选择已上传完成的文件')
          return
        }
        let self = this
        let pending = files.length
        let urls = []
        files.forEach(function (row) {
          self.$http.get('/disk/downloadUrl', {params: {id: row.id}}).then(function (res) {
            if (res && res.status === 'ok' && res.data && res.data.url) {
              urls.push({url: res.data.url, name: row.name})
            }
            pending -= 1
            if (pending === 0) {
              if (!urls.length) {
                return
              }
              urls.forEach(function (item) {
                self.saveDownload(item.url, item.name)
              })
              self.$Message.success('开始下载 ' + urls.length + ' 个文件')
            }
          }).catch(function () {
            pending -= 1
            if (pending === 0 && urls.length) {
              urls.forEach(function (item) {
                self.saveDownload(item.url, item.name)
              })
            }
          })
        })
      },
      saveDownload (url) {
        let frame = document.createElement('iframe')
        frame.style.display = 'none'
        frame.src = url
        document.body.appendChild(frame)
        setTimeout(function () {
          if (frame.parentNode) {
            frame.parentNode.removeChild(frame)
          }
        }, 60000)
      },
      download (id) {
        let self = this
        self.$http.get('/disk/downloadUrl', {params: {id: id}}).then(function (res) {
          if (res && res.status === 'ok' && res.data && res.data.url) {
            self.openUrl(res.data.url)
          }
        })
      },
      previewRow (row) {
        this.selectedRow = row
        this.previewSelected()
      },
      previewSelected () {
        let row = this.selectedRow
        if (!row || row.nodeType !== 2) {
          this.$Message.warning('请选择文件')
          return
        }
        let self = this
        self.$http.get('/disk/previewUrl', {params: {id: row.id}}).then(function (res) {
          if (res && res.status === 'ok' && res.data && (res.data.url || res.data.engine === 'aliyun')) {
            self.resetPreview()
            self.previewUrl = res.data.url || ''
            self.previewType = self.resolvePreviewType(row)
            self.previewExt = self.resolveExt(row)
            self.previewName = row.name
            self.previewDownloadId = row.id
            if (self.previewType === 'office' && res.data.engine === 'aliyun') {
              self.openAliyunOffice(res.data)
            } else {
              self.previewEngine = 'oss'
              self.showPreviewModal = true
              if (self.previewType === 'pdf') {
                self.loadPreviewPdf()
              } else if (self.isTextPreview()) {
                self.loadPreviewText()
              }
            }
          }
        })
      },
      resetPreview () {
        this.destroyAliyunOffice()
        this.revokePreviewBlob()
        this.previewUrl = ''
        this.previewType = ''
        this.previewExt = ''
        this.previewName = ''
        this.previewDownloadId = null
        this.previewLoading = false
        this.previewError = ''
        this.previewText = ''
        this.previewHtml = ''
        this.previewRows = []
        this.previewEmpty = false
        this.jsonError = false
        this.mediaError = false
      },
      resolveExt (row) {
        let ext = (row.ext || '').toLowerCase().replace(/^\./, '')
        if (ext) {
          return ext
        }
        let name = row.name || ''
        let index = name.lastIndexOf('.')
        if (index >= 0 && index < name.length - 1) {
          return name.substring(index + 1).toLowerCase()
        }
        return ''
      },
      resolvePreviewType (row) {
        let ext = this.resolveExt(row)
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'].indexOf(ext) >= 0) {
          return 'image'
        }
        if (ext === 'pdf') {
          return 'pdf'
        }
        if (['mp4', 'webm', 'ogv', 'mov', 'm4v'].indexOf(ext) >= 0) {
          return 'video'
        }
        if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].indexOf(ext) >= 0) {
          return 'audio'
        }
        if (['md', 'markdown'].indexOf(ext) >= 0) {
          return 'markdown'
        }
        if (ext === 'json') {
          return 'json'
        }
        if (['csv', 'tsv'].indexOf(ext) >= 0) {
          return 'csv'
        }
        if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].indexOf(ext) >= 0) {
          return 'office'
        }
        if (['txt', 'text', 'log', 'xml', 'html', 'htm', 'css', 'js', 'yml', 'yaml',
          'ini', 'properties', 'sql', 'conf', 'cfg'].indexOf(ext) >= 0) {
          return 'text'
        }
        return ''
      },
      stopPreviewMedia () {
        let players = [this.$refs.previewVideo, this.$refs.previewAudio]
        for (let i = 0; i < players.length; i++) {
          let player = players[i]
          if (!player) {
            continue
          }
          player.pause()
          player.removeAttribute('src')
          player.load()
        }
        this.previewUrl = ''
      },
      onPreviewVisible (visible) {
        if (!visible) {
          this.stopPreviewMedia()
          this.revokePreviewBlob()
          this.destroyAliyunOffice()
        }
      },
      openAliyunOffice (data) {
        let self = this
        self.previewEngine = 'aliyun'
        self.previewWebofficeUrl = data.webofficeUrl
        self.previewAccessToken = data.accessToken
        self.previewRefreshToken = data.refreshToken
        self.showPreviewModal = true
        self.loadAliyunSdk().then(function () {
          self.$nextTick(function () {
            if (!self.$refs.aliyunOffice) {
              self.$nextTick(function () {
                self.mountAliyunOffice()
              })
              return
            }
            self.mountAliyunOffice()
          })
        }).catch(function () {
          self.previewError = '阿里云预览组件加载失败'
        })
      },
      loadAliyunSdk () {
        if (window.aliyun && window.aliyun.config) {
          return Promise.resolve()
        }
        if (this._aliyunSdkPromise) {
          return this._aliyunSdkPromise
        }
        this._aliyunSdkPromise = new Promise(function (resolve, reject) {
          let script = document.createElement('script')
          script.src = 'https://g.alicdn.com/IMM/office-js/1.1.19/aliyun-web-office-sdk.min.js'
          script.onload = function () { resolve() }
          script.onerror = function () { reject() }
          document.head.appendChild(script)
        })
        return this._aliyunSdkPromise
      },
      mountAliyunOffice () {
        let self = this
        let mount = self.$refs.aliyunOffice
        if (!mount || !window.aliyun || !window.aliyun.config) {
          self.previewError = '阿里云预览组件加载失败'
          return
        }
        let ins = window.aliyun.config({
          mount: mount,
          url: self.previewWebofficeUrl,
          refreshToken: function () {
            return self.refreshAliyunOffice()
          }
        })
        ins.setToken({
          token: self.previewAccessToken,
          timeout: 25 * 60 * 1000
        })
        self._aliyunOffice = ins
      },
      refreshAliyunOffice () {
        let self = this
        return self.$http.post('/disk/previewRefresh', {
          accessToken: self.previewAccessToken,
          refreshToken: self.previewRefreshToken
        }).then(function (res) {
          if (!res || res.status !== 'ok' || !res.data || !res.data.accessToken) {
            return Promise.reject()
          }
          self.previewAccessToken = res.data.accessToken
          if (res.data.refreshToken) {
            self.previewRefreshToken = res.data.refreshToken
          }
          return {
            token: res.data.accessToken,
            timeout: 25 * 60 * 1000
          }
        })
      },
      destroyAliyunOffice () {
        let ins = this._aliyunOffice
        this._aliyunOffice = null
        if (ins && ins.destroy) {
          try {
            ins.destroy()
          } catch (e) {}
        }
        this.previewEngine = ''
        this.previewWebofficeUrl = ''
        this.previewAccessToken = ''
        this.previewRefreshToken = ''
      },
      revokePreviewBlob () {
        if (this.previewBlobUrl) {
          URL.revokeObjectURL(this.previewBlobUrl)
          this.previewBlobUrl = ''
        }
      },
      loadPreviewPdf () {
        let self = this
        let maxSize = 40 * 1024 * 1024
        let tooLarge = false
        self.previewLoading = true
        self.previewError = ''
        self.revokePreviewBlob()
        let xhr = new XMLHttpRequest()
        xhr.open('GET', self.previewUrl, true)
        xhr.responseType = 'arraybuffer'
        xhr.onreadystatechange = function () {
          if (xhr.readyState !== 2) {
            return
          }
          let headerLength = Number(xhr.getResponseHeader('Content-Length'))
          if (!isNaN(headerLength) && headerLength > maxSize) {
            tooLarge = true
            xhr.abort()
          }
        }
        xhr.onload = function () {
          if (xhr.status < 200 || xhr.status >= 300) {
            self.previewLoading = false
            self.previewError = '预览加载失败，请下载'
            return
          }
          let blob = new Blob([xhr.response], {type: 'application/pdf'})
          self.previewBlobUrl = URL.createObjectURL(blob)
          self.previewLoading = false
        }
        xhr.onerror = function () {
          self.previewLoading = false
          self.previewError = '预览加载失败，请下载'
        }
        xhr.onabort = function () {
          if (tooLarge) {
            self.previewLoading = false
            self.previewError = '文件过大，请下载'
          }
        }
        xhr.send()
      },
      isTextPreview () {
        return ['markdown', 'json', 'csv', 'text'].indexOf(this.previewType) >= 0
      },
      onMediaError () {
        this.mediaError = true
      },
      loadPreviewText () {
        let self = this
        let maxSize = 2 * 1024 * 1024
        let tooLarge = false
        self.previewLoading = true
        self.previewError = ''
        self.previewText = ''
        self.previewHtml = ''
        self.previewRows = []
        self.previewEmpty = false
        self.jsonError = false
        let xhr = new XMLHttpRequest()
        xhr.open('GET', self.previewUrl, true)
        xhr.responseType = 'arraybuffer'
        xhr.onreadystatechange = function () {
          if (xhr.readyState !== 2) {
            return
          }
          let headerLength = Number(xhr.getResponseHeader('Content-Length'))
          if (!isNaN(headerLength) && headerLength > maxSize) {
            tooLarge = true
            xhr.abort()
          }
        }
        xhr.onload = function () {
          if (xhr.status < 200 || xhr.status >= 300) {
            self.previewLoading = false
            self.previewError = '预览加载失败，请下载'
            return
          }
          if (self.isSpreadsheetBytes(xhr.response)) {
            self.loadPreviewSheet()
            return
          }
          let text = self.decodePreviewBytes(xhr.response)
          let bodyLength = Number(xhr.getResponseHeader('Content-Length'))
          if (isNaN(bodyLength) || bodyLength <= 0) {
            bodyLength = self.byteLength(text)
          }
          if (bodyLength > maxSize) {
            self.previewLoading = false
            self.previewError = '文件过大，请下载'
            return
          }
          self.previewText = text
          self.renderPreviewText()
          self.previewLoading = false
        }
        xhr.onerror = function () {
          self.previewLoading = false
          self.previewError = '预览加载失败，请下载'
        }
        xhr.onabort = function () {
          if (tooLarge) {
            self.previewLoading = false
            self.previewError = '文件过大，请下载'
          }
        }
        xhr.send()
      },
      isSpreadsheetBytes (buffer) {
        let bytes = new Uint8Array(buffer || [])
        if (bytes.length >= 8 && bytes[0] === 208 && bytes[1] === 207 && bytes[2] === 17 && bytes[3] === 224) {
          return true
        }
        return bytes.length >= 4 && bytes[0] === 80 && bytes[1] === 75
      },
      loadPreviewSheet () {
        let self = this
        self.previewType = 'csv'
        self.$http.get('/disk/previewTable', {params: {id: self.previewDownloadId}}).then(function (res) {
          self.previewLoading = false
          if (res && res.status === 'ok') {
            self.previewRows = res.data || []
            self.previewEmpty = !self.previewRows.length
            return
          }
          self.previewError = (res && res.msg) || '请下载后查看'
        }).catch(function () {
          self.previewLoading = false
          self.previewError = '预览加载失败，请下载'
        })
      },
      decodePreviewBytes (buffer) {
        let bytes = new Uint8Array(buffer || [])
        if (bytes.length >= 3 && bytes[0] === 239 && bytes[1] === 187 && bytes[2] === 191) {
          bytes = bytes.subarray(3)
        }
        if (!window.TextDecoder) {
          return ''
        }
        try {
          return new TextDecoder('utf-8', {fatal: true}).decode(bytes)
        } catch (e) {
          try {
            return new TextDecoder('gbk').decode(bytes)
          } catch (e2) {
            return new TextDecoder('utf-8').decode(bytes)
          }
        }
      },
      byteLength (text) {
        let value = String(text == null ? '' : text)
        if (window.Blob) {
          return new Blob([value]).size
        }
        return value.length
      },
      renderPreviewText () {
        if (this.previewType === 'markdown') {
          this.previewHtml = this.renderMarkdown(this.previewText)
          return
        }
        if (this.previewType === 'json') {
          try {
            this.previewText = JSON.stringify(JSON.parse(this.previewText), null, 2)
          } catch (e) {
            this.jsonError = true
          }
          return
        }
        if (this.previewType === 'csv') {
          this.previewRows = this.parseTable(this.previewText)
          this.previewEmpty = !this.previewRows.length
        }
      },
      parseTable (text) {
        let content = String(text == null ? '' : text).replace(/^\uFEFF/, '')
        if (!content.replace(/\s/g, '')) {
          return []
        }
        let lines = content.replace(/\r\n?/g, '\n').split('\n')
        while (lines.length && !lines[lines.length - 1].trim()) {
          lines.pop()
        }
        if (this.previewExt === 'tsv') {
          return lines.map(function (line) {
            return line.split('\t')
          })
        }
        return this.parseCsv(lines.join('\n'))
      },
      parseCsv (text) {
        let rows = []
        let row = []
        let field = ''
        let inQuotes = false
        let index = 0
        while (index < text.length) {
          let char = text.charAt(index)
          if (inQuotes) {
            if (char === '"') {
              if (text.charAt(index + 1) === '"') {
                field += '"'
                index += 2
              } else {
                inQuotes = false
                index++
              }
            } else {
              field += char
              index++
            }
            continue
          }
          if (char === '"' && field === '') {
            inQuotes = true
            index++
            continue
          }
          if (char === ',') {
            row.push(field)
            field = ''
            index++
            continue
          }
          if (char === '\n') {
            row.push(field)
            rows.push(row)
            row = []
            field = ''
            index++
            continue
          }
          field += char
          index++
        }
        if (field !== '' || row.length) {
          row.push(field)
          rows.push(row)
        }
        return rows
      },
      escapeHtml (text) {
        return String(text == null ? '' : text).
          replace(/&/g, '&amp;').
          replace(/</g, '&lt;').
          replace(/>/g, '&gt;').
          replace(/"/g, '&quot;').
          replace(/'/g, '&#39;')
      },
      safeUrl (url) {
        let value = String(url == null ? '' : url).trim()
        if (/^(https?:|mailto:|\/|\.\/|\.\.\/|#)/i.test(value)) {
          return value
        }
        return '#'
      },
      attrValue (text) {
        return String(text == null ? '' : text).
          replace(/&quot;|&#34;|&#x0*22;|&#39;|&#x0*27;|"|'/gi, '')
      },
      renderInline (text) {
        let self = this
        let segments = String(text == null ? '' : text).split('`')
        let html = ''
        for (let i = 0; i < segments.length; i++) {
          if (i % 2 === 1) {
            html += '<code>' + segments[i] + '</code>'
          } else {
            html += self.renderInlineFormat(segments[i])
          }
        }
        return html
      },
      renderInlineFormat (text) {
        let self = this
        let html = text
        html = html.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, function (match, alt, src) {
          return '<img src="' + self.attrValue(self.safeUrl(src)) + '" alt="' + self.attrValue(alt) + '">'
        })
        html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, function (match, label, href) {
          return '<a href="' + self.attrValue(self.safeUrl(href)) + '" target="_blank" rel="noopener">' + label + '</a>'
        })
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>')
        return html
      },
      renderMarkdown (text) {
        let self = this
        let lines = self.escapeHtml(text).replace(/\r\n?/g, '\n').split('\n')
        let html = []
        let paragraph = []
        let quoteLines = []
        let listType = ''
        let codeLines = []
        let inCode = false
        let index = 0
        let flushParagraph = function () {
          if (paragraph.length) {
            html.push('<p>' + self.renderInline(paragraph.join(' ')) + '</p>')
            paragraph = []
          }
        }
        let flushQuote = function () {
          if (quoteLines.length) {
            html.push('<blockquote>' + self.renderInline(quoteLines.join(' ')) + '</blockquote>')
            quoteLines = []
          }
        }
        let flushList = function () {
          if (listType) {
            html.push('</' + listType + '>')
            listType = ''
          }
        }
        while (index < lines.length) {
          let line = lines[index]
          if (/^\s*```/.test(line)) {
            if (inCode) {
              html.push('<pre><code>' + codeLines.join('\n') + '</code></pre>')
              codeLines = []
              inCode = false
            } else {
              flushParagraph()
              flushList()
              flushQuote()
              inCode = true
            }
            index++
            continue
          }
          if (inCode) {
            codeLines.push(line)
            index++
            continue
          }
          if (line.indexOf('|') >= 0 && index + 1 < lines.length && self.isTableSeparator(lines[index + 1])) {
            flushParagraph()
            flushList()
            flushQuote()
            let rows = [line]
            index += 2
            while (index < lines.length && lines[index].indexOf('|') >= 0 && lines[index].trim()) {
              rows.push(lines[index])
              index++
            }
            html.push(self.renderTable(rows))
            continue
          }
          if (!line.trim()) {
            flushParagraph()
            flushList()
            flushQuote()
            index++
            continue
          }
          let heading = /^(#{1,6})\s+(.*)$/.exec(line)
          if (heading) {
            flushParagraph()
            flushList()
            flushQuote()
            html.push('<h' + heading[1].length + '>' + self.renderInline(heading[2].trim()) +
              '</h' + heading[1].length + '>')
            index++
            continue
          }
          if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
            flushParagraph()
            flushList()
            flushQuote()
            html.push('<hr>')
            index++
            continue
          }
          let quote = /^\s*&gt;\s?(.*)$/.exec(line)
          if (quote) {
            flushParagraph()
            flushList()
            quoteLines.push(quote[1])
            index++
            continue
          }
          flushQuote()
          let unordered = /^\s*[-*+]\s+(.*)$/.exec(line)
          if (unordered) {
            flushParagraph()
            if (listType !== 'ul') {
              flushList()
              html.push('<ul>')
              listType = 'ul'
            }
            html.push('<li>' + self.renderInline(unordered[1]) + '</li>')
            index++
            continue
          }
          let ordered = /^\s*\d+\.\s+(.*)$/.exec(line)
          if (ordered) {
            flushParagraph()
            if (listType !== 'ol') {
              flushList()
              html.push('<ol>')
              listType = 'ol'
            }
            html.push('<li>' + self.renderInline(ordered[1]) + '</li>')
            index++
            continue
          }
          flushList()
          paragraph.push(line.trim())
          index++
        }
        if (inCode) {
          html.push('<pre><code>' + codeLines.join('\n') + '</code></pre>')
        }
        flushParagraph()
        flushList()
        flushQuote()
        return html.join('\n')
      },
      isTableSeparator (line) {
        if (!line || line.indexOf('-') < 0) {
          return false
        }
        let cells = this.splitTableRow(line)
        if (!cells.length) {
          return false
        }
        for (let i = 0; i < cells.length; i++) {
          if (!/^:?-{2,}:?$/.test(cells[i].replace(/\s/g, ''))) {
            return false
          }
        }
        return true
      },
      splitTableRow (line) {
        let text = String(line == null ? '' : line).trim()
        if (text.charAt(0) === '|') {
          text = text.substring(1)
        }
        if (text.charAt(text.length - 1) === '|') {
          text = text.substring(0, text.length - 1)
        }
        return text.split('|').map(function (cell) {
          return cell.trim()
        })
      },
      renderTable (rows) {
        let self = this
        let head = self.splitTableRow(rows[0])
        let html = ['<table>', '<thead><tr>']
        head.forEach(function (cell) {
          html.push('<th>' + self.renderInline(cell) + '</th>')
        })
        html.push('</tr></thead><tbody>')
        for (let i = 1; i < rows.length; i++) {
          let cells = self.splitTableRow(rows[i])
          html.push('<tr>')
          for (let c = 0; c < head.length; c++) {
            html.push('<td>' + self.renderInline(cells[c] || '') + '</td>')
          }
          html.push('</tr>')
        }
        html.push('</tbody></table>')
        return html.join('')
      },
      openUrl (url) {
        let link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },
      deleteSelected () {
        this.confirmDelete(this.pickedRows)
      },
      confirmDelete (rows) {
        let self = this
        let list = (rows || []).filter(row => self.hasPerm(row, 16))
        if (!list.length) {
          return
        }
        let names = list.map(function (row) { return row.name }).join('、')
        self.$Modal.confirm({
          title: '移入回收站',
          content: '确定把「' + names + '」移入回收站吗？云上的文件会保留，可在回收站恢复。',
          onOk: function () {
            let chain = Promise.resolve()
            list.forEach(function (row) {
              chain = chain.then(function () {
                return self.$http.post('/disk/delete', {id: row.id})
              })
            })
            chain.then(function () {
              self.$Message.success('已移入回收站')
              self.selectedRow = null
              self.selectedRows = []
              self.loadList()
            })
          }
        })
      },
      batchTitle (rows, action) {
        let list = rows || []
        if (list.length === 1) {
          let kind = list[0].nodeType === 2 ? '文件' : '文件夹'
          return '当前' + kind + '：' + list[0].name
        }
        if (list.length > 1) {
          return action + ' ' + list.length + ' 项：' + list.map(function (row) {
            return row.name
          }).join('、')
        }
        return action
      },
      openAcl (row) {
        let rows = row && row.id ? [row] : this.pickedRows.filter(item => this.hasPerm(item, 64))
        if (!rows.length) {
          return
        }
        this.aclNodes = rows
        this.aclNode = rows[0]
        this.aclList = []
        this.aclForm = {subjectType: 1, subjectId: null, subjectIds: [], permBits: [1, 2]}
        this.showAclModal = true
        this.loadAdminUsers()
        this.loadAdminGroups()
        if (rows.length === 1) {
          this.loadAclList()
        }
      },
      loadAclList () {
        let self = this
        if (!self.aclNode) {
          return
        }
        self.aclLoading = true
        self.$http.get('/disk/acl/list', {params: {nodeId: self.aclNode.id}}).then(function (res) {
          self.aclLoading = false
          if (res && res.status === 'ok') {
            self.aclList = res.data || []
          }
        })
      },
      openCreateGroup () {
        this.createGroupName = ''
        this.showCreateGroupModal = true
      },
      onCreateGroup () {
        let self = this
        let name = (self.createGroupName || '').trim()
        if (!name) {
          self.$Message.warning('请输入群名称')
          return
        }
        if (self.createGroupLoading) {
          return
        }
        self.createGroupLoading = true
        self.$http.post('/disk/group/create', {name: name}).then(function (res) {
          self.createGroupLoading = false
          if (res && res.status === 'ok') {
            self.showCreateGroupModal = false
            self.$Message.success('已创建')
            self.loadSpace()
          } else {
            self.$Message.error((res && res.msg) || '创建失败')
          }
        }).catch(function () {
          self.createGroupLoading = false
        })
      },

      activityLinkable (item) {
        if (!item || item.targetType !== 'NODE' || !item.targetId || !item.targetName) return false
        const code = String(item.actionCode || '').toUpperCase()
        return code.indexOf('DELETE') < 0 && code.indexOf('PURGE') < 0
      },
      activitySummaryParts (item) {
        const summary = item.summary || ''
        const mark = '「' + item.targetName + '」'
        const idx = summary.indexOf(mark)
        if (idx < 0) {
          return [
            {text: summary ? summary + ' ' : '', link: false},
            {text: mark, link: true}
          ]
        }
        return [
          {text: summary.slice(0, idx), link: false},
          {text: mark, link: true},
          {text: summary.slice(idx + mark.length), link: false}
        ]
      },
      locateActivity (item) {
        const self = this
        if (self.activityLocating || !self.activeGroup || !self.activeGroup.id || !item) return
        self.activityLocating = true
        self.$http.get('/disk/group/activity/locate', {
          params: {groupId: self.activeGroup.id, nodeId: item.targetId}
        }).then(function (res) {
          self.activityLocating = false
          if (!res || res.status !== 'ok' || !res.data) return
          self.activityPanelOpen = false
          if (self._flashTimer) {
            clearTimeout(self._flashTimer)
            self._flashTimer = null
          }
          self.flashNodeId = res.data.targetNodeId || null
          self.flashFolderId = res.data.node ? res.data.node.id : null
          self.onNavOpenTarget(res.data)
        }).catch(function () {
          self.activityLocating = false
        })
      },
      openActivityPanel () {
        if (!this.activeGroup || !this.activeGroup.id) {
          this.$Message.warning('请先进入群空间')
          return
        }
        this.activityPanelOpen = true
        this.activityPageIndex = 1
        this.activityNoMore = false
        this.activityList = []
        this.loadActivity(true)
      },
      loadActivity (reset) {
        const self = this
        if (!self.activeGroup || !self.activeGroup.id) return
        if (self.activityLoading) return
        if (!reset && self.activityNoMore) return
        self.activityLoading = true
        const pageIndex = reset ? 1 : self.activityPageIndex
        self.$http.post('/disk/group/activity/list', {
          groupId: self.activeGroup.id,
          pageIndex: pageIndex,
          pageSize: self.activityPageSize
        }).then(function (res) {
          self.activityLoading = false
          if (!res || res.status !== 'ok') {
            self.$Message.error((res && res.msg) || '加载群动态失败')
            return
          }
          const page = res.data || {}
          const rows = page.data || []
          self.activityTotal = page.totalCount || 0
          if (reset) {
            self.activityList = rows
          } else {
            self.activityList = self.activityList.concat(rows)
          }
          self.activityPageIndex = pageIndex + 1
          if (!rows.length || self.activityList.length >= self.activityTotal) {
            self.activityNoMore = true
          }
        }).catch(function () {
          self.activityLoading = false
          self.$Message.error('加载群动态失败')
        })
      },
      onActivityScroll (e) {
        const el = e && e.target
        if (!el) return
        if (el.scrollTop + el.clientHeight + 40 >= el.scrollHeight) {
          this.loadActivity(false)
        }
      },
      activityDayKey (v) {
        const d = this.parseActivityDate(v)
        if (!d) return '未知'
        const m = d.getMonth() + 1
        const day = d.getDate()
        return d.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day)
      },
      parseActivityDate (v) {
        if (!v) return null
        if (v instanceof Date) return v
        if (typeof v === 'number') return new Date(v)
        const s = String(v).replace('T', ' ').replace(/-/g, '/')
        const d = new Date(s)
        return isNaN(d.getTime()) ? null : d
      },
      formatActivityTime (v) {
        const d = this.parseActivityDate(v)
        if (!d) return ''
        const h = d.getHours()
        const m = d.getMinutes()
        return (h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m)
      },
      openMemberManage () {
        this.memberForm = {subjectType: 1, subjectIds: []}
        this.showMemberModal = true
        this.loadAdminUsers()
        this.loadAdminGroups()
        this.loadGroupMembers()
      },
      loadGroupMembers () {
        let self = this
        if (!self.activeGroup) {
          return
        }
        self.memberLoading = true
        self.$http.get('/disk/group/members', {params: {groupId: self.activeGroup.id}}).then(function (res) {
          self.memberLoading = false
          if (res && res.status === 'ok') {
            self.memberList = res.data || []
          }
        }).catch(function () {
          self.memberLoading = false
        })
      },
      addGroupMembers () {
        let self = this
        let ids = self.memberForm.subjectIds || []
        if (!ids.length) {
          self.$Message.warning(self.memberForm.subjectType === 2 ? '请选择账号组' : '请选择员工')
          return
        }
        if (!self.activeGroup || self.memberSaving) {
          return
        }
        self.memberSaving = true
        self.$http.post('/disk/group/member/add', {
          groupId: self.activeGroup.id,
          subjectType: self.memberForm.subjectType,
          subjectIds: ids
        }).then(function (res) {
          self.memberSaving = false
          if (res && res.status === 'ok') {
            self.memberForm.subjectIds = []
            self.$Message.success('已添加')
            self.loadGroupMembers()
            self.loadSpace()
          } else {
            self.$Message.error((res && res.msg) || '添加失败')
          }
        }).catch(function () {
          self.memberSaving = false
        })
      },
      removeGroupMember (row) {
        let self = this
        if (!self.activeGroup || !row) {
          return
        }
        self.$Modal.confirm({
          title: '移除成员',
          content: '确定移除「' + (row.subjectName || row.subjectId) + '」？',
          onOk: function () {
            self.$http.post('/disk/group/member/remove', {
              groupId: self.activeGroup.id,
              memberId: row.id
            }).then(function (res) {
              if (res && res.status === 'ok') {
                self.$Message.success('已移除')
                self.loadGroupMembers()
                self.loadSpace()
              } else {
                self.$Message.error((res && res.msg) || '移除失败')
              }
            })
          }
        })
      },
      toggleMemberAdmin (row) {
        let self = this
        if (!self.activeGroup || !row || row.subjectType !== 1) {
          return
        }
        let admin = row.role === 2 ? 0 : 1
        self.$http.post('/disk/group/member/setAdmin', {
          groupId: self.activeGroup.id,
          adminUserId: row.subjectId,
          admin: admin
        }).then(function (res) {
          if (res && res.status === 'ok') {
            self.$Message.success('已更新')
            self.loadGroupMembers()
            self.loadSpace()
          } else {
            self.$Message.error((res && res.msg) || '操作失败')
          }
        })
      },
      toggleMemberUpload (row, enabled) {
        let self = this
        if (!self.activeGroup || !row) {
          return
        }
        self.$http.post('/disk/group/member/setUpload', {
          groupId: self.activeGroup.id,
          subjectType: row.subjectType,
          subjectId: row.subjectId,
          uploadEnabled: enabled ? 1 : 0
        }).then(function (res) {
          if (res && res.status === 'ok') {
            row.uploadEnabled = !!enabled
            self.$Message.success('已更新')
            if (row.subjectType === 2 && row._users) {
              self.loadAccountGroupUsers(row)
            }
          } else {
            self.$Message.error((res && res.msg) || '操作失败')
            self.loadGroupMembers()
          }
        }).catch(function () {
          self.loadGroupMembers()
        })
      },
      onMemberExpand (row, status) {
        if (!status || !row || row.subjectType !== 2) {
          return
        }
        this.loadAccountGroupUsers(row)
      },
      loadAccountGroupUsers (row) {
        let self = this
        if (!self.activeGroup || !row || row.subjectType !== 2) {
          return
        }
        self.$set(row, '_usersLoading', true)
        self.$http.get('/disk/group/member/accountUsers', {
          params: {groupId: self.activeGroup.id, adminGroupId: row.subjectId}
        }).then(function (res) {
          self.$set(row, '_usersLoading', false)
          if (res && res.status === 'ok') {
            let list = res.data || []
            list.forEach(function (u) {
              u._adminGroupId = row.subjectId
            })
            self.$set(row, '_users', list)
          } else {
            self.$set(row, '_users', [])
            self.$Message.error((res && res.msg) || '加载失败')
          }
        }).catch(function () {
          self.$set(row, '_usersLoading', false)
          self.$set(row, '_users', [])
        })
      },
      toggleAccountUserUpload (user, enabled) {
        let self = this
        if (!self.activeGroup || !user || user.owner) {
          return
        }
        self.$http.post('/disk/group/member/setUploadOverride', {
          groupId: self.activeGroup.id,
          adminUserId: user.adminUserId,
          uploadEnabled: enabled ? 1 : 0
        }).then(function (res) {
          if (res && res.status === 'ok') {
            user.uploadEnabled = !!enabled
            user.hasOverride = true
            self.$Message.success('已覆盖个人上传权限')
          } else {
            self.$Message.error((res && res.msg) || '操作失败')
            self.refreshAccountUsersFor(user._adminGroupId)
          }
        }).catch(function () {
          self.refreshAccountUsersFor(user._adminGroupId)
        })
      },
      clearAccountUserOverride (user) {
        let self = this
        if (!self.activeGroup || !user) {
          return
        }
        self.$http.post('/disk/group/member/clearUploadOverride', {
          groupId: self.activeGroup.id,
          adminUserId: user.adminUserId
        }).then(function (res) {
          if (res && res.status === 'ok') {
            self.$Message.success('已恢复跟随账号组')
            self.refreshAccountUsersFor(user._adminGroupId)
          } else {
            self.$Message.error((res && res.msg) || '操作失败')
          }
        })
      },
      refreshAccountUsersFor (adminGroupId) {
        let row = (this.memberList || []).filter(function (m) {
          return m.subjectType === 2 && m.subjectId === adminGroupId
        })[0]
        if (row) {
          this.loadAccountGroupUsers(row)
        }
      },
      openRenameGroup () {
        this.renameGroupName = this.activeGroup ? this.activeGroup.name : ''
        this.showRenameGroupModal = true
      },
      onRenameGroup () {
        let self = this
        let name = (self.renameGroupName || '').trim()
        if (!name || !self.activeGroup) {
          self.$Message.warning('请输入群名称')
          return
        }
        self.renameGroupLoading = true
        self.$http.post('/disk/group/rename', {groupId: self.activeGroup.id, name: name}).then(function (res) {
          self.renameGroupLoading = false
          if (res && res.status === 'ok') {
            self.showRenameGroupModal = false
            self.$Message.success('已改名')
            self.loadSpace()
          } else {
            self.$Message.error((res && res.msg) || '改名失败')
          }
        }).catch(function () {
          self.renameGroupLoading = false
        })
      },
      openTransferGroup () {
        this.transferOwnerId = null
        this.showTransferModal = true
        this.loadGroupMembers()
      },
      onTransferGroup () {
        let self = this
        if (!self.activeGroup || !self.transferOwnerId) {
          self.$Message.warning('请选择新群主')
          return
        }
        self.transferLoading = true
        self.$http.post('/disk/group/transfer', {
          groupId: self.activeGroup.id,
          newOwnerAdminId: self.transferOwnerId
        }).then(function (res) {
          self.transferLoading = false
          if (res && res.status === 'ok') {
            self.showTransferModal = false
            self.showMemberModal = false
            self.$Message.success('已转让')
            self.loadSpace()
          } else {
            self.$Message.error((res && res.msg) || '转让失败')
          }
        }).catch(function () {
          self.transferLoading = false
        })
      },
      dissolveGroup () {
        let self = this
        if (!self.activeGroup) {
          return
        }
        self.$Modal.confirm({
          title: '解散群',
          content: '解散后群文件将整体迁入你的个人空间，其他成员失去访问。确定解散？',
          onOk: function () {
            self.$http.post('/disk/group/dissolve', {groupId: self.activeGroup.id}).then(function (res) {
              if (res && res.status === 'ok') {
                self.showMemberModal = false
                self.$Message.success('已解散')
                self.activeGroup = null
                self.loadSpace()
              } else {
                self.$Message.error((res && res.msg) || '解散失败')
              }
            })
          }
        })
      },
      leaveGroup () {
        let self = this
        if (!self.activeGroup) {
          return
        }
        self.$Modal.confirm({
          title: '退群',
          content: '确定退出「' + self.activeGroup.name + '」？',
          onOk: function () {
            self.$http.post('/disk/group/leave', {groupId: self.activeGroup.id}).then(function (res) {
              if (res && res.status === 'ok') {
                self.$Message.success('已退群')
                self.activeGroup = null
                self.loadSpace()
              } else {
                self.$Message.error((res && res.msg) || '退群失败')
              }
            })
          }
        })
      },
      loadAdminUsers () {
        let self = this
        if (self.adminUsers.length || self.adminUserLoading) {
          return
        }
        self.adminUserLoading = true
        self.$http.post('/adminUser/listByCondition', {status: 1}).then(function (res) {
          self.adminUserLoading = false
          let list = []
          if (res && res.status === 'ok' && res.data) {
            list = Array.isArray(res.data) ? res.data : (res.data.data || [])
          }
          self.adminUsers = list
          self.adminUserSelectKey += 1
        }).catch(function () {
          self.adminUserLoading = false
        })
      },
      userLabel (user) {
        let name = user.nickname || user.mobile || user.phone || String(user.id)
        let dingName = user.dingtalkName == null ? '' : String(user.dingtalkName).trim()
        if (dingName && dingName !== name) {
          return name + '（' + dingName + '）'
        }
        return name
      },
      selectAllUsers () {
        this.aclForm.subjectIds = this.aclAdminUsers.map(function (user) {
          return user.id
        })
      },
      selectAllGroups () {
        this.aclForm.subjectIds = this.adminGroups.map(function (group) {
          return group.id
        })
      },
      loadAdminGroups () {
        let self = this
        if (self.adminGroups.length || self.adminGroupLoading) {
          return
        }
        self.adminGroupLoading = true
        self.$http.get('/adminGroup/listByCondition?status=1').then(function (res) {
          self.adminGroupLoading = false
          let list = []
          if (res && res.status === 'ok' && res.data) {
            list = Array.isArray(res.data) ? res.data : (res.data.data || [])
          }
          self.adminGroups = list
          self.adminGroupSelectKey += 1
        }).catch(function () {
          self.adminGroupLoading = false
        })
      },
      onSubjectTypeChange () {
        this.aclForm.subjectId = null
        this.aclForm.subjectIds = []
      },
      saveAcl () {
        let self = this
        let nodes = self.aclNodes && self.aclNodes.length ? self.aclNodes : (self.aclNode ? [self.aclNode] : [])
        if (!nodes.length || self.aclSaving) {
          return
        }
        let ids = self.aclForm.subjectIds || []
        if (!ids.length) {
          self.$Message.warning(self.aclForm.subjectType === 2 ? '请选择权限组' : '请选择员工')
          return
        }
        let bits = self.aclForm.permBits || []
        if (!bits.length) {
          self.$Message.warning('请选择权限')
          return
        }
        let perm = 0
        bits.forEach(function (bit) {
          perm = perm | Number(bit)
        })
        self.aclSaving = true
        let nodeIds = nodes.map(function (node) {
          return node.id
        })
        self.$http.post('/disk/acl/saveBatch', {
          nodeIds: nodeIds,
          subjectType: self.aclForm.subjectType,
          subjectIds: ids,
          perm: perm
        }).then(function (res) {
          self.aclSaving = false
          if (res && res.status === 'ok') {
            self.$Message.success('保存成功')
            self.aclForm.subjectIds = []
            if (nodes.length === 1) {
              self.loadAclList()
            }
          }
        }).catch(function () {
          self.aclSaving = false
        })
      },
      removeAcl (row) {
        let self = this
        if (!self.aclNode) {
          return
        }
        self.$http.post('/disk/acl/save', {
          nodeId: self.aclNode.id,
          subjectType: row.subjectType,
          subjectId: row.subjectId,
          perm: 0
        }).then(function (res) {
          if (res && res.status === 'ok') {
            self.$Message.success('已移除')
            self.loadAclList()
          }
        })
      },
      permText (perm) {
        if (perm == null || perm === 0) {
          return ''
        }
        let names = []
        ;(this.permOptions || []).forEach(function (item) {
          if ((perm & item.value) === item.value) {
            names.push(item.label)
          }
        })
        return names.length ? names.join('、') : String(perm)
      },
      openShare (row) {
        let rows = row && row.id ? [row] : this.pickedRows.filter(item => this.hasPerm(item, 32))
        if (!rows.length) {
          return
        }
        this.shareNodes = rows
        this.shareNode = rows[0]
        this.shareList = []
        this.shareForm = {expireType: 'forever', customExpire: '', customExpireDate: '', extractCode: ''}
        this.showShareModal = true
        if (rows.length === 1) {
          this.loadShareList()
        }
      },
      loadShareList () {
        let self = this
        if (!self.shareNode) {
          return
        }
        self.$http.get('/disk/share/list', {params: {nodeId: self.shareNode.id}}).then(function (res) {
          if (res && res.status === 'ok') {
            let name = self.shareNode.name
            self.shareList = (res.data || []).map(function (item) {
              if (!item.nodeName) {
                item.nodeName = name
              }
              return item
            })
          }
        })
      },
      openShareLog (row) {
        let self = this
        if (!row) {
          return
        }
        self.shareLogList = []
        self.shareLogLoading = true
        self.showShareLogModal = true
        self.$http.get('/disk/share/access', {params: {id: row.id}}).then(function (res) {
          self.shareLogLoading = false
          if (res && res.status === 'ok') {
            self.shareLogList = res.data || []
          }
        }).catch(function () {
          self.shareLogLoading = false
        })
      },
      onCustomExpire (val) {
        this.shareForm.customExpire = val
      },
      createShare () {
        let self = this
        let nodes = self.shareNodes && self.shareNodes.length ? self.shareNodes : (self.shareNode ? [self.shareNode] : [])
        if (!nodes.length) {
          return
        }
        let expireTime = ''
        if (self.shareForm.expireType === 'custom') {
          if (!self.shareForm.customExpire) {
            self.$Message.warning('请选择到期时间')
            return
          }
          expireTime = self.shareForm.customExpire
        } else if (self.shareForm.expireType !== 'forever') {
          expireTime = self.$moment().add(Number(self.shareForm.expireType), 'days').format('YYYY-MM-DD HH:mm:ss')
        }
        if (self.shareForm.extractCode && !/^\S{4}$/.test(self.shareForm.extractCode)) {
          self.$Message.warning('提取码为4位')
          return
        }
        self.shareSaving = true
        let index = 0
        let created = []
        let next = function () {
          if (index >= nodes.length) {
            self.shareSaving = false
            self.$Message.success('已生成 ' + created.length + ' 条链接')
            self.shareForm.customExpire = ''
            self.shareForm.customExpireDate = ''
            self.shareForm.extractCode = ''
            if (nodes.length === 1) {
              self.loadShareList()
            } else {
              self.shareList = created
            }
            if (created.length === 1) {
              self.copyText(self.buildShareUrl(created[0].token, created[0].extractCode || ''))
            }
            return
          }
          let node = nodes[index]
          index += 1
          self.$http.post('/disk/share/create', {
            nodeId: node.id,
            expireTime: expireTime || null,
            extractCode: self.shareForm.extractCode || null
          }).then(function (res) {
            if (res && res.status === 'ok' && res.data) {
              res.data.nodeName = node.name
              created.push(res.data)
              next()
            } else {
              self.shareSaving = false
              if (created.length) {
                self.shareList = created
              }
            }
          }).catch(function () {
            self.shareSaving = false
            if (created.length) {
              self.shareList = created
            }
          })
        }
        next()
      },
      revokeShare (row) {
        let self = this
        self.$Modal.confirm({
          title: '提示',
          content: '确定撤销该分享链接吗？',
          onOk: function () {
            self.$http.post('/disk/share/revoke', {id: row.id}).then(function (res) {
              if (res && res.status === 'ok') {
                self.$Message.success('已撤销')
                self.loadShareList()
              }
            })
          }
        })
      },
      buildShareUrl (token, extractCode) {
        let url = window.location.origin + window.location.pathname + '#/disk/share?token=' + (token || '')
        if (extractCode) {
          url += '&extractCode=' + extractCode
        }
        return url
      },
      copyText (text) {
        let input = document.createElement('textarea')
        input.value = text
        document.body.appendChild(input)
        input.select()
        try {
          document.execCommand('copy')
          this.$Message.success('已复制')
        } catch (e) {
          this.$Message.error('复制失败，请手动复制')
        }
        document.body.removeChild(input)
      },
      chooseUpload () {
        if (!this.canUpload) {
          return
        }
        this.resumeSession = null
        this.pendingParentId = this.currentFolder ? this.currentFolder.id : 0
        if (!this.uploading && !this.scanning) {
          this._uploadQueue = []
          this.uploadPage = 1
          this.uploadWaiting = 0
          this.scanCount = 0
          this._uploadFiles = Object.create(null)
          this._uploadSeen = Object.create(null)
          this.commitQueue()
        }
        this.showUploadModal = true
      },
      pickFiles () {
        if (this.scanning) {
          return
        }
        this.$refs.fileInput.value = ''
        this.$refs.fileInput.click()
      },
      pickFolder () {
        if (this.scanning) {
          return
        }
        this.$refs.folderInput.value = ''
        this.$refs.folderInput.click()
      },
      resumeUpload (row) {
        this.resumeSession = row
        this.$refs.fileInput.value = ''
        this.$refs.fileInput.click()
      },
      onDropFiles (event) {
        let items = event.dataTransfer && event.dataTransfer.items
        if (items && items.length && items[0].webkitGetAsEntry) {
          let self = this
          let pending = []
          for (let i = 0; i < items.length; i++) {
            let entry = items[i].webkitGetAsEntry()
            if (entry) {
              pending.push(this.readDroppedEntry(entry, ''))
            }
          }
          Promise.all(pending).then(function (groups) {
            let files = []
            groups.forEach(function (group) {
              files = files.concat(group)
            })
            self.addQueuedFiles(files)
          })
          return
        }
        this.addQueuedFiles(event.dataTransfer ? event.dataTransfer.files : [])
      },
      readDroppedEntry (entry, dir) {
        let self = this
        return new Promise(function (resolve) {
          if (entry.isFile) {
            entry.file(function (file) {
              file._diskDir = dir
              resolve([file])
            }, function () {
              resolve([])
            })
            return
          }
          if (!entry.isDirectory) {
            resolve([])
            return
          }
          let reader = entry.createReader()
          let nextDir = dir ? dir + '/' + entry.name : entry.name
          let all = []
          let readBatch = function () {
            reader.readEntries(function (entries) {
              if (!entries.length) {
                Promise.all(all).then(function (groups) {
                  let files = []
                  groups.forEach(function (group) {
                    files = files.concat(group)
                  })
                  resolve(files)
                })
                return
              }
              for (let i = 0; i < entries.length; i++) {
                all.push(self.readDroppedEntry(entries[i], nextDir))
              }
              readBatch()
            }, function () {
              resolve([])
            })
          }
          readBatch()
        })
      },
      onFilePicked (event) {
        let picked = event.target.files
        let files = []
        if (picked) {
          for (let i = 0; i < picked.length; i++) {
            files.push(picked[i])
          }
        }
        let resume = this.resumeSession
        this.resumeSession = null
        event.target.value = ''
        if (!files.length) {
          return
        }
        if (resume) {
          let file = files[0]
          if (file.size !== resume.fileSize) {
            this.$Message.warning('请选择与原任务相同的文件')
            return
          }
          this.resumeTask(resume, file)
          return
        }
        this.addQueuedFiles(files)
      },
      addQueuedFiles (files) {
        if (!files || !files.length) {
          return
        }
        if (!this._scanWait) {
          this._scanWait = []
        }
        this._scanWait.push(files)
        this.showUploadModal = true
        if (!this.scanning) {
          this._rejectedNames = 0
          this.scanNext()
        }
      },
      scanNext () {
        if (!this._scanWait || !this._scanWait.length) {
          this.scanning = false
          if (this._rejectedNames) {
            this.$Message.warning('已跳过 ' + this._rejectedNames + ' 个不符合命名规范的文件')
            this._rejectedNames = 0
          }
          return
        }
        let files = this._scanWait.shift()
        this.scanning = true
        this.scanChunk(files, 0, [])
      },
      scanChunk (files, index, batch) {
        let self = this
        if (!self._uploadFiles) {
          self._uploadFiles = Object.create(null)
        }
        if (!self._uploadSeen) {
          self._uploadSeen = Object.create(null)
        }
        let limit = 10000
        let end = Math.min(index + 400, files.length)
        let stop = false
        for (let i = index; i < end; i++) {
          if ((self._uploadQueue ? self._uploadQueue.length : 0) + batch.length >= limit) {
            stop = true
            break
          }
          let file = files[i]
          if (!file || file.name === '.DS_Store') {
            continue
          }
          let rel = file._diskDir != null ? file._diskDir : (file.webkitRelativePath || '')
          let dir = ''
          if (file._diskDir != null) {
            dir = file._diskDir
          } else if (rel && rel.indexOf('/') >= 0) {
            dir = rel.substring(0, rel.lastIndexOf('/'))
          }
          if (!self.validUploadName(file.name) || !self.validUploadDir(dir)) {
            self._rejectedNames = (self._rejectedNames || 0) + 1
            continue
          }
          let identity = (dir || '/') + '/' + file.name + ':' + file.size
          if (self._uploadSeen[identity]) {
            continue
          }
          self._uploadSeen[identity] = 1
          let typeName = file.type || ''
          if (!typeName && file.name.indexOf('.') > 0) {
            typeName = file.name.split('.').pop()
          }
          let key = identity + ':' + i
          self._uploadFiles[key] = file
          batch.push({
            key: key,
            identity: identity,
            name: file.name,
            dir: dir || '/',
            typeName: typeName || '-',
            size: file.size,
            percent: 0,
            statusText: '等待上传'
          })
        }
        self.scanCount = (self._uploadQueue ? self._uploadQueue.length : 0) + batch.length
        if (!stop && end < files.length) {
          setTimeout(function () {
            self.scanChunk(files, end, batch)
          }, 0)
          return
        }
        if (batch.length) {
          if (!self._uploadQueue) {
            self._uploadQueue = []
          }
          self._uploadQueue = self._uploadQueue.concat(batch)
          self.uploadWaiting += batch.length
        }
        self.commitQueue()
        if (stop) {
          self._scanWait = []
          self.$Message.warning('待上传列表最多允许添加 10000 个文件，超出部分被丢弃，如果要完整上传，请将文件打包成 Zip 包，上传后解压。')
        }
        self.scanNext()
      },
      validUploadName (name) {
        if (!name || name === '.' || name === '..' || name.indexOf('/') >= 0 || name.indexOf('\\') >= 0) {
          return false
        }
        let bytes
        try {
          bytes = new TextEncoder().encode(name)
        } catch (e) {
          return false
        }
        if (bytes.length < 1 || bytes.length > 1023) {
          return false
        }
        try {
          return new TextDecoder('utf-8', {fatal: true}).decode(bytes) === name
        } catch (e) {
          return false
        }
      },
      validUploadDir (dir) {
        if (!dir) {
          return true
        }
        let parts = String(dir).split(/[/\\]/)
        for (let i = 0; i < parts.length; i++) {
          if (!parts[i]) {
            continue
          }
          if (!this.validUploadName(parts[i])) {
            return false
          }
        }
        return true
      },
      commitQueue () {
        let list = this._uploadQueue || []
        this.uploadTotal = list.length
        let maxPage = Math.max(1, Math.ceil(list.length / this.uploadPageSize) || 1)
        if (this.uploadPage > maxPage) {
          this.uploadPage = maxPage
        }
        this.uploadStamp++
      },
      removeQueued (row) {
        if (this._uploadFiles) {
          delete this._uploadFiles[row.key]
        }
        if (this._uploadSeen && row.identity) {
          delete this._uploadSeen[row.identity]
        }
        if (row.statusText === '等待上传' && this.uploadWaiting > 0) {
          this.uploadWaiting--
        }
        this._uploadQueue = (this._uploadQueue || []).filter(item => item.key !== row.key)
        this.commitQueue()
      },
      onUploadPage (page) {
        this.uploadPage = page
      },
      markQueueFailed (item) {
        if (item.statusText === '等待上传' && this.uploadWaiting > 0) {
          this.uploadWaiting--
        }
        item.statusText = this.uploadHalt ? '已停止' : '上传失败'
        this.touchQueue(item)
        if (item.statusText === '上传失败') {
          this.abortFailedSession(item)
        }
      },
      touchQueue (task) {
        let now = new Date().getTime()
        let done = task && (task.statusText === '已完成' || task.statusText === '上传失败' || task.statusText === '合并失败' || task.statusText === '已停止')
        if (!done && this._touchAt && now - this._touchAt < 200) {
          return
        }
        this._touchAt = now
        this.uploadStamp++
      },
      taskState (statusText) {
        if (statusText === '上传中' || statusText === '续传中') {
          return {key: 'uploading', label: '上传中'}
        }
        if (statusText === '已停止') {
          return {key: 'stopped', label: '已停止'}
        }
        if (statusText === '已完成') {
          return {key: 'success', label: '上传成功'}
        }
        return {key: 'failed', label: '上传失败'}
      },
      stopUploads () {
        let self = this
        self.uploadHalt = true
        self.stopSmartConcurrencyAdapt()
        self._uploadPoolNext = null
        ;(self._uploadXhrs || []).forEach(function (xhr) {
          try {
            xhr.abort()
          } catch (e) {}
        })
        self._uploadXhrs = []
        ;(self._uploadQueue || []).forEach(function (item) {
          if (item.statusText === '上传中' || item.statusText === '续传中' || item.statusText === '等待上传') {
            if (item.statusText === '等待上传' && self.uploadWaiting > 0) {
              self.uploadWaiting--
            }
            item.statusText = '已停止'
          }
        })
        self.uploading = false
        self.showUploadModal = false
        self.commitQueue()
      },
      clearTask (item) {
        let task = item.queueKey ? this.queueByKey(item.queueKey) : null
        if (task) {
          task.paused = true
          task.runId = (task.runId || 0) + 1
          this.abortTaskXhrs(task.key)
          if (this._uploadFiles) {
            delete this._uploadFiles[task.key]
          }
        }
        let sessionId = (item.session && item.session.sessionId) || (task && item.state !== 'success' ? task.sessionId : null)
        if (sessionId && item.state !== 'success') {
          this.pendingSessions = this.pendingSessions.filter(session => session.sessionId !== sessionId)
          this.$http.post('/disk/upload/abort', {sessionId: sessionId})
        }
        if (item.queueKey) {
          this._uploadQueue = (this._uploadQueue || []).filter(row => row.key !== item.queueKey)
          this.uploadTotal = this._uploadQueue.length
        }
        this.commitQueue()
      },
      clearAllTasks () {
        let self = this
        if (self.taskFilter === 'uploading') {
          return
        }
        let list = self.taskView.filter(function (item) {
          return item.state === 'stopped' || item.state === 'failed' || item.state === 'success'
        })
        if (!list.length) {
          return
        }
        // Snapshot keys/items first so clearing one does not skip others via reactive view churn
        list.slice().forEach(function (item) {
          self.clearTask(item)
        })
      },
      queueByKey (key) {
        let list = this._uploadQueue || []
        for (let i = 0; i < list.length; i++) {
          if (list[i].key === key) {
            return list[i]
          }
        }
        return null
      },
      abortTaskXhrs (key) {
        ;(this._uploadXhrs || []).forEach(function (xhr) {
          if (key && xhr._diskKey !== key) {
            return
          }
          try {
            xhr.abort()
          } catch (e) {}
        })
      },
      pauseUpload (item) {
        let task = this.queueByKey(item.queueKey)
        if (!task) {
          return
        }
        task.paused = true
        task.runId = (task.runId || 0) + 1
        this.abortTaskXhrs(task.key)
        task.statusText = '已停止'
        this.touchQueue(task)
      },
      finishStoppedOrFailed (task, runId) {
        if (!task || task.runId !== runId) {
          return
        }
        this.releaseSignedSlot(task)
        task.statusText = (task.paused || this.uploadHalt) ? '已停止' : '上传失败'
        this.touchQueue(task)
        // 失败时中止会话，避免残留 RUNNING 导致重传弹「文件正在上传」；暂停保留会话以便续传
        if (task.statusText === '上传失败') {
          this.abortFailedSession(task)
        }
      },
      abortFailedSession (task) {
        if (!task || !task.sessionId) {
          return
        }
        let sessionId = task.sessionId
        task.sessionId = null
        task.mode = null
        this.$http.post('/disk/upload/abort', {sessionId: sessionId}, {silent: true}).catch(function () {})
      },
      continueUpload (item) {
        let self = this
        if (item.session && !item.queueKey) {
          self.resumeUpload(item.session)
          return Promise.resolve()
        }
        let task = self.queueByKey(item.queueKey)
        if (!task) {
          return Promise.resolve()
        }
        let file = self._uploadFiles && self._uploadFiles[task.key]
        if (!file) {
          if (task.sessionId) {
            self.resumeUpload({
              sessionId: task.sessionId,
              nodeId: task.nodeId,
              name: task.name,
              fileSize: task.size,
              partSize: task.partSize || 0,
              donePartNumbers: task.donePartNumbers || []
            })
            return Promise.resolve()
          }
          self.$Message.warning('原文件已不在，请重新选择')
          return Promise.resolve()
        }
        self.uploadHalt = false
        task.paused = false
        if (task.sessionId && task.mode === 'multipart') {
          return self.resumeQueueMultipart(task, file)
        }
        if (task.sessionId && task.mode === 'single') {
          return self.$http.post('/disk/upload/abort', {sessionId: task.sessionId}).then(function () {
            task.sessionId = null
            task.mode = null
            return self.startUpload(file, task.parentId, task)
          }).catch(function () {
            task.statusText = '上传失败'
            self.touchQueue(task)
          })
        }
        let root = task.parentId || self.pendingParentId
        let dir = !task.parentId && task.dir && task.dir !== '/' ? task.dir : ''
        return self.ensureDir(root, dir, {}).then(function (parentId) {
          return self.startUpload(file, parentId, task)
        }).catch(function () {
          task.statusText = '上传失败'
          self.touchQueue(task)
        })
      },
      resumeQueueMultipart (task, file) {
        let self = this
        task.paused = false
        task.runId = (task.runId || 0) + 1
        let runId = task.runId
        task.statusText = '续传中'
        self.touchQueue(task)
        return self.uploadMultipart(task, file, task.partSize, task.partCount, task.donePartNumbers || []).catch(function () {
          self.finishStoppedOrFailed(task, runId)
        })
      },
      startAllUploads () {
        let self = this
        if (self.uploading) {
          return
        }
        let resumeItems = self.taskItems.filter(function (item) {
          if (item.state !== 'stopped' && item.state !== 'failed') {
            return false
          }
          if (!item.queueKey) {
            return false
          }
          let task = self.queueByKey(item.queueKey)
          return !!(task && self._uploadFiles && self._uploadFiles[task.key])
        })
        let waiting = (self._uploadQueue || []).filter(item => item.statusText === '等待上传')
        if (!resumeItems.length && !waiting.length) {
          return
        }
        let autoItems = waiting.slice()
        resumeItems.forEach(function (item) {
          let task = self.queueByKey(item.queueKey)
          if (task) {
            autoItems.push(task)
          }
        })
        self.initSmartConcurrency(autoItems)
        self._signedLive = 0
        self._initWaiters = []
        self._completeBuffer = []
        self.uploadHalt = false
        self._uploadPoolNext = null
        self.uploading = true
        self.startSmartConcurrencyAdapt()
        let destBase = self.currentFolder ? self.currentFolder.name : ''
        waiting.forEach(function (item) {
          let dir = item.dir && item.dir !== '/' ? item.dir : ''
          item.dest = dir ? destBase + '/' + dir : (destBase || '/')
        })
        let work = []
        resumeItems.forEach(function (item) {
          work.push({kind: 'resume', item: item})
        })
        waiting.forEach(function (item) {
          work.push({kind: 'waiting', item: item})
        })
        let cache = {}
        let index = 0
        let active = 0
        let settled = 0
        let total = work.length
        let finished = false
        let markStoppedWaiting = function (item) {
          if (item.statusText === '等待上传') {
            if (self.uploadWaiting > 0) {
              self.uploadWaiting--
            }
            item.statusText = '已停止'
            self.touchQueue(item)
          }
        }
        let finishPool = function () {
          if (finished) {
            return
          }
          if (settled >= total && active === 0) {
            finished = true
            self._uploadPoolNext = null
            self.stopSmartConcurrencyAdapt()
            self.uploading = false
            self.showUploadModal = false
            self.loadList()
          }
        }
        let runOne = function (job) {
          if (self.uploadHalt) {
            if (job.kind === 'waiting') {
              markStoppedWaiting(job.item)
            }
            return Promise.resolve()
          }
          if (job.kind === 'resume') {
            return Promise.resolve(self.continueUpload(job.item)).catch(function () {})
          }
          let item = job.item
          let dir = item.dir === '/' ? '' : item.dir
          return self.ensureDir(self.pendingParentId, dir, cache).then(function (parentId) {
            if (self.uploadHalt) {
              markStoppedWaiting(item)
              return
            }
            let file = self._uploadFiles ? self._uploadFiles[item.key] : null
            if (!file) {
              self.markQueueFailed(item)
              return
            }
            return self.startUpload(file, parentId, item)
          }).catch(function () {
            self.markQueueFailed(item)
          })
        }
        let next = function () {
          if (finished) {
            return
          }
          if (self.uploadHalt) {
            while (index < total) {
              let left = work[index]
              index++
              settled++
              if (left.kind === 'waiting') {
                markStoppedWaiting(left.item)
              }
            }
            finishPool()
            return
          }
          let limit = self.clampSmartConcurrency(self.uploadConcurrency)
          while (active < limit && index < total && !self.uploadHalt) {
            let job = work[index]
            index++
            active++
            runOne(job).then(function () {
              active--
              settled++
              let tracked = job.kind === 'waiting' ? job.item : self.queueByKey(job.item && job.item.queueKey)
              self.noteSmartFileResult(tracked || job.item)
              if (settled >= total) {
                finishPool()
              } else {
                next()
              }
            })
          }
          if (self.uploadHalt) {
            next()
          }
        }
        let waitingForWarm = []
        work.forEach(function (job) {
          if (job && job.kind === 'waiting' && job.item) {
            waitingForWarm.push(job.item)
          }
        })
        self._warmItems = waitingForWarm
        self._warmCache = cache
        self._warmRootParentId = self.pendingParentId
        self._uploadPoolNext = function () {
          self.warmUploadWindow(waitingForWarm, cache, self.pendingParentId)
          next()
        }
        self.warmUploadWindow(waitingForWarm, cache, self.pendingParentId)
        next()
      },
      clearPersistedUploadConcurrency () {
        try {
          window.localStorage.removeItem('disk_upload_concurrency')
        } catch (e) {
        }
      },
      clampSmartConcurrency (n) {
        n = parseInt(n, 10)
        if (isNaN(n)) {
          return 6
        }
        if (n < 2) {
          return 2
        }
        if (n > 20) {
          return 20
        }
        return n
      },
      // A. 批开始：按文件构成选文件级并发（百度网盘风格启发式，上限 20）
      pickFileConcurrency (files) {
        let list = files || []
        let n = list.length
        if (!n) {
          return {fileConc: 6, tinyRatio: 0, smallRatio: 0, largeRatio: 0, largeByteShare: 0, avg: 0}
        }
        let tinyLimit = 256 * 1024
        let smallLimit = 8 * 1024 * 1024
        let largeLimit = 100 * 1024 * 1024
        let totalBytes = 0
        let tinyCount = 0
        let smallCount = 0
        let largeCount = 0
        let largeBytes = 0
        for (let i = 0; i < n; i++) {
          let size = Number(list[i] && list[i].size) || 0
          totalBytes += size
          if (size < tinyLimit) {
            tinyCount++
          }
          if (size < smallLimit) {
            smallCount++
          }
          if (size >= largeLimit) {
            largeCount++
            largeBytes += size
          }
        }
        let avg = totalBytes / n
        let tinyRatio = tinyCount / n
        let smallRatio = smallCount / n
        let largeRatio = largeCount / n
        let largeByteShare = totalBytes > 0 ? largeBytes / totalBytes : 0
        let fileConc
        if (largeByteShare >= 0.7 || largeRatio >= 0.5) {
          fileConc = 3
        } else if (n >= 2000 || (n >= 200 && tinyRatio >= 0.7)) {
          fileConc = 16
        } else if (n >= 200 || tinyRatio >= 0.7 || avg < 512 * 1024) {
          fileConc = 12
        } else if (smallRatio >= 0.7 || avg < 8 * 1024 * 1024) {
          fileConc = 8
        } else {
          fileConc = 6
        }
        return {
          fileConc: fileConc,
          tinyRatio: tinyRatio,
          smallRatio: smallRatio,
          largeRatio: largeRatio,
          largeByteShare: largeByteShare,
          avg: avg
        }
      },
      // B. 单文件分片并发
      pickPartConcurrency (fileSize, partSize) {
        let size = Number(fileSize) || 0
        if (size < 8 * 1024 * 1024) {
          return 1
        }
        if (size >= 100 * 1024 * 1024) {
          return 6
        }
        return 5
      },
      initSmartConcurrency (items) {
        let self = this
        this.clearPersistedUploadConcurrency()
        let picked = this.pickFileConcurrency(items)
        let n = this.clampSmartConcurrency(picked.fileConc)
        this.uploadConcurrency = n
        this._smartConc = {
          target: n,
          tinyRatio: picked.tinyRatio || 0,
          windowStartAt: new Date().getTime(),
          windowBytesStart: this.taskOverallDone || 0,
          windowDone: 0,
          windowFail: 0,
          windowThrottle: false
        }
        this.uploadAutoHint = '智能调节'
        if (this._uploadAutoHintTimer) {
          clearTimeout(this._uploadAutoHintTimer)
        }
        this._uploadAutoHintTimer = setTimeout(function () {
          self.uploadAutoHint = ''
          self._uploadAutoHintTimer = null
        }, 2500)
        return n
      },
      startSmartConcurrencyAdapt () {
        let self = this
        this.stopSmartConcurrencyAdapt()
        this._smartAdaptTimer = setInterval(function () {
          self.maybeAdaptFileConcurrency(true)
        }, 3000)
      },
      stopSmartConcurrencyAdapt () {
        if (this._smartAdaptTimer) {
          clearInterval(this._smartAdaptTimer)
          this._smartAdaptTimer = null
        }
      },
      noteSmartFileResult (item) {
        let s = this._smartConc
        if (!s || !item) {
          return
        }
        let st = item.statusText || ''
        if (st === '已停止') {
          return
        }
        s.windowDone++
        if (st === '上传失败' || st === '合并失败') {
          s.windowFail++
          let msg = String(item.failMsg || '')
          if (msg.indexOf('429') >= 0 || msg.indexOf('503') >= 0) {
            s.windowThrottle = true
          }
        }
        if (s.windowDone >= 20) {
          this.maybeAdaptFileConcurrency(false)
        }
      },
      // C. 运行期轻量自适应：失败降并发；小文件过慢则抬；健康则向目标靠拢
      maybeAdaptFileConcurrency (byTimer) {
        let s = this._smartConc
        if (!s || !this.uploading) {
          return
        }
        let now = new Date().getTime()
        let enoughFiles = s.windowDone >= 20
        let enoughTime = now - s.windowStartAt >= 3000
        if (!enoughFiles && !enoughTime) {
          return
        }
        if (s.windowDone < 3 && !s.windowThrottle) {
          return
        }
        let failRate = s.windowDone > 0 ? s.windowFail / s.windowDone : 0
        let bytesNow = this.taskOverallDone || 0
        let dt = Math.max(0.001, (now - s.windowStartAt) / 1000)
        let speed = (bytesNow - s.windowBytesStart) / dt
        let cur = this.clampSmartConcurrency(this.uploadConcurrency)
        let next = cur
        if (failRate >= 0.15 || s.windowThrottle) {
          next = Math.max(2, Math.floor(cur / 2))
        } else if (speed < 200 * 1024 && s.tinyRatio >= 0.5 && failRate < 0.05) {
          next = Math.min(20, cur + 2)
        } else if (failRate < 0.05 && cur < s.target) {
          next = Math.min(s.target, cur + 1)
        }
        next = this.clampSmartConcurrency(next)
        s.windowStartAt = now
        s.windowBytesStart = bytesNow
        s.windowDone = 0
        s.windowFail = 0
        s.windowThrottle = false
        if (next !== cur) {
          this.uploadConcurrency = next
          if (typeof this._uploadPoolNext === 'function') {
            this._uploadPoolNext()
          }
        }
      },
      partUploadConcurrency (file, partSize) {
        let size = file && file.size ? Number(file.size) : 0
        return this.pickPartConcurrency(size, partSize)
      },
      beginUpload () {
        let self = this
        let waiting = (self._uploadQueue || []).filter(item => item.statusText === '等待上传')
        if (!waiting.length || self.uploading) {
          return
        }
        self.initSmartConcurrency(waiting)
        self._signedLive = 0
        self._initWaiters = []
        self._completeBuffer = []
        self.uploading = true
        self.uploadHalt = false
        self.startSmartConcurrencyAdapt()
        let destBase = self.currentFolder ? self.currentFolder.name : ''
        waiting.forEach(function (item) {
          let dir = item.dir && item.dir !== '/' ? item.dir : ''
          item.dest = dir ? destBase + '/' + dir : (destBase || '/')
        })
        let cache = {}
        let index = 0
        let active = 0
        let settled = 0
        let total = waiting.length
        let finished = false
        let markStoppedWaiting = function (item) {
          if (item.statusText === '等待上传') {
            if (self.uploadWaiting > 0) {
              self.uploadWaiting--
            }
            item.statusText = '已停止'
            self.touchQueue(item)
          }
        }
        let finishPool = function () {
          if (finished) {
            return
          }
          if (settled >= total && active === 0) {
            finished = true
            self._uploadPoolNext = null
            self.stopSmartConcurrencyAdapt()
            self.uploading = false
            self.showUploadModal = false
            self.loadList()
          }
        }
        let runOne = function (item) {
          if (self.uploadHalt) {
            markStoppedWaiting(item)
            return Promise.resolve()
          }
          let dir = item.dir === '/' ? '' : item.dir
          return self.ensureDir(self.pendingParentId, dir, cache).then(function (parentId) {
            if (self.uploadHalt) {
              markStoppedWaiting(item)
              return
            }
            let file = self._uploadFiles ? self._uploadFiles[item.key] : null
            if (!file) {
              self.markQueueFailed(item)
              return
            }
            return self.startUpload(file, parentId, item)
          }).catch(function () {
            self.markQueueFailed(item)
          })
        }
        let next = function () {
          if (finished) {
            return
          }
          if (self.uploadHalt) {
            while (index < total) {
              markStoppedWaiting(waiting[index])
              index++
              settled++
            }
            finishPool()
            return
          }
          let limit = self.clampSmartConcurrency(self.uploadConcurrency)
          while (active < limit && index < total && !self.uploadHalt) {
            let item = waiting[index]
            index++
            active++
            runOne(item).then(function () {
              active--
              settled++
              self.noteSmartFileResult(item)
              if (settled >= total) {
                finishPool()
              } else {
                next()
              }
            })
          }
          if (self.uploadHalt) {
            next()
          }
        }
        self._warmItems = waiting
        self._warmCache = cache
        self._warmRootParentId = self.pendingParentId
        self._uploadPoolNext = function () {
          self.warmUploadWindow(waiting, cache, self.pendingParentId)
          next()
        }
        self.warmUploadWindow(waiting, cache, self.pendingParentId)
        next()
      },
      ensureDir (rootId, dir, cache) {
        if (!dir) {
          return Promise.resolve(rootId)
        }
        let self = this
        let parts = dir.split('/')
        let walk = Promise.resolve(rootId)
        let acc = ''
        parts.forEach(function (name) {
          walk = walk.then(function (parentId) {
            acc = acc ? acc + '/' + name : name
            if (cache[acc]) {
              return cache[acc]
            }
            cache[acc] = self.findOrMkdir(parentId, name).then(function (id) {
              return id
            }, function (err) {
              delete cache[acc]
              return Promise.reject(err)
            })
            return cache[acc]
          })
        })
        return walk
      },
      findOrMkdir (parentId, name) {
        let self = this
        return self.$http.get('/disk/list', {params: {parentId: parentId}}).then(function (res) {
          let list = res && res.status === 'ok' && res.data ? res.data : []
          for (let i = 0; i < list.length; i++) {
            if (list[i].nodeType === 1 && list[i].name === name) {
              return list[i].id
            }
          }
          return self.$http.post('/disk/mkdir', {parentId: parentId, name: name}).then(function (created) {
            if (created && created.status === 'ok' && created.data != null) {
              return created.data
            }
            return self.$http.get('/disk/list', {params: {parentId: parentId}}).then(function (again) {
              let rows = again && again.status === 'ok' && again.data ? again.data : []
              for (let j = 0; j < rows.length; j++) {
                if (rows[j].nodeType === 1 && rows[j].name === name) {
                  return rows[j].id
                }
              }
              return Promise.reject(new Error('mkdir'))
            })
          })
        })
      },
      startUpload (file, parentId, queued, preInit) {
        let self = this
        let item = queued
        let runId = 0
        if (item) {
          if (item.statusText === '等待上传' && self.uploadWaiting > 0) {
            self.uploadWaiting--
          }
          item.parentId = parentId
          item.paused = false
          item.runId = (item.runId || 0) + 1
          runId = item.runId
          item.statusText = '上传中'
          item.percent = 0
          self.touchQueue(item)
        }
        let initPromise
        let cached = preInit || (item && item._preInit)
        if (cached && cached.ok !== false && cached.sessionId) {
          initPromise = Promise.resolve({status: 'ok', data: cached})
        } else if (item && item._preInitPromise) {
          initPromise = item._preInitPromise.then(function (data) {
            return {status: 'ok', data: data}
          })
        } else if (item) {
          initPromise = self.warmUploadInit(item, parentId, file).then(function (data) {
            return {status: 'ok', data: data}
          })
        } else {
          initPromise = self.$http.post('/disk/upload/init', {
            parentId: parentId,
            fileName: file.name,
            size: file.size,
            mime: file.type || 'application/octet-stream',
            overwrite: self.overwriteSame ? 1 : 0
          }, {silent: true})
        }
        return initPromise.then(function (res) {
          if (item && item.runId !== runId) {
            return
          }
          if (!res || res.status !== 'ok' || !res.data || res.data.ok === false) {
            if (item) {
              item.failMsg = (res && res.data && res.data.error) || (res && res.msg) || '初始化失败'
            }
            self.finishStoppedOrFailed(item, runId)
            return
          }
          self.showUploadModal = false
          let data = res.data
          let task = item || {
            key: data.sessionId + '-' + new Date().getTime(),
            name: file.name,
            dir: '/',
            typeName: file.type || '-',
            size: file.size,
            percent: 0,
            statusText: '上传中',
            parentId: parentId,
            paused: false,
            runId: 1
          }
          if (!item) {
            runId = task.runId
          }
          task.nodeId = data.nodeId
          task.sessionId = data.sessionId
          task.mode = data.mode
          task.partSize = data.partSize
          task.partCount = data.partCount
          task.parentId = parentId
          task.donePartNumbers = []
          task.putUrl = data.url || ''
          task.urlSignedAt = data.url ? Date.now() : 0
          task.urlExpireSeconds = Number(data.expireSeconds) || 7200
          if (data.fileName) {
            task.name = data.fileName
            self.commitQueue()
          }
          task.statusText = '上传中'
          if (!item) {
            if (!self._uploadQueue) {
              self._uploadQueue = []
            }
            self._uploadQueue.push(task)
            self.commitQueue()
          }
          if (data.mode === 'single') {
            return self.ensureFreshPutUrl(task).then(function (url) {
              return self.putPartRetry(url, file, function (loaded) {
                let percent = file.size ? Math.floor(loaded * 100 / file.size) : 100
                if (percent > task.percent) {
                  task.percent = percent > 99 ? 99 : percent
                  self.touchQueue(task)
                }
              }, 1, task)
            }).then(function () {
              if (task.runId !== runId || task.paused) {
                return
              }
              self.releaseSignedSlot(task)
              return self.completeUpload(task)
            }).catch(function (err) {
              self.releaseSignedSlot(task)
              if (task && err && err.message) {
                task.failMsg = err.message
              }
              self.finishStoppedOrFailed(task, runId)
            })
          }
          return self.uploadMultipart(task, file, data.partSize, data.partCount, []).then(function () {
            self.releaseSignedSlot(task)
          }).catch(function (err) {
            self.releaseSignedSlot(task)
            if (task && err && err.message) {
              task.failMsg = err.message
            }
            self.finishStoppedOrFailed(task, runId)
          })
        }).catch(function (err) {
          if (item && err && err.message && err.message !== 'stopped') {
            item.failMsg = err.message
          }
          self.finishStoppedOrFailed(item, runId)
        })
      },
      loadPending () {
        let self = this
        self.$http.get('/disk/upload/pending').then(function (res) {
          if (res && res.status === 'ok') {
            self.pendingSessions = res.data || []
          }
        })
      },
      cancelPending (session) {
        let self = this
        self.$Modal.confirm({
          title: '取消上传',
          content: '取消后这个未完成任务会被清掉，云上不会留下半截文件。',
          onOk: function () {
            self.$http.post('/disk/upload/abort', {sessionId: session.sessionId}).then(function () {
              self.loadPending()
            })
          }
        })
      },
      resumeTask (session, file) {
        let self = this
        self.uploadHalt = false
        let partSize = Number(session.partSize) || 0
        let partCount = partSize > 0 ? Math.ceil(session.fileSize / partSize) : 1
        if (partSize > 0 && partCount > 1) {
          let task = {
            key: session.sessionId,
            nodeId: session.nodeId,
            sessionId: session.sessionId,
            name: session.name,
            dir: '/',
            typeName: '-',
            size: session.fileSize,
            mode: 'multipart',
            partSize: partSize,
            partCount: partCount,
            percent: 0,
            statusText: '续传中'
          }
          if (!self._uploadQueue) {
            self._uploadQueue = []
          }
          self._uploadQueue.push(task)
          self.commitQueue()
          self.uploadMultipart(task, file, partSize, partCount, session.donePartNumbers || []).catch(function () {
            task.statusText = self.uploadHalt ? '已停止' : '上传失败'
            self.touchQueue(task)
          })
          return
        }
        self.$http.post('/disk/upload/abort', {sessionId: session.sessionId}).then(function () {
          self.startUpload(file, self.currentFolder ? self.currentFolder.id : 0)
        })
      },
      putPart (url, blob, onProgress, taskKey) {
        let self = this
        return new Promise(function (resolve, reject) {
          let xhr = new XMLHttpRequest()
          xhr._diskKey = taskKey || ''
          self._uploadXhrs = self._uploadXhrs || []
          self._uploadXhrs.push(xhr)
          let finish = function () {
            self._uploadXhrs = (self._uploadXhrs || []).filter(function (item) {
              return item !== xhr
            })
          }
          xhr.open('PUT', url, true)
          xhr.setRequestHeader('Content-Type', 'application/octet-stream')
          xhr.upload.onprogress = function (event) {
            if (event.lengthComputable && onProgress) {
              onProgress(event.loaded)
            }
          }
          xhr.onload = function () {
            finish()
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(xhr)
            } else {
              reject(new Error('上传失败：' + xhr.status))
            }
          }
          xhr.onerror = function () {
            finish()
            // 常见于 OSS CORS / 混合内容 / 网络中断；状态码多半为 0
            reject(new Error('上传失败(网络或CORS)'))
          }
          xhr.onabort = function () {
            finish()
            reject(new Error('stopped'))
          }
          xhr.send(blob)
        })
      },
      putPartRetry (url, blob, onProgress, attempt, task) {
        let self = this
        if (self.uploadHalt || (task && task.paused)) {
          return Promise.reject(new Error('stopped'))
        }
        return self.putPart(url, blob, onProgress, task && task.key).catch(function (error) {
          if (self.uploadHalt || (task && task.paused) || !error || error.message === 'stopped' || attempt >= 3) {
            return Promise.reject(error)
          }
          return new Promise(function (resolve) {
            setTimeout(resolve, 800 * attempt)
          }).then(function () {
            return self.putPartRetry(url, blob, onProgress, attempt + 1, task)
          })
        })
      },
      sendPart (task, file, partSize, item, attempt, partLoaded, updatePercent) {
        let self = this
        if (self.uploadHalt || task.paused) {
          return Promise.reject(new Error('stopped'))
        }
        let partNumber = item.partNumber
        let start = (partNumber - 1) * partSize
        let end = Math.min(start + partSize, file.size)
        let blob = file.slice(start, end)
        return self.putPart(item.url, blob, function (loaded) {
          partLoaded[partNumber] = loaded
          updatePercent()
        }, task.key).then(function (xhr) {
          delete partLoaded[partNumber]
          let etag = xhr.getResponseHeader('ETag') || ''
          return self.$http.post('/disk/upload/partDone', {
            sessionId: task.sessionId,
            partNumber: partNumber,
            etag: etag
          }).then(function (res) {
            if (!res || res.status !== 'ok') {
              return Promise.reject(new Error('分片记录失败'))
            }
            if (!task.donePartNumbers) {
              task.donePartNumbers = []
            }
            if (task.donePartNumbers.indexOf(partNumber) < 0) {
              task.donePartNumbers.push(partNumber)
            }
            return blob.size
          })
        }).catch(function (error) {
          delete partLoaded[partNumber]
          if (updatePercent) {
            updatePercent()
          }
          if (task.paused || self.uploadHalt || !error || error.message === 'stopped' || attempt >= 3) {
            return Promise.reject(error)
          }
          return new Promise(function (resolve) {
            setTimeout(resolve, 800 * attempt)
          }).then(function () {
            return self.signParts(task.sessionId, [partNumber]).then(function (signed) {
              let next = signed && signed[0] && signed[0].url ? signed[0] : item
              return self.sendPart(task, file, partSize, next, attempt + 1, partLoaded, updatePercent)
            })
          })
        })
      },
      signParts (sessionId, partNumbers) {
        let self = this
        let batches = []
        for (let i = 0; i < partNumbers.length; i += 20) {
          batches.push(partNumbers.slice(i, i + 20))
        }
        let requests = batches.map(function (batch) {
          return self.$http.post('/disk/upload/signParts', {
            sessionId: sessionId,
            partNumbers: batch.join(',')
          }, {silent: true})
        })
        return Promise.all(requests).then(function (results) {
          let signed = []
          results.forEach(function (res) {
            if (res && res.status === 'ok' && res.data) {
              signed = signed.concat(res.data)
            }
          })
          return signed
        })
      },
      uploadMultipart (task, file, partSize, partCount, donePartNumbers) {
        let self = this
        partSize = Number(partSize) || file.size
        partCount = Number(partCount) || Math.ceil(file.size / partSize)
        let doneSet = {}
        let doneParts = donePartNumbers || []
        doneParts.forEach(function (number) {
          doneSet[number] = true
        })
        let missing = []
        let completedBytes = 0
        for (let number = 1; number <= partCount; number++) {
          if (doneSet[number]) {
            completedBytes += Math.min(partSize, Math.max(file.size - (number - 1) * partSize, 0))
          } else {
            missing.push(number)
          }
        }
        let partLoaded = {}
        let updatePercent = function () {
          let sum = completedBytes
          Object.keys(partLoaded).forEach(function (key) {
            sum += partLoaded[key]
          })
          let percent = Math.floor(sum * 100 / file.size)
          if (percent > 99) {
            percent = 99
          }
          if (percent > task.percent) {
            task.percent = percent
            self.touchQueue(task)
          }
        }
        task.percent = Math.floor(completedBytes * 100 / file.size)
        self.touchQueue(task)
        if (!missing.length) {
          if (task.paused || self.uploadHalt) {
            return Promise.reject(new Error('stopped'))
          }
          return self.completeUpload(task)
        }
        let concurrency = self.partUploadConcurrency(file, partSize)
        // 分片签名窗口：即将上传的 concurrency*2（上限 20），避免超大文件一次签完全部分片
        let signBatch = Math.min(20, Math.max(concurrency * 2, 4))
        let batchIndex = 0
        let uploadOne = function (item) {
          return self.sendPart(task, file, partSize, item, 1, partLoaded, updatePercent).then(function (size) {
            completedBytes += size
            updatePercent()
          })
        }
        let uploadNextBatch = function () {
          if (task.paused || self.uploadHalt) {
            return Promise.reject(new Error('stopped'))
          }
          if (batchIndex >= missing.length) {
            return Promise.resolve()
          }
          let batch = missing.slice(batchIndex, batchIndex + signBatch)
          batchIndex += signBatch
          return self.signParts(task.sessionId, batch).then(function (signed) {
            if (task.paused || self.uploadHalt) {
              return Promise.reject(new Error('stopped'))
            }
            return self.runWithConcurrency(signed, concurrency, uploadOne)
          }).then(uploadNextBatch)
        }
        return uploadNextBatch().then(function () {
          if (task.paused || self.uploadHalt) {
            return Promise.reject(new Error('stopped'))
          }
          task.percent = 100
          return self.completeUpload(task)
        })
      },
      runWithConcurrency (items, limit, worker) {
        return new Promise(function (resolve, reject) {
          if (!items.length) {
            resolve([])
            return
          }
          let index = 0
          let active = 0
          let finished = 0
          let failed = false
          let next = function () {
            if (failed) {
              return
            }
            while (active < limit && index < items.length) {
              let current = index
              index++
              active++
              worker(items[current], current).then(function () {
                active--
                finished++
                if (finished === items.length) {
                  resolve([])
                } else {
                  next()
                }
              }).catch(function (error) {
                failed = true
                reject(error)
              })
            }
          }
          next()
        })
      },
      /**
       * 签名 TTL=2h（后端 DiskController.UPLOAD_URL_EXPIRE_SECONDS）。
       * 滑动窗口 = min(fileConcurrency*2, 30)，远小于半 TTL，避免排队过久签名过期；
       * 若已过半 TTL 则 signPut 重签。
       */
      uploadSignWindowSize () {
        return Math.min(this.clampSmartConcurrency(this.uploadConcurrency) * 2, 30)
      },
      postDiskJson (url, body) {
        return this.$http.post(url, body, {
          silent: true,
          transformRequest: [function (data) {
            return JSON.stringify(data)
          }],
          headers: {'Content-Type': 'application/json;charset=utf-8'}
        })
      },
      releaseSignedSlot (task) {
        if (task && task._signedCounted) {
          task._signedCounted = false
          if (this._signedLive > 0) {
            this._signedLive--
          }
          this.scheduleInitFlush()
          if (this._warmItems) {
            this.warmUploadWindow(this._warmItems, this._warmCache || {}, this._warmRootParentId || this.pendingParentId)
          }
        }
      },
      warmUploadInit (item, parentId, file) {
        let self = this
        if (!item) {
          return Promise.reject(new Error('参数错误'))
        }
        if (item._preInit && item._preInit.sessionId && item._preInit.ok !== false) {
          return Promise.resolve(item._preInit)
        }
        if (item._preInitPromise) {
          return item._preInitPromise
        }
        item._preInitPromise = self.enqueueUploadInit(file, parentId, item).then(function (data) {
          item._preInit = data
          return data
        }, function (err) {
          item._preInitPromise = null
          return Promise.reject(err)
        })
        return item._preInitPromise
      },
      enqueueUploadInit (file, parentId, item) {
        let self = this
        return new Promise(function (resolve, reject) {
          if (!self._initWaiters) {
            self._initWaiters = []
          }
          self._initWaiters.push({
            file: file,
            parentId: parentId,
            item: item,
            resolve: resolve,
            reject: reject
          })
          self.scheduleInitFlush()
        })
      },
      /**
       * 在 PUT 并发槽之外预热 init：只预签 window 内文件（TTL=2h，窗口≤30）。
       */
      warmUploadWindow (items, cache, rootParentId) {
        let self = this
        if (!items || !items.length) {
          return
        }
        let windowSize = self.uploadSignWindowSize()
        let live = (self._signedLive || 0) + ((self._initWaiters && self._initWaiters.length) || 0)
        let room = windowSize - live
        if (room <= 0) {
          return
        }
        let started = 0
        for (let i = 0; i < items.length && started < room; i++) {
          let item = items[i]
          if (!item || item.statusText !== '等待上传') {
            continue
          }
          if (item._preInit || item._preInitPromise || item.sessionId) {
            continue
          }
          started++
          let dir = item.dir === '/' ? '' : item.dir
          self.ensureDir(rootParentId, dir, cache).then(function (parentId) {
            if (self.uploadHalt || item.statusText !== '等待上传') {
              return
            }
            let file = self._uploadFiles ? self._uploadFiles[item.key] : null
            if (!file) {
              return
            }
            item.parentId = parentId
            return self.warmUploadInit(item, parentId, file)
          }).catch(function () {})
        }
      },
      scheduleInitFlush () {
        let self = this
        if (self._initFlushTimer) {
          return
        }
        self._initFlushTimer = setTimeout(function () {
          self._initFlushTimer = null
          self.flushInitBatch()
        }, 16)
      },
      flushInitBatch () {
        let self = this
        if (self._initFlushing) {
          return
        }
        let waiters = self._initWaiters || []
        if (!waiters.length) {
          return
        }
        // 窗口：仅给「未完成且已占签名位」之外的名额签 PUT，防止一次预签过多
        let windowSize = self.uploadSignWindowSize()
        let room = windowSize - (self._signedLive || 0)
        if (room <= 0) {
          return
        }
        let take = Math.min(50, room, waiters.length)
        let batch = waiters.splice(0, take)
        if (!batch.length) {
          return
        }
        self._initFlushing = true
        let payload = {
          overwrite: self.overwriteSame ? 1 : 0,
          items: batch.map(function (entry) {
            return {
              clientKey: entry.item.key,
              parentId: entry.parentId,
              fileName: entry.file.name,
              size: entry.file.size,
              mime: entry.file.type || 'application/octet-stream'
            }
          })
        }
        self.postDiskJson('/disk/upload/initBatch', payload).then(function (res) {
          let rows = res && res.status === 'ok' && res.data ? res.data : []
          let byKey = {}
          rows.forEach(function (row) {
            if (row && row.clientKey) {
              byKey[row.clientKey] = row
            }
          })
          let renamed = false
          batch.forEach(function (entry, idx) {
            let row = byKey[entry.item.key] || rows[idx]
            if (!row || row.ok === false || !row.sessionId) {
              entry.reject(new Error((row && row.error) || '初始化失败'))
              return
            }
            if (row.fileName && entry.item) {
              entry.item.name = row.fileName
              renamed = true
            }
            entry.item._signedCounted = true
            self._signedLive = (self._signedLive || 0) + 1
            entry.resolve(row)
          })
          if (renamed) {
            self.commitQueue()
          }
        }).catch(function () {
          batch.forEach(function (entry) {
            entry.reject(new Error('初始化失败'))
          })
        }).then(function () {
          self._initFlushing = false
          if ((self._initWaiters || []).length) {
            self.scheduleInitFlush()
          }
        })
      },
      ensureFreshPutUrl (task) {
        let self = this
        if (!task || !task.sessionId) {
          return Promise.reject(new Error('参数错误'))
        }
        let expireSeconds = Number(task.urlExpireSeconds) || 7200
        let halfMs = expireSeconds * 500
        let age = Date.now() - (task.urlSignedAt || 0)
        if (task.putUrl && age < halfMs) {
          return Promise.resolve(task.putUrl)
        }
        return self.$http.post('/disk/upload/signPut', {sessionId: task.sessionId}, {silent: true}).then(function (res) {
          if (!res || res.status !== 'ok' || !res.data || !res.data.url) {
            return Promise.reject(new Error((res && res.msg) || '签名失败'))
          }
          task.putUrl = res.data.url
          task.urlSignedAt = Date.now()
          task.urlExpireSeconds = Number(res.data.expireSeconds) || expireSeconds
          return task.putUrl
        })
      },
      completeUpload (task) {
        let self = this
        if (!task || task.paused || self.uploadHalt) {
          return Promise.resolve()
        }
        let runId = task.runId
        return new Promise(function (resolve) {
          if (!self._completeBuffer) {
            self._completeBuffer = []
          }
          self._completeBuffer.push({task: task, runId: runId, resolve: resolve})
          self.scheduleCompleteFlush()
        })
      },
      scheduleCompleteFlush () {
        let self = this
        if (self._completeFlushTimer) {
          return
        }
        self._completeFlushTimer = setTimeout(function () {
          self._completeFlushTimer = null
          self.flushCompleteBatch()
        }, 30)
      },
      flushCompleteBatch () {
        let self = this
        if (self._completeFlushing) {
          return
        }
        let buffer = self._completeBuffer || []
        if (!buffer.length) {
          return
        }
        let batch = buffer.splice(0, 50)
        self._completeFlushing = true
        let sessionIds = batch.map(function (entry) {
          return entry.task.sessionId
        })
        self.postDiskJson('/disk/upload/completeBatch', {sessionIds: sessionIds}).then(function (res) {
          let rows = res && res.status === 'ok' && res.data ? res.data : null
          let byId = {}
          if (rows && rows.length) {
            rows.forEach(function (row) {
              if (row && row.sessionId != null) {
                byId[row.sessionId] = row
              }
            })
          }
          batch.forEach(function (entry) {
            let task = entry.task
            if (task.runId !== entry.runId) {
              entry.resolve()
              return
            }
            let row = byId[task.sessionId]
            let ok = rows ? (row && row.ok !== false) : (res && res.status === 'ok')
            if (ok) {
              task.percent = 100
              task.statusText = '已完成'
              self.touchQueue(task)
              if (!self.uploading) {
                self.loadList()
              }
              self.loadPending()
            } else {
              task.failMsg = (row && row.error) || (res && res.msg) || '合并失败'
              task.statusText = '合并失败'
              self.touchQueue(task)
              self.abortFailedSession(task)
            }
            entry.resolve()
          })
        }).catch(function () {
          // 批量失败时回退逐个 complete，避免整批误伤
          let chain = Promise.resolve()
          batch.forEach(function (entry) {
            chain = chain.then(function () {
              return self.completeUploadSingle(entry.task, entry.runId).then(entry.resolve, entry.resolve)
            })
          })
          return chain
        }).then(function () {
          self._completeFlushing = false
          if ((self._completeBuffer || []).length) {
            self.scheduleCompleteFlush()
          }
        })
      },
      completeUploadSingle (task, runId) {
        let self = this
        if (!task || task.paused || self.uploadHalt) {
          return Promise.resolve()
        }
        runId = runId == null ? task.runId : runId
        return self.$http.post('/disk/upload/complete', {sessionId: task.sessionId}, {silent: true}).then(function (res) {
          if (task.runId !== runId) {
            return
          }
          if (res && res.status === 'ok') {
            task.percent = 100
            task.statusText = '已完成'
            self.touchQueue(task)
            if (!self.uploading) {
              self.loadList()
            }
            self.loadPending()
          } else {
            if (res && res.msg) {
              task.failMsg = res.msg
            }
            task.statusText = '合并失败'
            self.touchQueue(task)
            self.abortFailedSession(task)
          }
        }).catch(function () {
          if (task.runId !== runId) {
            return
          }
          task.statusText = '合并失败'
          self.touchQueue(task)
          self.abortFailedSession(task)
        })
      }
    }
  }
</script>
<style scoped>
  .disk-app {
    color: #17233d;
  }

  .disk-body {
    display: flex;
    align-items: stretch;
    min-height: calc(100vh - 140px);
    border: 1px solid #eef0f4;
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
  }

  .disk-side {
    width: 220px;
    flex: 0 0 220px;
    padding: 16px 12px;
    background: #f7f9fc;
    border-right: 1px solid #eef0f4;
  }

  .disk-brand {
    display: flex;
    align-items: center;
    padding: 0 8px 16px;
    font-size: 18px;
    font-weight: 600;
    color: #17233d;
  }

  .disk-brand img {
    width: 28px;
    height: 28px;
    margin-right: 8px;
  }

  .disk-nav {
    padding-bottom: 12px;
  }

  .disk-side-section {
    padding-top: 12px;
    border-top: 1px solid #e8ebf0;
  }

  .disk-side-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px 8px;
    color: #808695;
    font-size: 12px;
  }

  .disk-side-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    color: #515a6e;
  }

  .disk-side-add:hover {
    background: #e8f2ff;
    color: #2d8cf0;
  }

  .disk-group-search {
    padding: 0 4px 8px;
  }

  .disk-group-search /deep/ .ivu-input {
    border-radius: 14px;
    background: #fff;
  }

  .disk-group-list {
    max-height: calc(100vh - 420px);
    overflow-y: auto;
  }

  .disk-side-empty {
    padding: 12px 8px;
    color: #a8adb7;
    font-size: 12px;
    text-align: center;
  }

  .disk-space-item {
    display: flex;
    align-items: center;
    height: 40px;
    margin-bottom: 2px;
    padding: 0 12px;
    border-radius: 8px;
    color: #515a6e;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }

  .disk-space-item .ivu-icon {
    width: 20px;
    margin-right: 10px;
    text-align: center;
  }

  .disk-space-item:hover {
    background: #eef2f8;
  }

  .disk-space-item.on {
    color: #2d8cf0;
    background: #e3eeff;
    font-weight: 500;
  }

  .disk-group-item {
    height: 44px;
    white-space: nowrap;
    overflow: hidden;
  }

  .disk-group-item-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .disk-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: 8px;
    color: #fff;
    font-weight: 600;
  }

  .disk-avatar-sm {
    width: 26px;
    height: 26px;
    margin-right: 10px;
    font-size: 13px;
  }

  .disk-main {
    position: relative;
    flex: 1;
    min-width: 0;
    padding: 16px 24px 24px;
  }

  .disk-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
    margin-bottom: 12px;
  }

  .disk-path {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    min-width: 0;
    font-size: 15px;
  }

  .disk-path-back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: 8px;
    border-radius: 6px;
    color: #515a6e;
  }

  .disk-path-back:hover {
    background: #eef2f8;
  }

  .disk-path-back.disabled {
    color: #c5c8ce;
    cursor: default;
    background: transparent;
  }

  .disk-path-sep {
    margin: 0 6px;
    color: #c5c8ce;
  }

  .disk-path-item {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #515a6e;
  }

  a.disk-path-item:hover {
    color: #2d8cf0;
  }

  .disk-path-item.current {
    color: #17233d;
    font-weight: 600;
  }

  .disk-search {
    width: 260px;
    margin-left: 16px;
  }

  .disk-search /deep/ .ivu-input {
    height: 34px;
    border-radius: 17px;
    background: #f5f7fa;
    border-color: transparent;
  }

  .disk-search /deep/ .ivu-input:focus,
  .disk-search /deep/ .ivu-input:hover {
    background: #fff;
    border-color: #2d8cf0;
  }

  .disk-search /deep/ .ivu-input-icon {
    height: 34px;
    line-height: 34px;
    cursor: pointer;
  }

  .disk-topbar-right {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }

  .disk-topbar-right > * + * {
    margin-left: 6px;
  }

  .disk-path-group {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    padding: 2px 8px 2px 2px;
    border-radius: 8px;
    color: #515a6e;
    cursor: pointer;
  }

  .disk-path-group:hover {
    background: #eef2f8;
  }

  .disk-path-group.current {
    color: #17233d;
    font-weight: 600;
    cursor: default;
  }

  .disk-path-group.current:hover {
    background: transparent;
  }

  .disk-path-group-name {
    max-width: 240px;
    margin-left: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .disk-avatar-xs {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    font-size: 12px;
  }

  .disk-role-tag {
    margin-left: 8px;
    padding: 0 8px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: normal;
    line-height: 20px;
    color: #515a6e;
    background: #eef0f4;
  }

  .disk-role-tag.role-1 {
    color: #d46b08;
    background: #fff4e5;
  }

  .disk-role-tag.role-2 {
    color: #2d8cf0;
    background: #e3eeff;
  }

  .disk-members,
  .disk-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    min-width: 34px;
    border-radius: 8px;
    color: #515a6e;
  }

  .disk-members {
    padding: 0 10px;
  }

  .disk-members span {
    margin-left: 4px;
    font-size: 13px;
  }

  .disk-members:hover,
  .disk-icon-btn:hover {
    background: #eef2f8;
    color: #2d8cf0;
  }

  .disk-members.readonly {
    cursor: default;
  }

  .disk-members.readonly:hover {
    background: transparent;
    color: #515a6e;
  }

  .disk-text-danger,
  .disk-text-danger:hover {
    color: #ed4014;
  }

  .disk-toolbar {
    display: flex;
    align-items: center;
    min-height: 40px;
    margin-bottom: 8px;
  }

  .disk-toolbar > .ivu-btn {
    margin-right: 10px;
    border-radius: 8px;
  }

  .disk-toolbar > .ivu-btn-primary {
    padding-left: 18px;
    padding-right: 18px;
  }

  .disk-batch {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    padding: 4px 8px 4px 16px;
    border-radius: 10px;
    background: #f0f6ff;
  }

  .disk-batch-count {
    margin-right: 16px;
    color: #515a6e;
  }

  .disk-batch-count b {
    color: #2d8cf0;
  }

  .disk-batch .ivu-btn {
    margin-right: 8px;
    border-radius: 8px;
    background: #fff;
  }

  .disk-batch .disk-btn-danger:hover {
    color: #ed4014;
    border-color: #ed4014;
  }

  .disk-batch-clear {
    margin-left: auto;
    color: #808695;
  }

  .disk-batch-clear:hover {
    color: #2d8cf0;
  }

  .disk-recycle-note {
    display: flex;
    align-items: center;
    color: #808695;
    font-size: 13px;
  }

  .disk-recycle-note .ivu-icon {
    margin-right: 6px;
    font-size: 16px;
  }

  .disk-count {
    margin-left: auto;
    padding-left: 16px;
    color: #a8adb7;
    font-size: 12px;
    white-space: nowrap;
  }

  .disk-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 96px 0 80px;
    color: #808695;
  }

  .disk-empty-icon {
    width: 96px;
    height: 96px;
    opacity: 0.85;
  }

  .disk-empty-title {
    margin-top: 16px;
    color: #515a6e;
    font-size: 15px;
  }

  .disk-empty-sub {
    margin: 6px 0 20px;
    font-size: 13px;
  }

  .disk-empty .ivu-btn {
    border-radius: 8px;
  }

  .disk-tip {
    margin-top: 8px;
    color: #999;
    font-size: 12px;
  }

  .disk-file-table /deep/ .ivu-table:before,
  .disk-file-table /deep/ .ivu-table:after {
    display: none;
  }

  .disk-file-table /deep/ .ivu-table-wrapper,
  .disk-file-table {
    border: none;
  }

  .disk-file-table /deep/ .disk-op-btn.disk-op-danger {
    color: #ed4014;
  }

  .disk-file-table /deep/ .disk-op-btn.disk-op-danger:hover {
    background: #fff1f0;
  }

  .disk-purge {
    display: flex;
    align-items: flex-start;
    padding-top: 6px;
  }

  .disk-purge-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    margin-right: 14px;
    border-radius: 50%;
    background: #fff1f0;
    color: #ed4014;
  }

  .disk-purge-main {
    flex: 1;
    min-width: 0;
  }

  .disk-purge-title {
    color: #ed4014;
    font-size: 17px;
    font-weight: 600;
  }

  .disk-purge-target {
    display: flex;
    align-items: center;
    margin: 12px 0 10px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #f7f9fc;
    color: #17233d;
  }

  .disk-purge-target img {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    margin-right: 8px;
  }

  .disk-purge-target span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .disk-purge-list {
    padding-left: 18px;
    color: #515a6e;
    font-size: 13px;
    line-height: 22px;
  }

  .disk-purge-list li {
    list-style: disc;
  }

  .disk-file-table /deep/ .disk-op-btn.disk-op-text {
    width: auto;
    padding: 0 10px;
    white-space: nowrap;
  }

  .disk-file-table /deep/ .disk-op-text span {
    margin-left: 4px;
    font-size: 13px;
  }

  .disk-file-table /deep/ .ivu-table-header th:first-child .ivu-table-cell,
  .disk-file-table /deep/ .ivu-table-body td:first-child .ivu-table-cell {
    padding-left: 8px;
    padding-right: 8px;
    overflow: visible;
    text-overflow: clip;
  }

  .disk-file-table /deep/ .ivu-table-header th {
    background: #fafbfc;
    color: #808695;
    font-weight: normal;
  }

  .disk-file-table /deep/ .ivu-table-body td {
    height: 56px;
    border-bottom-color: #f2f3f5;
  }

  .disk-file-table /deep/ .ivu-table-row-hover td,
  .disk-file-table /deep/ .ivu-table-row:hover td {
    background: #f3f8ff;
  }

  .disk-file-table /deep/ .ivu-table-row-highlight td {
    background: #e8f2ff;
  }

  .disk-file-table /deep/ .disk-col-muted {
    color: #808695;
  }

  .disk-file-table /deep/ .disk-name-cell {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .disk-file-table /deep/ .disk-name-main {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
  }

  .disk-file-table /deep/ .disk-name-icon {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    margin-right: 12px;
  }

  .disk-file-table /deep/ .disk-name-text {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: #17233d;
    font-size: 14px;
  }

  .disk-file-table /deep/ a.disk-name-text:hover {
    color: #2d8cf0;
  }

  .disk-file-table /deep/ .disk-name-tag {
    flex: 0 0 auto;
    margin-left: 8px;
    padding: 0 6px;
    border-radius: 2px;
    font-size: 12px;
    line-height: 18px;
  }

  .disk-file-table /deep/ .disk-name-tag-warn {
    color: #ff9900;
    background: #fff7e6;
  }

  .disk-file-table /deep/ .disk-name-tag-err {
    color: #ed4014;
    background: #fff1f0;
  }

  .disk-file-table /deep/ .disk-row-ops {
    display: flex;
    align-items: center;
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    padding: 2px 4px;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(23, 35, 61, 0.08);
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s;
  }

  .disk-file-table /deep/ .ivu-table-row:hover .disk-row-ops,
  .disk-file-table /deep/ .disk-row-ops.is-active {
    visibility: visible;
    opacity: 1;
  }

  .disk-file-table /deep/ .disk-op-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 28px;
    border-radius: 4px;
    color: #2d8cf0;
    cursor: pointer;
  }

  .disk-file-table /deep/ .disk-op-btn:hover {
    background: #e8f2ff;
  }

  .disk-dm-field {
    display: flex;
    align-items: center;
  }

  .disk-dm-field-icon {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    margin-right: 12px;
  }

  .disk-dm-field .disk-avatar {
    margin-right: 12px;
  }

  .disk-dm-field /deep/ .ivu-input {
    border-radius: 8px;
  }

  .disk-dm-hint {
    margin-top: 12px;
    color: #a8adb7;
    font-size: 12px;
  }

  .disk-dm-hint-top {
    margin: 0 0 12px;
  }

  .disk-dm-target {
    display: flex;
    align-items: center;
    min-width: 0;
    margin-bottom: 16px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f7f9fc;
    color: #515a6e;
  }

  .disk-dm-target img {
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    margin-right: 10px;
  }

  .disk-dm-target span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .disk-dm-card {
    padding: 16px;
    border: 1px solid #eef0f4;
    border-radius: 12px;
    background: #fff;
  }

  .disk-dm-card-title {
    margin-bottom: 12px;
    font-weight: 600;
    color: #17233d;
  }

  .disk-dm-section-title {
    margin: 20px 0 10px;
    font-weight: 600;
    color: #17233d;
  }

  .disk-dm-section-title span {
    margin-left: 4px;
    color: #a8adb7;
    font-weight: normal;
  }

  .disk-dm-tabs {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .disk-dm-tab {
    margin: 0 8px 6px 0;
    padding: 4px 14px;
    border-radius: 16px;
    background: #f5f7fa;
    color: #515a6e;
  }

  .disk-dm-tab:hover {
    color: #2d8cf0;
  }

  .disk-dm-tab.on {
    background: #e3eeff;
    color: #2d8cf0;
    font-weight: 500;
  }

  .disk-picker {
    border: 1px solid #eef0f4;
    border-radius: 12px;
    overflow: hidden;
  }

  .disk-picker-path {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 10px 14px;
    border-bottom: 1px solid #eef0f4;
    background: #fafbfc;
    font-size: 13px;
  }

  .disk-picker-path a {
    color: #515a6e;
  }

  .disk-picker-path a:hover {
    color: #2d8cf0;
  }

  .disk-picker-path .current {
    color: #17233d;
    font-weight: 600;
  }

  .disk-picker-body {
    height: 300px;
    overflow-y: auto;
    padding: 6px;
  }

  .disk-picker-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    height: 100%;
    color: #a8adb7;
    font-size: 13px;
  }

  .disk-picker-empty img {
    width: 56px;
    height: 56px;
    margin-bottom: 10px;
    opacity: 0.6;
  }

  .disk-move-folder {
    display: flex;
    align-items: center;
    height: 44px;
    padding: 0 10px;
    border-radius: 8px;
    cursor: pointer;
  }

  .disk-move-folder:hover {
    background: #f0f6ff;
  }

  .disk-move-folder img {
    width: 28px;
    height: 28px;
    margin-right: 10px;
  }

  .disk-move-folder span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .disk-move-folder-arrow {
    color: #c5c8ce;
  }

  .disk-acl-form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .disk-acl-select {
    flex: 1;
    min-width: 240px;
    margin-left: 12px;
  }

  .disk-acl-all {
    margin-left: 12px;
    white-space: nowrap;
  }

  .disk-acl-all.disabled {
    color: #c5c8ce;
    cursor: not-allowed;
  }

  .disk-acl-perm {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 14px;
  }

  .disk-acl-perm-label {
    margin-right: 12px;
    color: #808695;
  }

  .disk-acl-save {
    margin-left: auto;
    border-radius: 8px;
  }

  .disk-chip-checks /deep/ .ivu-checkbox-wrapper {
    margin: 4px 8px 4px 0;
    padding: 2px 12px 2px 8px;
    border-radius: 14px;
    background: #f5f7fa;
    font-size: 13px;
  }

  .disk-chip-checks /deep/ .ivu-checkbox-wrapper-checked {
    background: #e3eeff;
    color: #2d8cf0;
  }

  .disk-person-list,
  .disk-share-list {
    max-height: 300px;
    overflow-y: auto;
  }

  .disk-person-empty {
    padding: 28px 0;
    border-radius: 12px;
    background: #fafbfc;
    color: #a8adb7;
    text-align: center;
    font-size: 13px;
  }

  .disk-person {
    display: flex;
    align-items: center;
    padding: 10px 8px;
    border-radius: 10px;
  }

  .disk-person:hover {
    background: #f7f9fc;
  }

  .disk-person-inline {
    padding: 4px 0;
  }

  .disk-person-inline:hover {
    background: transparent;
  }

  .disk-avatar-md {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 15px;
  }

  .disk-person-main {
    flex: 1;
    min-width: 0;
    margin-left: 12px;
  }

  .disk-person-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #17233d;
    font-weight: 500;
  }

  .disk-person-sub {
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #a8adb7;
    font-size: 12px;
  }

  .disk-mini-tag {
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 4px;
    background: #f0f2f5;
    color: #808695;
    font-size: 11px;
    font-weight: normal;
    line-height: 18px;
  }

  .disk-link-danger,
  .disk-link-danger:hover {
    color: #ed4014;
  }

  .disk-share-row {
    display: flex;
    align-items: center;
    min-height: 36px;
  }

  .disk-share-row + .disk-share-row {
    margin-top: 12px;
  }

  .disk-share-label {
    width: 72px;
    flex: 0 0 72px;
    color: #808695;
  }

  .disk-share-random {
    margin-left: 12px;
  }

  .disk-share-submit .ivu-btn {
    border-radius: 8px;
  }

  .disk-share-item {
    padding: 12px 14px;
    border: 1px solid #eef0f4;
    border-radius: 12px;
  }

  .disk-share-item + .disk-share-item {
    margin-top: 10px;
  }

  .disk-share-item-top {
    display: flex;
    align-items: center;
  }

  .disk-share-item-icon {
    margin-right: 8px;
    color: #2d8cf0;
    font-size: 16px;
  }

  .disk-share-url {
    flex: 1;
    min-width: 0;
    margin-right: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .disk-share-item-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 8px;
    color: #a8adb7;
    font-size: 12px;
  }

  .disk-share-item-meta > span {
    margin-right: 16px;
  }

  .disk-share-item-meta b {
    color: #515a6e;
  }

  .disk-share-item-ops {
    margin-left: auto;
    margin-right: 0 !important;
  }

  .disk-share-item-ops a + a {
    margin-left: 14px;
  }

  .disk-upload-head {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  .disk-upload-head .disk-dm-target {
    flex: 1;
    margin-bottom: 0;
  }

  .disk-upload-switch {
    display: flex;
    align-items: center;
    margin-left: 16px;
    color: #515a6e;
    white-space: nowrap;
  }

  .disk-upload-switch span:first-child {
    margin-right: 8px;
  }

  .disk-upload-page {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
  }

  .disk-drop {
    padding: 32px 16px 28px;
    text-align: center;
    border: 2px dashed #c9dcf7;
    border-radius: 14px;
    background: #f7faff;
    transition: border-color 0.15s, background 0.15s;
  }

  .disk-drop:hover {
    border-color: #2d8cf0;
    background: #f0f6ff;
  }

  .disk-drop-icon {
    color: #2d8cf0;
  }

  .disk-drop-title {
    margin-top: 4px;
    color: #17233d;
    font-size: 15px;
  }

  .disk-drop-sub {
    margin: 4px 0 12px;
    color: #a8adb7;
    font-size: 12px;
  }

  .disk-drop .ivu-btn {
    border-radius: 8px;
  }

  .disk-upload-rules {
    margin-top: 10px;
    font-size: 12px;
  }

  .disk-upload-rules > a {
    color: #808695;
  }

  .disk-upload-rules-body {
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fafbfc;
    color: #808695;
    line-height: 20px;
  }

  .disk-upload-table {
    margin-top: 12px;
  }

  .disk-member-head {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  .disk-member-head-main {
    flex: 1;
    min-width: 0;
    margin-left: 12px;
  }

  .disk-member-toolbar {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  .disk-member-select {
    flex: 1;
    margin: 0 12px;
  }

  .disk-member-toolbar > .ivu-btn {
    border-radius: 8px;
  }

  .disk-member-table {
    border: none;
  }

  .disk-member-table /deep/ .ivu-table:before,
  .disk-member-table /deep/ .ivu-table:after {
    display: none;
  }

  .disk-member-table /deep/ .ivu-table-header th {
    background: #fafbfc;
    color: #808695;
    font-weight: normal;
  }

  .disk-member-table /deep/ td {
    border-bottom-color: #f2f3f5;
  }

  .disk-upload-task {
    margin-bottom: 12px;
  }

  .disk-upload-name {
    margin-bottom: 4px;
  }

  .disk-upload-status {
    margin-left: 8px;
    color: #999;
    font-size: 12px;
  }

  .disk-task-float {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 900;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .disk-task-float.open {
    width: 640px;
    align-items: stretch;
  }

  .disk-task-float.open .disk-task-bar {
    align-self: flex-end;
  }

  .disk-task-bar {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 14px;
    background: #fff;
    border: 1px solid #eef0f4;
    border-radius: 12px;
    box-shadow: 0 6px 24px rgba(23, 35, 61, 0.12);
    cursor: pointer;
    color: #515a6e;
  }

  .disk-task-bar span {
    margin-left: 8px;
  }

  .disk-task-bar.uploading {
    height: 48px;
    padding: 0 14px 0 9px;
    border-radius: 24px;
  }

  .disk-task-bar-arrow {
    margin-left: 10px;
    color: #a8adb7;
  }

  .disk-wave {
    position: relative;
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    margin-left: 0 !important;
    overflow: hidden;
    border-radius: 50%;
    background: #eaf3ff;
    box-shadow: inset 0 0 0 1px #c9dcf7;
  }

  .disk-wave-fill {
    position: absolute;
    left: 50%;
    width: 60px;
    height: 60px;
    margin: -4px 0 0 -30px !important;
    border-radius: 42%;
    background: #2d8cf0;
    animation: disk-wave-spin 2.4s linear infinite;
    transition: top 0.4s ease;
  }

  .disk-wave-fill.back {
    border-radius: 38%;
    background: rgba(45, 140, 240, 0.35);
    animation-duration: 3.6s;
    animation-direction: reverse;
  }

  @keyframes disk-wave-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .disk-task-progress {
    display: flex;
    flex-direction: column;
    margin-left: 10px !important;
    line-height: 16px;
  }

  .disk-task-progress-main {
    margin-left: 0 !important;
    color: #17233d;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
  }

  .disk-task-progress-speed {
    margin-left: 0 !important;
    color: #808695;
    font-size: 12px;
    white-space: nowrap;
  }

  .disk-task-badge {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #ed4014;
    color: #fff;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
  }


  .disk-file-table /deep/ .ivu-table-row.disk-row-flash td {
    background: #fff7e0;
    transition: background 0.6s;
  }
  .disk-activity-link {
    color: #2d8cf0;
    cursor: pointer;
  }
  .disk-activity-link:hover {
    text-decoration: underline;
  }
  .disk-activity-drawer {
    position: fixed;
    top: 64px;
    right: 24px;
    bottom: 72px;
    width: 360px;
    z-index: 910;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #eef0f4;
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(23, 35, 61, 0.16);
  }

  .disk-activity-head {
    display: flex;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid #e8eaec;
    font-weight: 500;
  }

  .disk-activity-body {
    flex: 1;
    overflow: auto;
    padding: 8px 0 16px;
  }

  .disk-activity-empty {
    flex: 1;
    padding: 24px 14px;
    color: #808695;
    font-size: 13px;
    text-align: center;
  }

  .disk-activity-day-label {
    padding: 10px 14px 6px;
    color: #808695;
    font-size: 12px;
  }

  .disk-activity-card {
    display: flex;
    padding: 8px 14px;
    position: relative;
  }

  .disk-activity-dot {
    width: 8px;
    height: 8px;
    margin-top: 6px;
    margin-right: 10px;
    border-radius: 50%;
    background: #2d8cf0;
    flex-shrink: 0;
  }

  .disk-activity-card-main {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
    background: #f8f8f9;
    border-radius: 4px;
  }

  .disk-activity-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }

  .disk-activity-time {
    color: #808695;
    font-size: 12px;
  }

  .disk-activity-summary {
    color: #515a6e;
    font-size: 13px;
    line-height: 1.5;
    word-break: break-all;
  }

  .disk-task-panel {
    margin-bottom: 8px;
    max-height: 480px;
    overflow: auto;
    background: #fff;
    border: 1px solid #eef0f4;
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(23, 35, 61, 0.16);
  }

  .disk-task-head {
    display: flex;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid #e8eaec;
    font-weight: 500;
  }

  .disk-task-overall {
    padding: 10px 14px 0;
  }

  .disk-task-overall-text {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 6px;
    color: #808695;
    font-size: 12px;
  }

  .disk-task-speed {
    margin-left: 12px;
    color: #2d8cf0;
    white-space: nowrap;
  }

  .disk-task-note {
    margin: 10px 14px 0;
    padding: 8px 12px;
    background: #f8f8f9;
    color: #808695;
    font-size: 12px;
  }

  .disk-task-filters {
    display: flex;
    padding: 10px 14px 0;
    border-bottom: 1px solid #e8eaec;
  }

  .disk-task-filters a {
    margin-right: 4px;
    padding: 6px 10px;
    color: #515a6e;
  }

  .disk-task-filters a.on {
    color: #2d8cf0;
    background: transparent;
    border-bottom: 2px solid #2d8cf0;
  }

  .disk-task-tools {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
  }

  .disk-task-concurrency {
    display: inline-flex;
    align-items: center;
    color: #515a6e;
    font-size: 12px;
  }

  .disk-task-tool-btns {
    display: inline-flex;
    align-items: center;
  }

  .disk-upload-auto-hint {
    margin-left: 8px;
    color: #808695;
    font-size: 12px;
    white-space: nowrap;
  }

  .disk-task-cols,
  .disk-task-row {
    display: grid;
    grid-template-columns: 1.6fr 1.2fr 88px 156px;
    gap: 8px;
    align-items: center;
    padding: 8px 14px;
  }

  .disk-task-cols {
    background: #f8f8f9;
    color: #808695;
    font-size: 12px;
  }

  .disk-task-empty {
    padding: 24px 14px;
    color: #999;
    text-align: center;
  }

  .disk-task-row {
    border-bottom: 1px solid #f4f4f5;
  }

  .disk-task-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .disk-task-size,
  .disk-task-dest {
    color: #999;
    font-size: 12px;
  }

  .disk-task-dest {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .disk-task-state.success {
    color: #19be6b;
  }

  .disk-task-state.failed {
    color: #ed4014;
  }

  .disk-task-state.uploading {
    color: #2d8cf0;
  }

  .disk-task-state.stopped {
    color: #808695;
  }

  .disk-task-ops {
    white-space: nowrap;
  }

  .disk-task-ops a + a {
    margin-left: 8px;
  }
  .disk-preview {
    min-height: 60px;
    text-align: left;
  }

  .disk-preview-center {
    text-align: center;
  }

  .disk-preview-image {
    max-width: 100%;
  }

  .disk-preview-media {
    max-width: 100%;
  }

  .disk-preview-frame {
    width: 100%;
    height: 640px;
    border: 0;
  }

  .disk-preview-tip {
    padding: 24px 0;
    color: #999;
  }

  .disk-preview-hint {
    margin-top: 8px;
    color: #999;
    font-size: 12px;
  }

  .disk-preview-pre {
    max-height: 560px;
    margin: 0;
    padding: 12px;
    overflow: auto;
    background: #f8f8f8;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    font-family: Consolas, Monaco, monospace;
    font-size: 13px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-all;
  }

  .disk-preview-table-wrap {
    max-height: 560px;
    overflow: auto;
  }

  .disk-preview-table {
    width: max-content;
    min-width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .disk-preview-table td {
    padding: 6px 10px;
    border: 1px solid #e8e8e8;
    text-align: left;
    white-space: nowrap;
  }

  .disk-preview-th {
    background: #f8f8f8;
    font-weight: bold;
  }

  .disk-preview-markdown {
    max-height: 560px;
    overflow: auto;
    text-align: left;
    line-height: 1.7;
  }

  .disk-preview-markdown >>> h1,
  .disk-preview-markdown >>> h2,
  .disk-preview-markdown >>> h3,
  .disk-preview-markdown >>> h4,
  .disk-preview-markdown >>> h5,
  .disk-preview-markdown >>> h6 {
    margin: 14px 0 8px;
    font-weight: bold;
  }

  .disk-preview-markdown >>> h1 {
    font-size: 24px;
  }

  .disk-preview-markdown >>> h2 {
    font-size: 20px;
  }

  .disk-preview-markdown >>> h3 {
    font-size: 17px;
  }

  .disk-preview-markdown >>> p {
    margin: 8px 0;
  }

  .disk-preview-markdown >>> ul,
  .disk-preview-markdown >>> ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  .disk-preview-markdown >>> li {
    margin: 4px 0;
  }

  .disk-preview-markdown >>> blockquote {
    margin: 8px 0;
    padding: 4px 12px;
    color: #666;
    border-left: 3px solid #dcdee2;
  }

  .disk-preview-markdown >>> code {
    padding: 1px 5px;
    background: #f1f1f1;
    border-radius: 3px;
    font-family: Consolas, Monaco, monospace;
  }

  .disk-preview-markdown >>> pre {
    margin: 8px 0;
    padding: 12px;
    overflow: auto;
    background: #f8f8f8;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
  }

  .disk-preview-markdown >>> pre code {
    padding: 0;
    background: none;
  }

  .disk-preview-markdown >>> table {
    width: 100%;
    margin: 8px 0;
    border-collapse: collapse;
    font-size: 13px;
  }

  .disk-preview-markdown >>> th,
  .disk-preview-markdown >>> td {
    padding: 6px 10px;
    border: 1px solid #e8e8e8;
    text-align: left;
  }

  .disk-preview-markdown >>> img {
    max-width: 100%;
  }

  .disk-preview-markdown >>> hr {
    margin: 12px 0;
    border: 0;
    border-top: 1px solid #e8e8e8;
  }
</style>

<style>
  .disk-modal .ivu-modal-content {
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(23, 35, 61, 0.16);
  }

  .disk-modal .ivu-modal-header {
    padding: 18px 24px 6px;
    border-bottom: none;
  }

  .disk-modal .ivu-modal-header-inner {
    font-size: 17px;
    font-weight: 600;
    color: #17233d;
  }

  .disk-modal .ivu-modal-close {
    top: 14px;
    right: 14px;
  }

  .disk-modal .ivu-modal-body {
    padding: 14px 24px 18px;
  }

  .disk-modal .ivu-modal-footer {
    padding: 0 24px 20px;
    border-top: none;
  }

  .disk-modal .ivu-modal-footer .ivu-btn {
    min-width: 76px;
    border-radius: 8px;
  }

  .disk-member-table .disk-person {
    display: flex;
    align-items: center;
    padding: 6px 0;
  }

  .disk-member-table .disk-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
  }

  .disk-member-table .disk-person-main {
    flex: 1;
    min-width: 0;
    margin-left: 12px;
  }

  .disk-member-table .disk-person-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #17233d;
    font-weight: 500;
  }

  .disk-member-table .disk-person-sub {
    margin-top: 2px;
    color: #a8adb7;
    font-size: 12px;
  }

  .disk-member-table .disk-role-tag {
    display: inline-block;
    padding: 0 8px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 20px;
    color: #515a6e;
    background: #eef0f4;
  }

  .disk-member-table .disk-role-tag.role-1 {
    color: #d46b08;
    background: #fff4e5;
  }

  .disk-member-table .disk-role-tag.role-2 {
    color: #2d8cf0;
    background: #e3eeff;
  }

  .disk-member-table .disk-link-danger,
  .disk-member-table .disk-link-danger:hover {
    color: #ed4014;
  }
</style>



// WEBPACK FOOTER //
// DiskIndex.vue?df85b166